/**
 * Wiki glossary — bilingual definitions for Italian and domain-specific
 * terms used in articles.  Authors mark a term in prose with `[[term-id]]`;
 * the Renderer resolves it to a hover/tap tooltip in the current UI language.
 *
 * Adding a term:
 *   1. Append a new entry below.  `id` is kebab-case ASCII; `ru` / `en` carry
 *      the surface label (what the reader sees) and a short definition.
 *   2. Keep `definition` to one or two sentences.  This is a tooltip, not an
 *      article.  Link to the relevant chapter from the article body if the
 *      reader needs depth.
 *   3. Reference from prose: `[[codice-fiscale]]` (renders as the label),
 *      `[[codice-fiscale|свой текст]]` (renders custom text, same tooltip).
 *   4. New terms must be paired (RU + EN) before merging.
 *
 * Per ARCHITECTURE §5.4: every Italian phrase, every Italy-specific acronym,
 * and every legal / tax / medical short form must either be glossed in line
 * (no tooltip needed) or marked with `[[term-id]]` so a reader has access
 * to the definition.
 */

import type { LangCode } from './architecture';

export interface GlossaryEntry {
  /** Surface label and short definition per language. */
  ru: { label: string; definition: string };
  en: { label: string; definition: string };
  /** Optional category for future grouping (admin, tax, education, etc.). */
  category?: 'admin' | 'tax' | 'health' | 'education' | 'property' | 'transit' | 'misc';
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  // ── Administrative / civil-registry ─────────────────────────────────
  'codice-fiscale': {
    category: 'admin',
    ru: {
      label: 'codice fiscale',
      definition: 'Налоговый код Италии: 16-значный буквенно-цифровой идентификатор резидента и нерезидента. Выдаётся бесплатно в Agenzia delle Entrate; нужен для любого договора, открытия счёта, записи к врачу.',
    },
    en: {
      label: 'codice fiscale',
      definition: 'The Italian tax code: a 16-character alphanumeric ID for residents and non-residents. Issued free by the Agenzia delle Entrate and required for any contract, bank account, or medical appointment.',
    },
  },
  'permesso-di-soggiorno': {
    category: 'admin',
    ru: {
      label: 'permesso di soggiorno',
      definition: 'Вид на жительство Италии. Выдаётся Questurа (полицейский орган) после въезда по визе D; типы зависят от основания (lavoro, studio, ricongiungimento familiare, asilo).',
    },
    en: {
      label: 'permesso di soggiorno',
      definition: 'The Italian residence permit. Issued by the Questura (police authority) after entry on a long-stay visa; subtypes depend on the basis (work, study, family reunification, asylum).',
    },
  },
  'questura': {
    category: 'admin',
    ru: {
      label: 'Questura',
      definition: 'Местный полицейский орган, выдающий и продлевающий permesso di soggiorno. В каждой провинции своя; принимает по записи.',
    },
    en: {
      label: 'Questura',
      definition: 'The provincial police authority that issues and renews the permesso di soggiorno. Every province has its own; appointment-based.',
    },
  },
  'anagrafe': {
    category: 'admin',
    ru: {
      label: 'Anagrafe',
      definition: 'Загс / реестр жителей муниципалитета. Сюда подаётся iscrizione anagrafica (регистрация по адресу), без которой не выдаётся tessera sanitaria и не оформляется SPID.',
    },
    en: {
      label: 'Anagrafe',
      definition: 'The municipal civil registry. Iscrizione anagrafica (address registration) is filed here and is the gate to tessera sanitaria and SPID.',
    },
  },
  'agenzia-delle-entrate': {
    category: 'admin',
    ru: {
      label: 'Agenzia delle Entrate',
      definition: 'Налоговая служба Италии (аналог ФНС). Выдаёт codice fiscale, принимает декларации, ведёт реестр недвижимости.',
    },
    en: {
      label: 'Agenzia delle Entrate',
      definition: 'The Italian revenue agency. Issues codice fiscale, accepts tax returns, maintains the property cadastre.',
    },
  },
  'spid': {
    category: 'admin',
    ru: {
      label: 'SPID',
      definition: 'Sistema Pubblico di Identità Digitale, единая электронная подпись Италии. Без неё не работают онлайн-запись в школу, личный кабинет INPS и большинство госсервисов.',
    },
    en: {
      label: 'SPID',
      definition: 'Sistema Pubblico di Identità Digitale, Italy\'s single digital identity. Without it most government online services (school enrolment, INPS, tax) do not work.',
    },
  },

  // ── Healthcare ──────────────────────────────────────────────────────
  'ssn': {
    category: 'health',
    ru: {
      label: 'SSN',
      definition: 'Servizio Sanitario Nazionale, итальянская национальная медицинская система. Финансируется из общих налогов, исполняется регионами через ASL.',
    },
    en: {
      label: 'SSN',
      definition: 'Servizio Sanitario Nazionale, the Italian national health service. Funded from general taxation, delivered by regions through ASL units.',
    },
  },
  'tessera-sanitaria': {
    category: 'health',
    ru: {
      label: 'tessera sanitaria',
      definition: 'Пластиковая карта SSN, дающая право на бесплатную или льготную медицину. Выдаётся в ASL после регистрации в Anagrafe.',
    },
    en: {
      label: 'tessera sanitaria',
      definition: 'The plastic SSN card that grants access to free or co-pay care. Issued at the ASL after Anagrafe registration.',
    },
  },
  'asl': {
    category: 'health',
    ru: {
      label: 'ASL',
      definition: 'Azienda Sanitaria Locale, местная служба здравоохранения региона. Регистрирует tessera sanitaria, прикрепляет к семейному врачу, ведёт амбулатории.',
    },
    en: {
      label: 'ASL',
      definition: 'Azienda Sanitaria Locale, the regional public-health unit. Registers tessera sanitaria, assigns a family doctor, runs outpatient clinics.',
    },
  },
  'medico-di-base': {
    category: 'health',
    ru: {
      label: 'medico di base',
      definition: 'Семейный врач (general practitioner) в SSN. Бесплатный для tessera-держателя, ведёт первичный приём и выписывает направления к специалистам.',
    },
    en: {
      label: 'medico di base',
      definition: 'The family doctor (general practitioner) under SSN. Free for tessera holders; handles primary care and refers to specialists.',
    },
  },
  'impegnativa': {
    category: 'health',
    ru: {
      label: 'impegnativa',
      definition: 'Направление от medico di base к специалисту или на диагностику. Без него специалист SSN не примет; для частного визита не нужна.',
    },
    en: {
      label: 'impegnativa',
      definition: 'The medico di base\'s referral to a specialist or for diagnostic tests. Required to book an SSN specialist; not needed for private visits.',
    },
  },
  'ticket': {
    category: 'health',
    ru: {
      label: 'ticket',
      definition: 'Соплатёж пациента за услугу или препарат, частично покрываемый SSN. От 3 до 35 €, льготы по возрасту, доходу и заболеванию.',
    },
    en: {
      label: 'ticket',
      definition: 'The patient co-payment for an SSN service or prescription. Ranges 3-35 €; waivers exist for age, income, and chronic conditions.',
    },
  },
  'cup': {
    category: 'health',
    ru: {
      label: 'CUP',
      definition: 'Centro Unico di Prenotazione, единый центр записи на приёмы SSN. Работает по телефону, через сайт региона и в физических окнах при ASL.',
    },
    en: {
      label: 'CUP',
      definition: 'Centro Unico di Prenotazione, the unified booking centre for SSN appointments. Operates by phone, on the regional website, and at ASL counters.',
    },
  },
  'pronto-soccorso': {
    category: 'health',
    ru: {
      label: 'pronto soccorso',
      definition: 'Отделение неотложной помощи (ER) в больнице. Триаж по цветовому коду: красный, жёлтый, зелёный, белый. Белый код с 2023 года часто платный.',
    },
    en: {
      label: 'pronto soccorso',
      definition: 'The hospital emergency department. Triaged on a colour code: red, yellow, green, white. White code carries a 15-25 € fee in most regions since 2023.',
    },
  },
  'ricetta-dematerializzata': {
    category: 'health',
    ru: {
      label: 'ricetta dematerializzata',
      definition: 'Электронный рецепт SSN. Врач отправляет его напрямую в систему; пациент приходит в аптеку с tessera sanitaria, без бумажного бланка.',
    },
    en: {
      label: 'ricetta dematerializzata',
      definition: 'The dematerialised SSN prescription. The doctor pushes it to the system; the patient walks into the pharmacy with a tessera sanitaria, no paper.',
    },
  },
  'farmacia-di-turno': {
    category: 'health',
    ru: {
      label: 'farmacia di turno',
      definition: 'Дежурная круглосуточная аптека района. Расписание висит на двери каждой обычной farmacia.',
    },
    en: {
      label: 'farmacia di turno',
      definition: 'The 24/7 on-duty pharmacy in the district. The rotation schedule is posted on the door of every standard farmacia.',
    },
  },
  'parafarmacia': {
    category: 'health',
    ru: {
      label: 'parafarmacia',
      definition: 'Магазин товаров для здоровья без права отпуска рецептурных препаратов. На 10-20 % дешевле обычной farmacia на витамины, БАДы и косметику.',
    },
    en: {
      label: 'parafarmacia',
      definition: 'A health-products shop without the right to dispense prescription drugs. 10-20 % cheaper than farmacia on vitamins, supplements, cosmetics.',
    },
  },
  'csm': {
    category: 'health',
    ru: {
      label: 'CSM',
      definition: 'Centro di Salute Mentale, государственный центр психиатрической помощи. Один в провинции; фокус на тяжёлые случаи (психоз, биполярка), очередь до полугода.',
    },
    en: {
      label: 'CSM',
      definition: 'Centro di Salute Mentale, the public mental-health centre. One per province; focused on severe cases (psychosis, bipolar), with multi-month waits.',
    },
  },
  'lea': {
    category: 'health',
    ru: {
      label: 'LEA',
      definition: 'Livelli Essenziali di Assistenza, гарантированные уровни помощи SSN. Список услуг, которые каждый регион обязан обеспечить.',
    },
    en: {
      label: 'LEA',
      definition: 'Livelli Essenziali di Assistenza, the SSN\'s guaranteed care levels. The list of services every region must deliver.',
    },
  },
  'agenas': {
    category: 'health',
    ru: {
      label: 'AGENAS',
      definition: 'Agenzia Nazionale per i Servizi Sanitari Regionali, государственное агентство мониторинга SSN. Публикует региональную статистику ожидания и качества.',
    },
    en: {
      label: 'AGENAS',
      definition: 'Agenzia Nazionale per i Servizi Sanitari Regionali, the state agency that monitors regional SSN performance and publishes wait-time data.',
    },
  },
  'bonus-psicologo': {
    category: 'health',
    ru: {
      label: 'Bonus Psicologo',
      definition: 'Государственная субсидия 1 500 € на курс психотерапии для семей с низким ISEE. Окно подачи раз в год, конкурс высокий.',
    },
    en: {
      label: 'Bonus Psicologo',
      definition: 'The state subsidy of 1,500 € for psychotherapy for low-ISEE families. Window opens once a year; heavily oversubscribed.',
    },
  },

  // ── Tax ─────────────────────────────────────────────────────────────
  'irpef': {
    category: 'tax',
    ru: {
      label: 'IRPEF',
      definition: 'Imposta sul Reddito delle Persone Fisiche, итальянский подоходный налог. Прогрессивные ставки 23-43 % с 2024-2025; декларация раз в год.',
    },
    en: {
      label: 'IRPEF',
      definition: 'Imposta sul Reddito delle Persone Fisiche, the Italian personal income tax. Progressive at 23-43 % since 2024-2025; annual return.',
    },
  },
  'irap': {
    category: 'tax',
    ru: {
      label: 'IRAP',
      definition: 'Imposta Regionale sulle Attività Produttive, региональный налог на бизнес-деятельность. 3,9 % базовая ставка, варьируется по регионам.',
    },
    en: {
      label: 'IRAP',
      definition: 'Imposta Regionale sulle Attività Produttive, the regional business activity tax. 3.9 % base rate, with regional adjustments.',
    },
  },
  'isee': {
    category: 'admin',
    ru: {
      label: 'ISEE',
      definition: 'Indicatore della Situazione Economica Equivalente, индекс семейного дохода и имущества. Определяет tuition в университете, льготы по жилью, школьные обеды, доступ к Bonus Psicologo.',
    },
    en: {
      label: 'ISEE',
      definition: 'Indicatore della Situazione Economica Equivalente, the Italian household income-and-asset index. Drives university tuition, housing benefits, school lunches, and Bonus Psicologo eligibility.',
    },
  },
  'imu': {
    category: 'property',
    ru: {
      label: 'IMU',
      definition: 'Imposta Municipale Unica, муниципальный налог на недвижимость. Платится за seconda casa, землю и коммерческую недвижимость; prima casa освобождена кроме категорий A/1, A/8, A/9 (люкс).',
    },
    en: {
      label: 'IMU',
      definition: 'Imposta Municipale Unica, the municipal property tax. Paid on seconda casa, land, and commercial property; prima casa is exempt except for luxury categories A/1, A/8, A/9.',
    },
  },
  'tari': {
    category: 'property',
    ru: {
      label: 'TARI',
      definition: 'Tassa sui rifiuti, муниципальный сбор за вывоз мусора. Платит владелец или арендатор; обычно 150-400 € в год за квартиру.',
    },
    en: {
      label: 'TARI',
      definition: 'Tassa sui rifiuti, the municipal waste collection fee. Paid by owner or tenant; typically 150-400 € a year per flat.',
    },
  },
  'cedolare-secca': {
    category: 'property',
    ru: {
      label: 'cedolare secca',
      definition: 'Плоский налог на арендный доход (21 % стандарт, 10 % при «канонизированной» аренде). Альтернатива включения дохода в IRPEF, не подлежит налоговым вычетам.',
    },
    en: {
      label: 'cedolare secca',
      definition: 'A flat tax on rental income (21 % standard, 10 % on rent-controlled "canonised" leases). Alternative to including rent in IRPEF; no deductions allowed.',
    },
  },
  'prima-casa': {
    category: 'property',
    ru: {
      label: 'prima casa',
      definition: 'Основное место жительства (первая квартира) для налоговых целей. Даёт льготную регистрацию (2 % вместо 9 %), освобождение от IMU и пониженные нотариальные расходы.',
    },
    en: {
      label: 'prima casa',
      definition: 'A principal residence (first home) for tax purposes. Triggers reduced registration (2 % vs 9 %), IMU exemption, and lower notary fees.',
    },
  },
  'seconda-casa': {
    category: 'property',
    ru: {
      label: 'seconda casa',
      definition: 'Вторая или любая последующая недвижимость для налоговых целей. Платится IMU, регистрационный налог 9 %, нет prima-casa-льгот.',
    },
    en: {
      label: 'seconda casa',
      definition: 'A second or any subsequent property for tax purposes. Full IMU applies, registration is 9 %, and prima-casa reliefs are not available.',
    },
  },
  'caparra': {
    category: 'property',
    ru: {
      label: 'caparra',
      definition: 'Залог-задаток при покупке недвижимости. 5-15 % цены; теряется покупателем при отказе и удваивается продавцом при его отказе.',
    },
    en: {
      label: 'caparra',
      definition: 'The earnest-money deposit in a real-estate purchase. 5-15 % of price; forfeited by the buyer on cancellation and doubled by the seller on theirs.',
    },
  },
  'rogito': {
    category: 'property',
    ru: {
      label: 'rogito',
      definition: 'Нотариальный акт окончательной продажи недвижимости. Подписывается у notaio в присутствии обеих сторон; собственность переходит в этот момент.',
    },
    en: {
      label: 'rogito',
      definition: 'The notarised final deed of a real-estate sale. Signed at a notaio with both parties present; ownership transfers at this moment.',
    },
  },
  'notaio': {
    category: 'property',
    ru: {
      label: 'notaio',
      definition: 'Государственный нотариус Италии. Обязателен для сделок с недвижимостью; гонорар 1-2 % цены, минимум 2 000 €.',
    },
    en: {
      label: 'notaio',
      definition: 'Italy\'s public notary. Mandatory for real-estate transactions; fees run 1-2 % of price with a 2,000 € floor.',
    },
  },
  'hnwi': {
    category: 'tax',
    ru: {
      label: 'HNWI',
      definition: 'High Net Worth Individual, состоятельный иностранец, переехавший в Италию. Имеет право на специальный налоговый режим, фиксированный платёж € 300 000 в год за весь зарубежный доход (до 15 лет).',
    },
    en: {
      label: 'HNWI',
      definition: 'High Net Worth Individual, a wealthy newcomer to Italy. Eligible for a special tax regime: a 300,000 € annual lump-sum on all foreign income for up to 15 years.',
    },
  },
  'dtt': {
    category: 'tax',
    ru: {
      label: 'DTT',
      definition: 'Double Taxation Treaty, соглашение об избежании двойного налогообложения между двумя странами. Россия,Италия частично приостановлено указом 585 (август 2023).',
    },
    en: {
      label: 'DTT',
      definition: 'A Double Taxation Treaty between two countries. Russia,Italy is partially suspended under Decree 585 (August 2023).',
    },
  },
  'cfc': {
    category: 'tax',
    ru: {
      label: 'CFC',
      definition: 'Controlled Foreign Corporation, правила налогообложения «контролируемых иностранных компаний». В Италии применяются с 2019 года; OECD-стандарт.',
    },
    en: {
      label: 'CFC',
      definition: 'Controlled Foreign Corporation rules, the tax treatment of foreign-controlled entities. In effect in Italy since 2019; OECD-aligned.',
    },
  },
  'pillar-2': {
    category: 'tax',
    ru: {
      label: 'Pillar 2',
      definition: 'Глобальный минимальный налог ОЭСР 15 % для крупных корпораций (оборот более €750 млн). В Италии действует с 2024 года.',
    },
    en: {
      label: 'Pillar 2',
      definition: 'The OECD global minimum tax of 15 % for large corporations (turnover above 750 M €). In force in Italy since 2024.',
    },
  },
  'cgt': {
    category: 'tax',
    ru: {
      label: 'CGT',
      definition: 'Capital Gains Tax, налог с прибыли от продажи активов. В Италии 26 % на финансовые активы; недвижимость освобождается после 5 лет владения как prima casa.',
    },
    en: {
      label: 'CGT',
      definition: 'Capital Gains Tax on asset disposals. In Italy: 26 % on financial assets; real-estate gains are exempt after a 5-year prima-casa holding period.',
    },
  },

  // ── Education ───────────────────────────────────────────────────────
  'maturita': {
    category: 'education',
    ru: {
      label: 'maturità',
      definition: 'Государственный экзамен в конце scuola superiore (19 лет). Открывает доступ к итальянским университетам; письменная + устная части.',
    },
    en: {
      label: 'maturità',
      definition: 'The state exam at the end of scuola superiore (age 19). Gates entry to Italian universities; written and oral parts.',
    },
  },
  'liceo': {
    category: 'education',
    ru: {
      label: 'liceo',
      definition: 'Академический подтип итальянской superiore. Шесть направлений: классический, научный, лингвистический, художественный, музыкальный, гуманитарных наук.',
    },
    en: {
      label: 'liceo',
      definition: 'The academic stream of Italian superiore. Six tracks: classico (classics), scientifico (STEM), linguistico, artistico, musicale, scienze umane.',
    },
  },
  'scuola-primaria': {
    category: 'education',
    ru: {
      label: 'scuola primaria',
      definition: 'Начальная школа Италии: 5 лет, возраст 6-11. Без оценок в первые годы, акцент на адаптацию и базовую грамоту.',
    },
    en: {
      label: 'scuola primaria',
      definition: 'Italian primary school: 5 years, ages 6-11. No grades in the early years; focus on adaptation and core literacy.',
    },
  },
  'scuola-media': {
    category: 'education',
    ru: {
      label: 'scuola media',
      definition: 'Средняя ступень итальянской школы (secondaria di primo grado): 3 года, возраст 11-14. Раздельные предметы и оценки 1-10.',
    },
    en: {
      label: 'scuola media',
      definition: 'Italian middle school (secondaria di primo grado): 3 years, ages 11-14. Subject-by-subject teaching with 1-10 grades.',
    },
  },
  'scuola-superiore': {
    category: 'education',
    ru: {
      label: 'scuola superiore',
      definition: 'Старшая ступень итальянской школы (secondaria di secondo grado): 5 лет, возраст 14-19. Liceo, istituto tecnico, istituto professionale.',
    },
    en: {
      label: 'scuola superiore',
      definition: 'Italian upper school (secondaria di secondo grado): 5 years, ages 14-19. Splits into liceo, istituto tecnico, istituto professionale.',
    },
  },
  'sostegno': {
    category: 'education',
    ru: {
      label: 'sostegno',
      definition: 'Учитель поддержки в публичной школе для детей с инвалидностью, дислексией, СДВГ, аутизмом. Гарантирован законом 104/92 после диагностики ASL.',
    },
    en: {
      label: 'sostegno',
      definition: 'A public-school support teacher for children with disability, dyslexia, ADHD, autism. Guaranteed by Law 104/92 after an ASL diagnosis.',
    },
  },
  'pei': {
    category: 'education',
    ru: {
      label: 'PEI',
      definition: 'Piano Educativo Individualizzato, индивидуальный учебный план для ребёнка под sostegno. Составляется школой, родителем и ASL ежегодно.',
    },
    en: {
      label: 'PEI',
      definition: 'Piano Educativo Individualizzato, the individual education plan for a sostegno child. Drafted yearly by school, parent, and ASL.',
    },
  },
  'l2-italiano': {
    category: 'education',
    ru: {
      label: 'L2 italiano',
      definition: 'Программа поддержки итальянского как второго языка для иностранных детей в публичной школе. Обычно 4-6 часов в неделю первый год.',
    },
    en: {
      label: 'L2 italiano',
      definition: 'Italian-as-a-second-language support for foreign children in public school. Typically 4-6 hours a week in the first year.',
    },
  },
  'ib': {
    category: 'education',
    ru: {
      label: 'IB',
      definition: 'International Baccalaureate, международный школьный диплом, признаваемый университетами всего мира. Преподаётся в международных школах Италии.',
    },
    en: {
      label: 'IB',
      definition: 'International Baccalaureate, a globally recognised school diploma. Offered by most international schools in Italy.',
    },
  },
  'invalsi': {
    category: 'education',
    ru: {
      label: 'INVALSI',
      definition: 'Istituto Nazionale per la Valutazione del Sistema Educativo, итальянский институт оценки школ. Публикует региональные результаты тестов и PISA-аналоги.',
    },
    en: {
      label: 'INVALSI',
      definition: 'Istituto Nazionale per la Valutazione del Sistema Educativo, Italy\'s school-assessment agency. Publishes regional test results and PISA-aligned data.',
    },
  },
  'eduscopio': {
    category: 'education',
    ru: {
      label: 'Eduscopio',
      definition: 'Бесплатный рейтинг итальянских лицеев от Fondazione Agnelli. Сортирует школы по тому, насколько успешно их выпускники продолжают в университетах. eduscopio.it.',
    },
    en: {
      label: 'Eduscopio',
      definition: 'A free ranking of Italian licei by Fondazione Agnelli. Sorts schools by how their graduates fare in university. eduscopio.it.',
    },
  },
  'bocconi': {
    category: 'education',
    ru: {
      label: 'Bocconi',
      definition: 'Università Commerciale Luigi Bocconi, ведущий частный бизнес-университет Италии (Милан). Tuition 13-17 тыс. €/год; MBA отдельно.',
    },
    en: {
      label: 'Bocconi',
      definition: 'Università Commerciale Luigi Bocconi, Italy\'s leading private business university (Milan). Tuition 13-17 k €/year; MBA separate.',
    },
  },
  'polimi': {
    category: 'education',
    ru: {
      label: 'Polimi',
      definition: 'Politecnico di Milano, ведущий технический университет Италии. Государственный; tuition по ISEE, до 4 тыс. €/год для нерезидентов.',
    },
    en: {
      label: 'Polimi',
      definition: 'Politecnico di Milano, Italy\'s leading technical university. Public; tuition is ISEE-based, capped near 4 k €/year for non-residents.',
    },
  },
  'sapienza': {
    category: 'education',
    ru: {
      label: 'Sapienza',
      definition: 'Università di Roma La Sapienza, крупнейший университет Италии и Европы (~110 тыс. студентов). Государственный, tuition по ISEE.',
    },
    en: {
      label: 'Sapienza',
      definition: 'Università di Roma La Sapienza, the largest university in Italy and Europe (~110 k students). Public; tuition is ISEE-based.',
    },
  },
  'tolc': {
    category: 'education',
    ru: {
      label: 'TOLC',
      definition: 'Test On Line CISIA, профильный вступительный тест в большинство итальянских университетов. Сдаётся за компьютером заранее; результат хранится 12 месяцев.',
    },
    en: {
      label: 'TOLC',
      definition: 'Test On Line CISIA, the entrance test for most Italian universities. Taken at a computer in advance; the score is valid for 12 months.',
    },
  },

  // ── Climate / culture ───────────────────────────────────────────────
  'sirocco': {
    category: 'misc',
    ru: {
      label: 'сирокко',
      definition: 'Юго-восточный ветер из Сахары. На юге Италии 30 дней в году; поднимает температуру на 5-8 °C за сутки, приносит красную пыль, тяжёл для аллергиков.',
    },
    en: {
      label: 'sirocco',
      definition: 'A southeasterly wind from the Sahara. Hits southern Italy ~30 days a year; lifts temperature 5-8 °C in 24 hours, deposits red dust, tough on allergies.',
    },
  },
  'ferragosto': {
    category: 'misc',
    ru: {
      label: 'ferragosto',
      definition: '15 августа, главный летний праздник Италии. Закрываются мелкие магазины, рестораны, частные клиники; работают только сетевые супермаркеты и дежурные аптеки.',
    },
    en: {
      label: 'ferragosto',
      definition: 'August 15, Italy\'s peak summer holiday. Small shops, restaurants, and private clinics close; only chain supermarkets and on-duty pharmacies stay open.',
    },
  },

  // ── Property / housing ──────────────────────────────────────────────
  'catasto': {
    category: 'property',
    ru: {
      label: 'catasto',
      definition: 'Государственный кадастр недвижимости Италии. Хранит cadastral value (rendita catastale), от которой считаются IMU, регистрационный налог и нотариальные расходы.',
    },
    en: {
      label: 'catasto',
      definition: 'Italy\'s national property cadastre. Holds the rendita catastale used to compute IMU, registration tax, and notary fees.',
    },
  },
  'rendita-catastale': {
    category: 'property',
    ru: {
      label: 'rendita catastale',
      definition: 'Кадастровая стоимость недвижимости (обычно 30-60 % рыночной). База для IMU, регистрационного налога и большинства расчётов.',
    },
    en: {
      label: 'rendita catastale',
      definition: 'The cadastral value of a property (typically 30-60 % of market). The base for IMU, registration tax, and most calculations.',
    },
  },
  'condominio': {
    category: 'property',
    ru: {
      label: 'condominio',
      definition: 'Совет жильцов многоквартирного дома и ежемесячный сбор на содержание. Обычно 80-250 € в месяц; включает лифт, лестницы, отопление при centralizzato.',
    },
    en: {
      label: 'condominio',
      definition: 'A multi-flat building\'s residents\' association and monthly fee for upkeep. Typically 80-250 €/month; covers lift, stairwells, central heating if applicable.',
    },
  },
  'riscaldamento-centralizzato': {
    category: 'property',
    ru: {
      label: 'riscaldamento centralizzato',
      definition: 'Центральное отопление в многоквартирном доме. Работает по графику кондоминиума и регионального закона (обычно 15.10-15.04, 14 ч/сутки на севере).',
    },
    en: {
      label: 'riscaldamento centralizzato',
      definition: 'Central heating in a multi-flat building. Runs on a residents\' schedule and regional law (typically 15 Oct,15 Apr, 14 h/day in the north).',
    },
  },
  'caldaia-autonoma': {
    category: 'property',
    ru: {
      label: 'caldaia autonoma',
      definition: 'Автономный газовый котёл в квартире. Даёт владельцу контроль над температурой, но платит за газ напрямую: 170-280 € в месяц зимой.',
    },
    en: {
      label: 'caldaia autonoma',
      definition: 'A standalone gas boiler in the flat. Gives the owner temperature control but you pay gas directly: 170-280 €/month in winter.',
    },
  },
  'iscrizione-anagrafica': {
    category: 'admin',
    ru: {
      label: 'iscrizione anagrafica',
      definition: 'Регистрация по адресу в Anagrafe муниципалитета. Привязывает к школе, ASL, ATM-зоне города; даёт право голосовать на муниципальных выборах ЕС-гражданам.',
    },
    en: {
      label: 'iscrizione anagrafica',
      definition: 'Address registration at the municipal Anagrafe. Anchors school assignment, ASL, ATM zone; gives EU citizens the right to vote in local elections.',
    },
  },

  // ── Transport / car ─────────────────────────────────────────────────
  'ztl': {
    category: 'transit',
    ru: {
      label: 'ZTL',
      definition: 'Zona a Traffico Limitato, зона ограниченного движения в исторических центрах. Въезд только по пропуску; камеры штрафуют 100-150 € за каждое нарушение.',
    },
    en: {
      label: 'ZTL',
      definition: 'Zona a Traffico Limitato, the restricted-traffic zone in historic centres. Entry only by permit; cameras fine 100-150 € per breach.',
    },
  },
  'area-c': {
    category: 'transit',
    ru: {
      label: 'Area C',
      definition: 'Платная зона въезда в центр Милана (cerchia dei Bastioni). 7,50 € в день для большинства машин; камеры на каждом въезде.',
    },
    en: {
      label: 'Area C',
      definition: 'Milan\'s paid central zone (cerchia dei Bastioni). 7.50 € a day for most cars; cameras on every gate.',
    },
  },
  'bollo': {
    category: 'transit',
    ru: {
      label: 'bollo',
      definition: 'Региональный годовой автоналог за владение машиной. От 180 € (малый объём, эффективный двигатель) до 1 500 € (большой объём, старый Euro-стандарт).',
    },
    en: {
      label: 'bollo',
      definition: 'The annual regional vehicle tax. Ranges 180 € (small, efficient engine) to 1,500 € (large engine, old Euro standard).',
    },
  },
  'rca': {
    category: 'transit',
    ru: {
      label: 'RCA',
      definition: 'Responsabilità Civile Auto, обязательная страховка автогражданской ответственности. 400-1 200 € в год в зависимости от региона, возраста водителя и истории.',
    },
    en: {
      label: 'RCA',
      definition: 'Responsabilità Civile Auto, the mandatory third-party motor insurance. 400-1,200 €/year depending on region, driver age, and claims history.',
    },
  },

  // ── Banking ────────────────────────────────────────────────────────
  'iban': {
    category: 'misc',
    ru: {
      label: 'IBAN',
      definition: 'International Bank Account Number, международный номер счёта. Итальянский IBAN начинается с IT и имеет 27 знаков. Нужен для зарплаты, аренды, коммуналки.',
    },
    en: {
      label: 'IBAN',
      definition: 'International Bank Account Number. Italian IBANs start with IT and are 27 characters. Required for salary, rent, utility payments.',
    },
  },
  'sepa': {
    category: 'misc',
    ru: {
      label: 'SEPA',
      definition: 'Single Euro Payments Area, единая зона евро-платежей (36 стран ЕС + соседи). Переводы внутри SEPA бесплатные или почти бесплатные.',
    },
    en: {
      label: 'SEPA',
      definition: 'Single Euro Payments Area, the EU\'s 36-country payment zone. Transfers within SEPA are free or near-free.',
    },
  },
  'mensa': {
    category: 'education',
    ru: {
      label: 'mensa',
      definition: 'Школьная столовая в публичной школе. Платная по ISEE: 80-150 € в месяц на ребёнка.',
    },
    en: {
      label: 'mensa',
      definition: 'The public-school cafeteria. ISEE-based fee: 80-150 € a month per child.',
    },
  },

  // ── Visa / immigration ─────────────────────────────────────────────
  'nulla-osta': {
    category: 'admin',
    ru: {
      label: 'nulla osta',
      definition: 'Разрешительная справка-«нет препятствий» от итальянского ведомства. Часто шаг при найме иностранного работника или воссоединении семьи.',
    },
    en: {
      label: 'nulla osta',
      definition: 'A "no objections" clearance from an Italian authority. A common step when hiring a foreign worker or processing family reunification.',
    },
  },
  'kit-postale': {
    category: 'admin',
    ru: {
      label: 'kit postale',
      definition: 'Стандартный почтовый набор для подачи документов на permesso di soggiorno (продление, замена). Заполняется в любом почтовом отделении.',
    },
    en: {
      label: 'kit postale',
      definition: 'The standard postal-application pack for permesso di soggiorno (renewal, replacement). Filed at any post office.',
    },
  },
  'decreto-flussi': {
    category: 'admin',
    ru: {
      label: 'Decreto Flussi',
      definition: 'Годовой декрет правительства, квота на трудовые ВНЖ для иностранцев. Окно подачи раз в год; в 2024-2026 расширен до 165 000 мест.',
    },
    en: {
      label: 'Decreto Flussi',
      definition: 'The annual government decree setting work-permit quotas for foreigners. Opens once a year; expanded to 165,000 slots for 2024-2026.',
    },
  },

  // ── Safety / police ─────────────────────────────────────────────────
  'carabinieri': {
    category: 'misc',
    ru: {
      label: 'Carabinieri',
      definition: 'Военизированная жандармерия Италии, подчинена Министерству обороны. Параллельно с Polizia di Stato обеспечивает порядок; единственная служба, представленная даже в маленьких городках.',
    },
    en: {
      label: 'Carabinieri',
      definition: 'Italy\'s military gendarmerie, under the Ministry of Defence. Operates alongside the Polizia di Stato; the only force with a station in nearly every small town.',
    },
  },
  'polizia-di-stato': {
    category: 'misc',
    ru: {
      label: 'Polizia di Stato',
      definition: 'Государственная гражданская полиция Италии, подчинена МВД. Отвечает за уголовную полицию, пограничный контроль, дорожный надзор на автострадах.',
    },
    en: {
      label: 'Polizia di Stato',
      definition: 'Italy\'s civilian state police, under the Ministry of the Interior. Handles criminal investigations, border control, and highway patrol.',
    },
  },
  'guardia-di-finanza': {
    category: 'misc',
    ru: {
      label: 'Guardia di Finanza',
      definition: 'Финансово-налоговая полиция Италии, подчинена Минфину. Расследует уклонение от налогов, контрабанду, отмывание; принимает жалобы на отказ магазина принять карту.',
    },
    en: {
      label: 'Guardia di Finanza',
      definition: 'Italy\'s financial and tax police, under the Ministry of Finance. Investigates tax evasion, smuggling, money-laundering; receives complaints when a merchant refuses card payment.',
    },
  },
  'vigili-urbani': {
    category: 'misc',
    ru: {
      label: 'Polizia Locale',
      definition: 'Муниципальная полиция (исторически называли vigili urbani). Парковки, ZTL-нарушения, бытовые жалобы, муниципальные правонарушения. Не носит огнестрельное оружие в большинстве комунн.',
    },
    en: {
      label: 'Polizia Locale',
      definition: 'The municipal police (historically called vigili urbani). Handles parking, ZTL breaches, neighbourhood complaints, local ordinances. Largely unarmed in most municipalities.',
    },
  },
  'codice-rosso': {
    category: 'misc',
    ru: {
      label: 'Codice Rosso',
      definition: 'Закон 69/2019 о приоритетном расследовании домашнего и гендерного насилия. Жалоба в Carabinieri или Polizia запускает 3-дневный срок реагирования; включает ускоренный охранный ордер.',
    },
    en: {
      label: 'Codice Rosso',
      definition: 'Law 69/2019 fast-tracking domestic and gender-violence investigations. Filing with Carabinieri or Polizia triggers a 3-day response window; includes an expedited protection order.',
    },
  },
  'gpi': {
    category: 'misc',
    ru: {
      label: 'GPI',
      definition: 'Global Peace Index от Institute for Economics and Peace. Оценивает страны по 23 индикаторам безопасности; меньше значение, безопаснее. Италия в 2025, около 1,6 (38-е место в мире).',
    },
    en: {
      label: 'GPI',
      definition: 'Global Peace Index by the Institute for Economics and Peace. Scores countries on 23 safety indicators; lower is safer. Italy 2025 at 1.6 (around 38th globally).',
    },
  },

  // ── Language / culture ──────────────────────────────────────────────
  'cils': {
    category: 'education',
    ru: {
      label: 'CILS',
      definition: 'Certificazione di Italiano come Lingua Straniera, государственный сертификат Università per Stranieri di Siena. Уровни A1-C2; B1 требуется для гражданства с 2018.',
    },
    en: {
      label: 'CILS',
      definition: 'Certificazione di Italiano come Lingua Straniera, the state certificate from the Università per Stranieri di Siena. Levels A1-C2; B1 is required for citizenship since 2018.',
    },
  },
  'celi': {
    category: 'education',
    ru: {
      label: 'CELI',
      definition: 'Certificato di Conoscenza della Lingua Italiana от Università per Stranieri di Perugia. Альтернатива CILS, признаётся МИДом Италии и большинством университетов.',
    },
    en: {
      label: 'CELI',
      definition: 'Certificato di Conoscenza della Lingua Italiana from the Università per Stranieri di Perugia. CILS\'s sibling certificate; accepted by the Italian MFA and most universities.',
    },
  },
  'plida': {
    category: 'education',
    ru: {
      label: 'PLIDA',
      definition: 'Progetto Lingua Italiana Dante Alighieri, сертификат от Società Dante Alighieri. Третий принятый стандарт; чаще всего сдают в зарубежных культурных центрах Данте.',
    },
    en: {
      label: 'PLIDA',
      definition: 'Progetto Lingua Italiana Dante Alighieri, the certificate from the Società Dante Alighieri. The third accepted standard; most often taken at overseas Dante cultural centres.',
    },
  },
  'aperitivo': {
    category: 'misc',
    ru: {
      label: 'aperitivo',
      definition: 'Социальный ритуал «бокал и закуски перед ужином» (обычно 18:00-21:00). В Милане за €8-15 получают спритц или негрони + буфет; в Риме формат скромнее.',
    },
    en: {
      label: 'aperitivo',
      definition: 'The "drink and snacks before dinner" social ritual (usually 18:00-21:00). In Milan €8-15 buys an Aperol Spritz or Negroni plus a buffet; Rome runs a leaner version.',
    },
  },
  'passeggiata': {
    category: 'misc',
    ru: {
      label: 'passeggiata',
      definition: 'Вечерняя прогулка по центру города как социальный ритуал (около 18:00 на юге, позже на севере). Не спорт и не транзит, способ показаться и обменяться приветствиями.',
    },
    en: {
      label: 'passeggiata',
      definition: 'The evening stroll through town as a social ritual (around 18:00 on the south, later on the north). Neither exercise nor transit; it is being seen and exchanging greetings.',
    },
  },
  'sagra': {
    category: 'misc',
    ru: {
      label: 'sagra',
      definition: 'Сельский гастрономический фестиваль, посвящённый одному продукту (трюфель в Альбе, фисташки в Бронте). Сотни проходят с июня по октябрь; вход бесплатный, еда дешёвая.',
    },
    en: {
      label: 'sagra',
      definition: 'A rural food festival dedicated to a single product (truffles in Alba, pistachios in Bronte). Hundreds run June to October; free entry, cheap food.',
    },
  },
  'comune': {
    category: 'admin',
    ru: {
      label: 'comune',
      definition: 'Муниципалитет, базовая административная единица Италии. 7 904 коммун в 2026; каждая ведёт свой Anagrafe, школьные кварталы, ZTL, локальные налоги.',
    },
    en: {
      label: 'comune',
      definition: 'The municipality, Italy\'s base administrative unit. 7,904 in 2026; each runs its own Anagrafe, school catchments, ZTL, and local taxes.',
    },
  },
  'ef-epi': {
    category: 'misc',
    ru: {
      label: 'EF EPI',
      definition: 'EF English Proficiency Index — ежегодный рейтинг владения английским среди взрослых, который составляет EF Education First. Строится по результатам бесплатного онлайн-теста EF, поэтому выборка самоотобранная, а балл — скользящее среднее за три года.',
    },
    en: {
      label: 'EF EPI',
      definition: 'The EF English Proficiency Index, an annual ranking of adult English skills published by EF Education First. It is built from people who take EF\'s free online test, so the sample is self-selected, and scores are a three-year rolling average.',
    },
  },
  'dialetto': {
    category: 'misc',
    ru: {
      label: 'dialetto',
      definition: 'Региональный язык Италии, не диалект в строгом смысле, отдельный язык со своей грамматикой. Napoletano, Siciliano, Veneziano, Romanesco, до сих пор живые дома; UNESCO защищает несколько.',
    },
    en: {
      label: 'dialetto',
      definition: 'A regional Italian language, not strictly a dialect but a separate language with its own grammar. Napoletano, Siciliano, Veneziano, Romanesco are still spoken at home; UNESCO protects several.',
    },
  },

  // ── Infrastructure / utilities ──────────────────────────────────────
  'pec': {
    category: 'admin',
    ru: {
      label: 'PEC',
      definition: 'Posta Elettronica Certificata, юридически признанная электронная почта Италии. Обязательна для общения с госорганами и работодателем; €5-20/год через Aruba, Poste Italiane, Legalmail.',
    },
    en: {
      label: 'PEC',
      definition: 'Posta Elettronica Certificata, Italy\'s legally recognised email. Required for government and employer correspondence; €5-20/year via Aruba, Poste Italiane, Legalmail.',
    },
  },
  'fibra': {
    category: 'misc',
    ru: {
      label: 'fibra',
      definition: 'Оптоволоконный интернет. В Италии существует FTTH (до квартиры, 1-2,5 Гбит/с) и FTTC (до шкафа в районе, до 200 Мбит/с); фактическая скорость зависит от типа.',
    },
    en: {
      label: 'fibra',
      definition: 'Fibre internet. Italy offers FTTH (to the flat, 1-2.5 Gbps) and FTTC (to the street cabinet, up to 200 Mbps); real speed depends on which.',
    },
  },
  'fwa': {
    category: 'misc',
    ru: {
      label: 'FWA',
      definition: 'Fixed Wireless Access, фиксированный беспроводной интернет для районов без оптоволокна. 4G/5G радиоприёмник на крыше; скорость 30-200 Мбит/с в зависимости от расстояния до вышки.',
    },
    en: {
      label: 'FWA',
      definition: 'Fixed Wireless Access, the wireless broadband alternative for areas without fibre. A 4G/5G rooftop receiver; 30-200 Mbps depending on tower distance.',
    },
  },
  'poste-italiane': {
    category: 'misc',
    ru: {
      label: 'Poste Italiane',
      definition: 'Итальянская почта; одновременно крупный банк (BancoPosta), оператор посылок и место подачи документов на permesso di soggiorno (kit postale).',
    },
    en: {
      label: 'Poste Italiane',
      definition: 'The Italian postal service; doubles as a major bank (BancoPosta), parcel courier, and the filing point for permesso di soggiorno renewals (kit postale).',
    },
  },
  'tari-rifiuti': {
    category: 'property',
    ru: {
      label: 'raccolta differenziata',
      definition: 'Раздельный сбор мусора в Италии. Большинство коммун требуют 5+ потоков: бумага, стекло, пластик-металл, органика, остаток; нарушение, штраф 25-500 €.',
    },
    en: {
      label: 'raccolta differenziata',
      definition: 'Italian separated waste collection. Most comuni require 5+ streams: paper, glass, plastic-metal, organic, residual; non-compliance fines 25-500 €.',
    },
  },

  // ── Portugal ────────────────────────────────────────────────────────
  'nif': {
    category: 'admin',
    ru: {
      label: 'NIF',
      definition: 'Número de Identificação Fiscal — португальский налоговый номер, девять цифр. Бесплатно в Finanças или удалённо через адвоката (€100-200). Нужен для любого контракта, счёта в банке, аренды.',
    },
    en: {
      label: 'NIF',
      definition: 'Número de Identificação Fiscal — Portugal\'s nine-digit tax ID. Issued free at Finanças (in person) or via lawyer remotely (€100-200). Required for any contract, bank account, lease.',
    },
  },
  'nhr': {
    category: 'tax',
    ru: {
      label: 'NHR',
      definition: 'Non-Habitual Resident — португальский налоговый режим 2009-2023, давший новоприбывшим 10 % на пенсии и 20 % на «высококвалифицированный» труд. Закрыт для новых заявок с 1 января 2024 года; действующие держатели сохраняют статус до конца 10-летнего срока.',
    },
    en: {
      label: 'NHR',
      definition: 'Non-Habitual Resident — Portugal\'s 2009-2023 tax regime giving newcomers 10 % on pensions and 20 % on "high-value-added" work. Closed to new applications from 1 January 2024; existing holders keep status for the rest of the 10-year run.',
    },
  },
  'ifici': {
    category: 'tax',
    ru: {
      label: 'IFICI',
      definition: 'Incentivo Fiscal à Investigação Científica e Inovação — преемник NHR с 2024 года. Узкий режим: только для R&D-учёных, IT-инженеров в признанных компаниях, академического персонала. 20 % flat на трудовой доход 10 лет, освобождение от налога на зарубежный доход.',
    },
    en: {
      label: 'IFICI',
      definition: 'Incentivo Fiscal à Investigação Científica e Inovação — the post-2024 successor to NHR. Narrow scope: R&D scientists, IT engineers at recognised companies, academic staff. 20 % flat on Portuguese employment income for 10 years, exemption on most foreign income.',
    },
  },
  'd7': {
    category: 'admin',
    ru: {
      label: 'D7',
      definition: 'Португальская виза для пассивного дохода. Минимум €870 в месяц на основного заявителя (один минимальный оклад страны). Подходит пенсионерам, рантье, держателям дивидендов. Ведёт к ВНЖ за 4-6 месяцев.',
    },
    en: {
      label: 'D7',
      definition: 'Portugal\'s passive-income visa. Minimum €870/month of stable income for the main applicant (one national minimum wage). Fits retirees, rentiers, dividend holders. Leads to residency in 4-6 months.',
    },
  },
  'd8': {
    category: 'admin',
    ru: {
      label: 'D8',
      definition: 'Португальская виза для цифровых кочевников (с октября 2022). Минимум €3 480/мес дохода удалённо (четыре минимальных оклада). Подходит наёмным сотрудникам зарубежной компании и фрилансерам.',
    },
    en: {
      label: 'D8',
      definition: 'Portugal\'s Digital Nomad Visa (since October 2022). Minimum €3,480/month remote income (four times the minimum wage). Fits salaried employees of a foreign company and freelancers.',
    },
  },
  'd2': {
    category: 'admin',
    ru: {
      label: 'D2',
      definition: 'Португальская виза предпринимателя для создания бизнеса в Португалии. Без минимального капитала, но требуется бизнес-план + регистрация компании.',
    },
    en: {
      label: 'D2',
      definition: 'Portugal\'s entrepreneur visa for founding a business in Portugal. No fixed capital requirement but a viable business plan and company registration are required.',
    },
  },
  'aima': {
    category: 'admin',
    ru: {
      label: 'AIMA',
      definition: 'Agência para a Integração, Migrações e Asilo — португальское миграционное агентство, заменившее SEF в октябре 2023. Выдаёт ВНЖ, продлевает permesso. Перегружено: запись в очередь, до 12 месяцев.',
    },
    en: {
      label: 'AIMA',
      definition: 'Agência para a Integração, Migrações e Asilo — Portugal\'s migration agency that replaced SEF in October 2023. Issues residency permits and renewals. Overloaded; appointment queues run up to 12 months.',
    },
  },
  'sef': {
    category: 'admin',
    ru: {
      label: 'SEF',
      definition: 'Serviço de Estrangeiros e Fronteiras — бывшая португальская иммиграционная служба, расформирована в октябре 2023. Функции переданы AIMA (гражданские) и PSP/GNR (пограничный контроль).',
    },
    en: {
      label: 'SEF',
      definition: 'Serviço de Estrangeiros e Fronteiras — Portugal\'s former immigration service, dissolved October 2023. Functions split between AIMA (civilian) and PSP/GNR (border control).',
    },
  },
  'sns': {
    category: 'health',
    ru: {
      label: 'SNS (PT)',
      definition: 'Serviço Nacional de Saúde — португальская национальная медицина. Бесплатна для резидентов с número de utente; копейки за визит к врачу (~€5), бесплатные госпитализации. Сильно перегружена в Лиссабоне и Алгарви.',
    },
    en: {
      label: 'SNS (PT)',
      definition: 'Serviço Nacional de Saúde — Portugal\'s national health service. Free for residents with a número de utente; nominal copay for a GP visit (~€5), free inpatient care. Heavily overloaded in Lisbon and the Algarve.',
    },
  },
  'irs-pt': {
    category: 'tax',
    ru: {
      label: 'IRS',
      definition: 'Imposto sobre o Rendimento das Pessoas Singulares — португальский подоходный налог. Прогрессивный 13,25 %-48 %. Декларация раз в год (Modelo 3) через сайт Portal das Finanças.',
    },
    en: {
      label: 'IRS',
      definition: 'Imposto sobre o Rendimento das Pessoas Singulares — Portugal\'s personal income tax. Progressive 13.25-48 %. Annual return (Modelo 3) filed via the Portal das Finanças.',
    },
  },
  'imi': {
    category: 'property',
    ru: {
      label: 'IMI',
      definition: 'Imposto Municipal sobre Imóveis — португальский муниципальный налог на недвижимость, 0,3-0,8 % от налоговой стоимости (VPT). Платится владельцем в апреле, мае и ноябре.',
    },
    en: {
      label: 'IMI',
      definition: 'Imposto Municipal sobre Imóveis — Portugal\'s municipal property tax, 0.3-0.8 % of the cadastral value (VPT). Owner pays in three instalments (April, May, November).',
    },
  },
  'imt': {
    category: 'property',
    ru: {
      label: 'IMT',
      definition: 'Imposto Municipal sobre as Transmissões Onerosas — португальский налог на покупку недвижимости. 0-7,5 % прогрессивно; для первого жилья освобождение до €92 407.',
    },
    en: {
      label: 'IMT',
      definition: 'Imposto Municipal sobre as Transmissões Onerosas — Portugal\'s property transfer tax. 0-7.5 % progressive; primary residence exempt up to €92,407.',
    },
  },
  'finanças': {
    category: 'admin',
    ru: {
      label: 'Finanças',
      definition: 'Autoridade Tributária e Aduaneira — португальская налоговая служба. Аналог Agenzia delle Entrate в Италии. Сайт Portal das Finanças для деклараций и налоговых писем.',
    },
    en: {
      label: 'Finanças',
      definition: 'Autoridade Tributária e Aduaneira — Portugal\'s revenue agency. Equivalent of Italy\'s Agenzia delle Entrate. Portal das Finanças handles returns and correspondence.',
    },
  },
  'golden-visa-pt': {
    category: 'admin',
    ru: {
      label: 'Golden Visa (PT)',
      definition: 'Португальская виза за инвестиции. С октября 2023 исключены покупка недвижимости и банковские депозиты; остались инвестиции в венчурные фонды (€500 000), исследования (€500 000), культурное наследие (€250 000). 5 лет до гражданства.',
    },
    en: {
      label: 'Golden Visa (PT)',
      definition: 'Portugal\'s investor visa. Since October 2023 real-estate purchase and bank deposits are no longer eligible; venture funds (€500k), research (€500k), and cultural heritage (€250k) remain. 5-year residency clock to citizenship.',
    },
  },
  'numero-de-utente': {
    category: 'health',
    ru: {
      label: 'número de utente',
      definition: 'Девятизначный пациентский номер SNS. Выдаётся центром здоровья (centro de saúde) после получения пластиковой карты ВНЖ от AIMA. Без него врач SNS не прикрепляет к участку, рецепт не выписывается, госпиталь принимает только по платному тарифу.',
    },
    en: {
      label: 'número de utente',
      definition: 'Nine-digit SNS patient ID. Issued by the local centro de saúde after the AIMA residence card lands. Without it the public GP will not register you, prescriptions are not issued, and hospitals only treat you on the private price list.',
    },
  },
  'medico-de-familia': {
    category: 'health',
    ru: {
      label: 'médico de família',
      definition: 'Прикреплённый семейный врач SNS, аналог итальянского medico di base. Принимает в centro de saúde, выписывает рецепты и направления к специалистам. В Лиссабоне и Алгарви очередь на прикрепление к новому пациенту, до 6 месяцев; в провинции, недели.',
    },
    en: {
      label: 'médico de família',
      definition: 'The assigned SNS family doctor, Portugal\'s equivalent of an Italian medico di base. Sees patients at the centro de saúde, writes prescriptions and specialist referrals. In Lisbon and the Algarve the wait to be assigned can run up to six months; in smaller towns, weeks.',
    },
  },
  'centro-de-saude': {
    category: 'health',
    ru: {
      label: 'centro de saúde',
      definition: 'Первичный центр здоровья SNS по месту прописки. Здесь регистрируют número de utente, прикрепляют médico de família, делают вакцинацию, забирают анализы. Каждый муниципалитет разделён на округа, и центр выбирается по адресу, а не по желанию.',
    },
    en: {
      label: 'centro de saúde',
      definition: 'The neighbourhood SNS primary-care clinic. This is where the número de utente is issued, the family doctor is assigned, routine vaccinations and basic labs happen. Catchment is by address, not by choice.',
    },
  },
  'adse': {
    category: 'health',
    ru: {
      label: 'ADSE',
      definition: 'Assistência na Doença aos Servidores do Estado, ведомственная медицинская страховка для госслужащих Португалии. Параллельно с SNS даёт доступ к частным сетям (Lusíadas, CUF, HPA) с низкими копейками. Для эмигрантов недоступна, кроме случаев найма в государственный сектор.',
    },
    en: {
      label: 'ADSE',
      definition: 'Assistência na Doença aos Servidores do Estado, the supplementary health scheme for Portuguese civil servants. Runs alongside SNS and unlocks the private hospital networks (Lusíadas, CUF, HPA) at low copays. Not open to most expatriates, only to those hired into the public sector.',
    },
  },
  'inem': {
    category: 'health',
    ru: {
      label: 'INEM',
      definition: 'Instituto Nacional de Emergência Médica, португальская служба скорой. Активируется через единый номер 112; INEM сам решает, нужна ли реанимобиль (SIV/VMER) или обычная карета. Время прибытия в Лиссабоне и Порту 10-15 минут, в сельских районах, до 30.',
    },
    en: {
      label: 'INEM',
      definition: 'Instituto Nacional de Emergência Médica, Portugal\'s emergency medical service. Dispatched through the European 112 number; INEM decides whether a paramedic vehicle (SIV/VMER) or a basic ambulance is sent. Response time in Lisbon and Porto runs 10-15 minutes; rural areas, up to 30.',
    },
  },
  'urgencias-triage': {
    category: 'health',
    ru: {
      label: 'triagem de Manchester',
      definition: 'Цветовая система сортировки в португальских отделениях неотложной помощи (urgências): vermelho (немедленно), laranja (до 10 минут), amarelo (до часа), verde (до двух часов), azul (не срочно, до четырёх часов). Соответствует международному стандарту Манчестерского триажа.',
    },
    en: {
      label: 'Manchester triage',
      definition: 'The colour-coded sorting used at Portuguese A&E departments (urgências): vermelho (immediate), laranja (within 10 minutes), amarelo (within an hour), verde (within two), azul (non-urgent, within four). Matches the international Manchester Triage System.',
    },
  },
  'vpt': {
    category: 'property',
    ru: {
      label: 'VPT',
      definition: 'Valor Patrimonial Tributário, кадастровая стоимость объекта в португальском реестре Finanças. Обычно составляет 60-80 % от рыночной цены. От VPT считаются IMI, IMT и AIMI; рыночная цена в этих формулах не участвует.',
    },
    en: {
      label: 'VPT',
      definition: 'Valor Patrimonial Tributário, the cadastral value held in the Finanças register. Typically 60-80 % of market price. IMI, IMT and AIMI are computed on VPT; market price does not enter those formulas.',
    },
  },
  'aimi': {
    category: 'property',
    ru: {
      label: 'AIMI',
      definition: 'Adicional ao IMI, надбавка к муниципальному налогу для собственников недвижимости с суммарной VPT свыше €600 000 (€1,2 млн на пару). Прогрессивная шкала 0,7-1,5 %. Платится один раз в год отдельно от IMI.',
    },
    en: {
      label: 'AIMI',
      definition: 'Adicional ao IMI, a surcharge for owners with total VPT above €600,000 (€1.2m for a couple). Progressive 0.7-1.5 % bands. Filed and paid annually, separate from IMI.',
    },
  },
  'caderneta-predial': {
    category: 'property',
    ru: {
      label: 'caderneta predial',
      definition: 'Налоговый паспорт объекта недвижимости, выгружается из Portal das Finanças. Содержит адрес, площадь, год постройки, VPT, имя владельца. Без актуальной caderneta predial нотариус сделку не оформит.',
    },
    en: {
      label: 'caderneta predial',
      definition: 'A property\'s tax record in the Portal das Finanças. Lists address, area, year built, VPT, and owner. Without a fresh caderneta predial no Portuguese notary will sign the deed.',
    },
  },
  'alojamento-local': {
    category: 'property',
    ru: {
      label: 'Alojamento Local',
      definition: 'Лицензия на краткосрочную сдачу жилья в Португалии (аналог итальянской CIN). С 2023 года выдача новых лицензий заморожена в Лиссабоне, Порту и большинстве курортных приходов. Уже выданные продлеваются, но не передаются.',
    },
    en: {
      label: 'Alojamento Local',
      definition: 'Portugal\'s short-term rental licence (the equivalent of Italy\'s CIN). New issuances have been frozen in Lisbon, Porto and most coastal parishes since 2023. Existing licences renew but cannot be transferred.',
    },
  },
  'imposto-do-selo': {
    category: 'property',
    ru: {
      label: 'Imposto do Selo',
      definition: 'Гербовый сбор Португалии. На покупку жилья, 0,8 % от цены сделки; на ипотеку дополнительно 0,5-0,6 % от суммы кредита. Платится один раз при оформлении.',
    },
    en: {
      label: 'Imposto do Selo',
      definition: 'Portugal\'s stamp duty. On property purchase, 0.8 % of the contract price; on a mortgage, an additional 0.5-0.6 % of the loan amount. Paid once at deed signing.',
    },
  },
  'conservatoria-predial': {
    category: 'property',
    ru: {
      label: 'Conservatória do Registo Predial',
      definition: 'Земельный реестр Португалии. Хранит titularidade (право собственности) и ônus (обременения, ипотеку, аресты). Свежий certidão permanente заказывается онлайн за €15 и обязателен перед сделкой.',
    },
    en: {
      label: 'Conservatória do Registo Predial',
      definition: 'Portugal\'s land registry. Holds titularidade (ownership) and ônus (encumbrances, mortgages, seizures). A fresh certidão permanente is ordered online for €15 and is mandatory before signing.',
    },
  },
  'renda-acessivel': {
    category: 'property',
    ru: {
      label: 'Renda Acessível',
      definition: 'Государственная программа доступной аренды Португалии. Жильё сдаётся ниже рыночной ставки в обмен на налоговую льготу владельцу. На практике предложение слабое и контроль за нарушениями условный.',
    },
    en: {
      label: 'Renda Acessível',
      definition: 'Portugal\'s state affordable-rental programme. Owners let units at below-market rents in exchange for a tax break. Supply is thin in practice and enforcement is weak.',
    },
  },
  'salario-minimo-pt': {
    category: 'tax',
    ru: {
      label: 'SMN',
      definition: 'Salário Mínimo Nacional, португальский минимальный оклад. В 2026 году €920 в месяц (14 выплат). К нему привязаны пороги виз D7, D8, D2 и часть социальных льгот.',
    },
    en: {
      label: 'SMN',
      definition: 'Salário Mínimo Nacional, Portugal\'s statutory minimum monthly wage. In 2026 it stands at €920 (paid over 14 instalments). D7, D8 and D2 visa thresholds, plus several social benefits, are pegged to it.',
    },
  },
  'bica': {
    category: 'misc',
    ru: {
      label: 'bica',
      definition: 'Лиссабонское название эспрессо (в Порту, cimbalino). Подаётся в маленькой чашке за стойкой; цена в обычном кафе €0,80-1,20. Базовая единица португальского кофейного бюджета.',
    },
    en: {
      label: 'bica',
      definition: 'Lisbon shorthand for an espresso (Porto uses cimbalino). Served standing at the counter for €0.80-1.20 in an ordinary café. The base unit of a Portuguese coffee budget.',
    },
  },
  'prato-do-dia': {
    category: 'misc',
    ru: {
      label: 'prato do dia',
      definition: 'Дневное меню в португальских tasca: суп + основное + хлеб + кофе за €8-15. Главный способ обедать вне дома без курсового счёта; почти всегда дешевле супермаркета по компонентам.',
    },
    en: {
      label: 'prato do dia',
      definition: 'The daily set lunch in a Portuguese tasca: soup, main, bread and a coffee for €8-15. The main affordable way to eat out and usually cheaper per portion than buying the same ingredients in a supermarket.',
    },
  },
  'navegante': {
    category: 'transit',
    ru: {
      label: 'Navegante',
      definition: 'Месячный единый проездной Лиссабонской агломерации (метро + автобусы Carris + пригородные поезда CP). Зона Лиссабона €40, расширенная зона €30. Платится с карты Navegante.',
    },
    en: {
      label: 'Navegante',
      definition: 'The Lisbon-metro monthly pass (metro + Carris buses + CP suburban trains). Inner Lisbon zone €40, extended metropolitan zone €30. Loaded onto the Navegante card.',
    },
  },
  'andante': {
    category: 'transit',
    ru: {
      label: 'Andante',
      definition: 'Месячный проездной агломерации Порту (метро + STCP-автобусы + пригородные поезда). Базовый Andante €30 в месяц, единая тарифная зона по большинству Большого Порту.',
    },
    en: {
      label: 'Andante',
      definition: 'The Porto-area monthly pass (metro + STCP buses + suburban trains). Base Andante runs €30 per month, with a unified fare zone across most of Greater Porto.',
    },
  },
  'certificado-energetico': {
    category: 'property',
    ru: {
      label: 'Certificado Energético',
      definition: 'Энергетический паспорт здания, шкала A-G. Обязателен при продаже или сдаче в долгосрочную аренду в Португалии. Старые лиссабонские дома часто получают E-G, что отражается на счетах за электричество.',
    },
    en: {
      label: 'Certificado Energético',
      definition: 'The building\'s energy-performance certificate, on an A-G scale. Mandatory before any sale or long-term let in Portugal. Older Lisbon buildings frequently land in E-G, which feeds straight into power bills.',
    },
  },

  // ── Spain ───────────────────────────────────────────────────────────
  'nie': {
    category: 'admin',
    ru: {
      label: 'NIE',
      definition: 'Número de Identidad de Extranjero — испанский идентификатор для иностранцев. Буква + 7 цифр + контрольная буква. Получается в Comisaría или в консульстве. Нужен для любой сделки, контракта, счёта.',
    },
    en: {
      label: 'NIE',
      definition: 'Número de Identidad de Extranjero — Spain\'s foreigner ID. A letter, 7 digits, and a check letter. Obtained at the Comisaría or at a consulate. Required for any transaction, contract, bank account.',
    },
  },
  'empadronamiento': {
    category: 'admin',
    ru: {
      label: 'empadronamiento',
      definition: 'Регистрация по адресу в padrón municipal (городском реестре жителей). Бесплатно в ayuntamiento. Привязывает к школьному кварталу, центру здоровья, голосованию на местных выборах ЕС-гражданам. Обновляется раз в 5 лет.',
    },
    en: {
      label: 'empadronamiento',
      definition: 'Address registration in the padrón municipal (the city\'s resident roster). Free at the ayuntamiento. Anchors school catchment, health centre, EU-citizen voting in local elections. Refreshed every 5 years.',
    },
  },
  'beckham': {
    category: 'tax',
    ru: {
      label: 'Ley Beckham',
      definition: 'Испанский льготный налоговый режим для импатриатов (закон 35/2006). Иностранец-новичок платит фиксированные 24 % на испанский доход до €600 000 за 6 лет, без налога на зарубежный (кроме страны льгот). Обновлён в 2023 году — теперь доступен фрилансерам.',
    },
    en: {
      label: 'Ley Beckham',
      definition: 'Spain\'s special impatriate tax regime (Law 35/2006). A newcomer pays a flat 24 % on Spanish income up to €600,000 for 6 years, with no tax on foreign income (except passive). Updated in 2023 to also cover freelancers.',
    },
  },
  'autonomo': {
    category: 'tax',
    ru: {
      label: 'autónomo',
      definition: 'Испанский статус самозанятого / ИП. Регистрация в Agencia Tributaria + Seguridad Social. Минимальный взнос в SS €230-350/мес в первый год (льгота), потом €310-1 270/мес по доходу.',
    },
    en: {
      label: 'autónomo',
      definition: 'Spain\'s self-employed status. Registration with the Agencia Tributaria + Seguridad Social. Minimum SS contribution €230-350/month in year one (relief), then €310-1,270/month by income tier.',
    },
  },
  'modelo-720': {
    category: 'tax',
    ru: {
      label: 'Modelo 720',
      definition: 'Испанская декларация зарубежных активов свыше €50 000 (счета, недвижимость, инвестиции). Подаётся ежегодно до 31 марта резидентами. После проигрыша Испании в Суде ЕС (2022) штрафы стали умеренные, но обязанность подавать осталась.',
    },
    en: {
      label: 'Modelo 720',
      definition: 'Spain\'s declaration of foreign assets above €50,000 (accounts, real estate, investments). Filed yearly by 31 March by residents. After Spain\'s 2022 ECJ defeat fines became moderate, but the filing duty remains.',
    },
  },
  'irpf': {
    category: 'tax',
    ru: {
      label: 'IRPF',
      definition: 'Impuesto sobre la Renta de las Personas Físicas — испанский подоходный налог. Прогрессивный 19-47 % (плюс региональная часть, может добавить ещё 1-3 %).',
    },
    en: {
      label: 'IRPF',
      definition: 'Impuesto sobre la Renta de las Personas Físicas — Spain\'s personal income tax. Progressive 19-47 % (plus a regional surcharge of 1-3 % depending on autonomous community).',
    },
  },
  'agencia-tributaria': {
    category: 'admin',
    ru: {
      label: 'Agencia Tributaria',
      definition: 'Испанская налоговая (AEAT). Подача деклараций IRPF, IVA, Modelo 720 через сайт agenciatributaria.es. Аналог Agenzia delle Entrate и Finanças.',
    },
    en: {
      label: 'Agencia Tributaria',
      definition: 'Spain\'s tax agency (AEAT). IRPF, IVA, Modelo 720 returns are filed at agenciatributaria.es. Equivalent of Italy\'s Agenzia delle Entrate and Portugal\'s Finanças.',
    },
  },
  'ibi': {
    category: 'property',
    ru: {
      label: 'IBI',
      definition: 'Impuesto sobre Bienes Inmuebles — испанский муниципальный налог на недвижимость. 0,4-1,1 % от кадастровой стоимости, ставка ставится ayuntamiento. Платится ежегодно в одной квитанции.',
    },
    en: {
      label: 'IBI',
      definition: 'Impuesto sobre Bienes Inmuebles — Spain\'s municipal property tax. 0.4-1.1 % of the cadastral value, set by the ayuntamiento. Billed once a year.',
    },
  },
  'itp': {
    category: 'property',
    ru: {
      label: 'ITP',
      definition: 'Impuesto sobre Transmisiones Patrimoniales — испанский налог на покупку б/у недвижимости. 6-10 % от цены, ставка регулируется comunidad autónoma. На новое жильё вместо ITP взимается IVA 10 %.',
    },
    en: {
      label: 'ITP',
      definition: 'Impuesto sobre Transmisiones Patrimoniales — Spain\'s second-hand property purchase tax. 6-10 % of price, set by the comunidad autónoma. New-build purchases pay IVA at 10 % instead.',
    },
  },
  'comunidad-autonoma': {
    category: 'admin',
    ru: {
      label: 'comunidad autónoma',
      definition: 'Автономная община Испании. 17 регионов + 2 автономных города; каждая имеет свой парламент, бюджет, региональные налоги и часть IRPF-надбавки. Здравоохранение, образование, региональная полиция переданы общинам.',
    },
    en: {
      label: 'comunidad autónoma',
      definition: 'Spain\'s autonomous community. 17 regions + 2 autonomous cities; each has its own parliament, budget, regional taxes, and IRPF surcharge. Health, education, regional police are devolved.',
    },
  },
  'ayuntamiento': {
    category: 'admin',
    ru: {
      label: 'ayuntamiento',
      definition: 'Муниципалитет Испании. Около 8 100 в стране. Ведёт padrón, выдаёт IBI, лицензирует местный бизнес. Аналог итальянского comune.',
    },
    en: {
      label: 'ayuntamiento',
      definition: 'Spain\'s municipality. Around 8,100 nationwide. Maintains the padrón, bills IBI, licenses local business. The Spanish counterpart of an Italian comune.',
    },
  },
  'snservicio': {
    category: 'health',
    ru: {
      label: 'SNS (ES)',
      definition: 'Sistema Nacional de Salud — испанская национальная медицина. Бесплатна для резидентов с tarjeta sanitaria; организация на уровне comunidad autónoma (CatSalud, SermAS, SACYL и т.д.). Сильно отличается по качеству между регионами.',
    },
    en: {
      label: 'SNS (ES)',
      definition: 'Sistema Nacional de Salud — Spain\'s national health service. Free for residents with a tarjeta sanitaria; organised at the comunidad autónoma level (CatSalud, SermAS, SACYL, etc.). Quality varies sharply between regions.',
    },
  },
  'tarjeta-sanitaria': {
    category: 'health',
    ru: {
      label: 'tarjeta sanitaria',
      definition: 'Карточка испанской SNS. Выдаётся в центре здоровья (centro de salud) по адресу регистрации, после empadronamiento + permiso de residencia. Открывает доступ к семейному врачу и специалистам бесплатно.',
    },
    en: {
      label: 'tarjeta sanitaria',
      definition: 'Spain\'s national health card. Issued at the local centro de salud after empadronamiento + permiso de residencia. Unlocks free access to a family doctor and specialists.',
    },
  },
  'guardia-civil': {
    category: 'misc',
    ru: {
      label: 'Guardia Civil',
      definition: 'Испанская военная жандармерия. Зелёная форма с треуголкой. Действует в сельской местности и на дорогах; параллельно с Policía Nacional в крупных городах.',
    },
    en: {
      label: 'Guardia Civil',
      definition: 'Spain\'s military gendarmerie. Green uniform with the tricorn hat. Operates in rural areas and on roads; runs alongside Policía Nacional in cities.',
    },
  },
  'policia-nacional': {
    category: 'misc',
    ru: {
      label: 'Policía Nacional',
      definition: 'Гражданская национальная полиция Испании. Уголовные дела, оборона границ, иммиграция (Comisaría выдаёт NIE и TIE). Работает в городах от 20 000 жителей.',
    },
    en: {
      label: 'Policía Nacional',
      definition: 'Spain\'s civilian national police. Criminal investigations, border defence, immigration (the Comisaría issues NIE and TIE). Active in cities of 20,000+.',
    },
  },

  // ── Spain: extras for cost-of-living / property / climate / healthcare ──
  'smi': {
    category: 'tax',
    ru: {
      label: 'SMI',
      definition: 'Salario Mínimo Interprofesional, минимальная межпрофессиональная зарплата Испании. На 2026 год €1 266 в месяц (14 выплат). К SMI привязан порог DN-визы (225 % SMI = €2 849) и часть социальных льгот.',
    },
    en: {
      label: 'SMI',
      definition: 'Salario Mínimo Interprofesional, Spain\'s statutory minimum wage. For 2026 it stands at €1,266 per month (paid over 14 instalments). The DN-visa income floor (225 % SMI = €2,849) and several social benefits are pegged to it.',
    },
  },
  'iprem': {
    category: 'tax',
    ru: {
      label: 'IPREM',
      definition: 'Indicador Público de Renta de Efectos Múltiples, базовый бюджетный индекс Испании. На 2026 год €600/мес. Используется для NLV-визы (400 % IPREM = €2 400), Reagrupación Familiar, льгот по жилью.',
    },
    en: {
      label: 'IPREM',
      definition: 'Indicador Público de Renta de Efectos Múltiples, Spain\'s reference income index. For 2026 it stands at €600/month. Pegs the NLV-visa floor (400 % IPREM = €2,400), family reunification thresholds and housing benefits.',
    },
  },
  'aemet': {
    category: 'misc',
    ru: {
      label: 'AEMET',
      definition: 'Agencia Estatal de Meteorología, испанская метеорологическая служба. Издаёт прогнозы, цветовые предупреждения (amarillo, naranja, rojo) и индексы пожарной опасности. Сайт aemet.es обновляется каждые шесть часов.',
    },
    en: {
      label: 'AEMET',
      definition: 'Agencia Estatal de Meteorología, Spain\'s national weather agency. Issues forecasts, colour-coded warnings (amarillo, naranja, rojo) and wildfire-risk indices. The aemet.es site updates every six hours.',
    },
  },
  'ajd': {
    category: 'property',
    ru: {
      label: 'AJD',
      definition: 'Actos Jurídicos Documentados, испанский гербовый сбор на нотариальные акты. На покупку новостройки 0,5-1,5 % от цены (ставка зависит от comunidad autónoma). На вторичный рынок не применяется (там работает ITP).',
    },
    en: {
      label: 'AJD',
      definition: 'Actos Jurídicos Documentados, Spain\'s stamp duty on notarial deeds. On new-build purchases, 0.5-1.5 % of price (the rate is set by the comunidad autónoma). Does not apply to second-hand sales, where ITP takes its place.',
    },
  },
  'plusvalia-municipal': {
    category: 'property',
    ru: {
      label: 'Plusvalía Municipal',
      definition: 'Муниципальный налог на прирост кадастровой стоимости земли с момента предыдущей покупки. Платит продавец. С 2021 года плательщик может выбрать между формулой по объективному коэффициенту и расчётом от реальной разницы цен.',
    },
    en: {
      label: 'Plusvalía Municipal',
      definition: 'Spain\'s municipal tax on the increase in cadastral land value since the previous sale. Paid by the seller. Since 2021 the taxpayer may choose between the objective-coefficient formula and a calculation based on the real price difference.',
    },
  },
  'cedula-habitabilidad': {
    category: 'property',
    ru: {
      label: 'Cédula de Habitabilidad',
      definition: 'Свидетельство пригодности жилья для проживания в Испании. Выдаётся comunidad autónoma на срок 10-15 лет. Требуется для подключения коммунальных услуг и долгосрочной аренды в Каталонии, Валенсии, Балеарах, Астурии, Кантабрии. В остальных регионах факультативно.',
    },
    en: {
      label: 'Cédula de Habitabilidad',
      definition: 'Spain\'s habitability certificate. Issued by the comunidad autónoma, valid for 10-15 years. Required for utility hook-ups and long-term lets in Catalonia, Valencia, the Balearics, Asturias and Cantabria. Optional in other regions.',
    },
  },
  'catastro': {
    category: 'property',
    ru: {
      label: 'Catastro',
      definition: 'Испанский кадастр (Dirección General del Catastro). Хранит ссылку catastral, площадь, кадастровую стоимость каждого объекта. Кадастровая стоимость обычно 50-70 % от рыночной; от неё считаются IBI и часть IRPF на вменённый доход от недвижимости.',
    },
    en: {
      label: 'Catastro',
      definition: 'Spain\'s cadastre (Dirección General del Catastro). Holds the cadastral reference, surface area and cadastral value of every property. Cadastral value typically sits at 50-70 % of market price; IBI and the imputed-income portion of IRPF are computed on it.',
    },
  },
  'comunidad-de-propietarios': {
    category: 'property',
    ru: {
      label: 'Comunidad de Propietarios',
      definition: 'Объединение собственников многоквартирного дома в Испании, аналог HOA. Собирает ежемесячные взносы (cuota) €30-200 на лифт, уборку, страховку, общий ремонт. Решения принимаются на ежегодном собрании (junta) большинством голосов по долям.',
    },
    en: {
      label: 'Comunidad de Propietarios',
      definition: 'Spain\'s homeowners\' association in a multi-unit building. Collects monthly dues (cuota) of €30-200 for the lift, cleaning, insurance and common repairs. Decisions are taken at the annual meeting (junta) by a weighted-share majority.',
    },
  },
  'vpo': {
    category: 'property',
    ru: {
      label: 'VPO',
      definition: 'Vivienda de Protección Oficial, испанское социальное жильё со сниженной ценой и ограничениями. Покупатель должен быть резидентом региона, не владеть другим жильём и не превышать порог дохода. Перепродажа ниже рыночной ставки в течение 10-30 лет.',
    },
    en: {
      label: 'VPO',
      definition: 'Vivienda de Protección Oficial, Spain\'s state-subsidised housing with reduced prices and resale restrictions. The buyer must be a regional resident, must not own other property, and must fall under an income cap. Resale is capped at below-market rates for 10-30 years.',
    },
  },
  'ley-de-vivienda': {
    category: 'property',
    ru: {
      label: 'Ley de Vivienda 2023',
      definition: 'Испанский жилищный закон 12/2023. Вводит «зоны напряжённого рынка» (zonas tensionadas), где comunidad autónoma может ограничить повышение арендной платы. Параллельно блокирует выдачу новых лицензий Alojamiento de Uso Turístico в Барселоне и центрах крупных городов.',
    },
    en: {
      label: 'Ley de Vivienda 2023',
      definition: 'Spain\'s housing law 12/2023. Designates "stressed market zones" (zonas tensionadas) where the comunidad autónoma may cap rent increases. Also blocks new short-term rental licences in Barcelona and most large-city centres.',
    },
  },
  'abono-transporte': {
    category: 'transit',
    ru: {
      label: 'Abono Transportes',
      definition: 'Мадридский месячный единый проездной на метро, автобусы EMT, пригородные поезда Cercanías. Зона A €56, по всей агломерации €131 (без скидок). Для молодёжи до 26 и пенсионеров 65+ цена €20-25.',
    },
    en: {
      label: 'Abono Transportes',
      definition: 'Madrid\'s monthly transit pass covering Metro, EMT buses and Cercanías commuter trains. Zone A €56, the full metropolitan area €131 at the regular rate. Under-26 and 65+ holders pay €20-25.',
    },
  },
  't-usual': {
    category: 'transit',
    ru: {
      label: 'T-usual',
      definition: 'Барселонский месячный безлимитный проездной (метро TMB, автобусы, трамвай, Rodalies в одной зоне). Зона 1 €43,30, по всей агломерации до €112. Для молодёжи до 25 действует T-Jove €40 на 3 месяца.',
    },
    en: {
      label: 'T-usual',
      definition: 'Barcelona\'s monthly unlimited pass (TMB metro, buses, tram, Rodalies in one zone). Zone 1 €43.30, the full metropolitan area up to €112. Riders under 25 use T-Jove at €40 for three months.',
    },
  },
  'centro-de-salud': {
    category: 'health',
    ru: {
      label: 'centro de salud',
      definition: 'Испанский первичный центр здоровья, аналог португальского centro de saúde. Приписка по адресу. Здесь принимает médico de cabecera, медсёстры, педиатр; выдаются справки, рецепты, направления к специалистам. Запись через приложение региональной SNS (Mi Carpeta Salud, La Meva Salut и т. д.).',
    },
    en: {
      label: 'centro de salud',
      definition: 'Spain\'s primary-care clinic, the equivalent of Portugal\'s centro de saúde. Catchment is by address. Houses the family doctor, nurses, paediatrician; issues certificates, prescriptions and specialist referrals. Booking runs through each region\'s SNS app (Mi Carpeta Salud, La Meva Salut, etc.).',
    },
  },
  'medico-de-cabecera': {
    category: 'health',
    ru: {
      label: 'médico de cabecera',
      definition: 'Прикреплённый семейный врач испанской SNS, аналог итальянского medico di base и португальского médico de família. Назначается автоматически по адресу регистрации. Сменить можно один раз в год без объяснений.',
    },
    en: {
      label: 'médico de cabecera',
      definition: 'The assigned SNS family doctor in Spain, the equivalent of an Italian medico di base or a Portuguese médico de família. Auto-assigned by address. May be swapped once a year without explanation.',
    },
  },
  'muface': {
    category: 'health',
    ru: {
      label: 'MUFACE',
      definition: 'Mutualidad General de Funcionarios Civiles del Estado, ведомственная медицинская страховка для гражданских госслужащих Испании. Параллельно с SNS даёт доступ к частным сетям (Adeslas, ASISA, DKV) без копеек. Для иммигрантов недоступна, кроме найма в государственный сектор.',
    },
    en: {
      label: 'MUFACE',
      definition: 'Mutualidad General de Funcionarios Civiles del Estado, the supplementary health scheme for Spanish civil servants. Runs alongside SNS and unlocks the private networks (Adeslas, ASISA, DKV) without copays. Not open to most newcomers, only to public-sector hires.',
    },
  },
  'copago-farmaceutico': {
    category: 'health',
    ru: {
      label: 'copago farmacéutico',
      definition: 'Соплатёж за рецептурные лекарства в испанской SNS. От 0 % для пенсионеров с минимальной пенсией и социальных категорий до 60 % для активных трудящихся с доходом свыше €100 000/год. Большинство резидентов платит 40-50 %.',
    },
    en: {
      label: 'copago farmacéutico',
      definition: 'Spain\'s drug copay under SNS. Ranges from 0 % for low-income pensioners and social-care categories up to 60 % for working residents earning over €100,000/year. Most residents pay 40-50 %.',
    },
  },

  // ── Spain: banking + transport + safety + language + infrastructure ──
  'bizum': {
    category: 'misc',
    ru: {
      label: 'Bizum',
      definition: 'Испанский стандарт мгновенного P2P платежа по номеру телефона между всеми банками страны. Бесплатно, моментально, лимит €1 000/день и €5 000/месяц на отправителя. Запущен консорциумом банков в 2016 году; покрытие более 25 млн пользователей.',
    },
    en: {
      label: 'Bizum',
      definition: 'Spain\'s instant P2P payment rail by phone number between all banks. Free, instant, daily limit €1,000 and monthly €5,000 per sender. Launched by a bank consortium in 2016; over 25 M users today.',
    },
  },
  'nomina': {
    category: 'tax',
    ru: {
      label: 'cuenta nómina',
      definition: 'Зарплатный счёт в испанском банке. При зачислении €600-900 ежемесячно банк отменяет плату за обслуживание и часто добавляет бонусы (страховка, кешбэк, повышенный лимит банкомата).',
    },
    en: {
      label: 'cuenta nómina',
      definition: 'A salary account at a Spanish bank. With monthly direct deposit of €600-900 the bank waives the maintenance fee and often adds perks (insurance, cashback, higher ATM limits).',
    },
  },
  'renfe': {
    category: 'transit',
    ru: {
      label: 'Renfe',
      definition: 'Государственный железнодорожный оператор Испании. Бренд AVE (Alta Velocidad Española) для высокоскоростных, Intercity, Avant, Cercanías для пригородных, Media Distancia для регионалов. Билеты в приложении Renfe или на renfe.com.',
    },
    en: {
      label: 'Renfe',
      definition: 'Spain\'s state-owned railway operator. The AVE (Alta Velocidad Española) brand for high-speed, plus Intercity, Avant, Cercanías commuter and Media Distancia regional services. Tickets via the Renfe app or renfe.com.',
    },
  },
  'ave': {
    category: 'transit',
    ru: {
      label: 'AVE',
      definition: 'Alta Velocidad Española, высокоскоростная сеть Испании (4 000+ км в 2026, вторая по протяжённости в мире после Китая). Скорость до 310 км/ч. Мадрид, центральный узел; AVE Мадрид-Барселона за 2 ч 30 мин.',
    },
    en: {
      label: 'AVE',
      definition: 'Alta Velocidad Española, Spain\'s high-speed rail network (4,000+ km in 2026, second-longest in the world after China). Top speed 310 km/h. Madrid is the central hub; AVE Madrid-Barcelona runs in 2 h 30.',
    },
  },
  'iryo': {
    category: 'transit',
    ru: {
      label: 'Iryo',
      definition: 'Испано-итальянский высокоскоростной оператор (Trenitalia + Air Nostrum), запущенный в 2022 году. Конкурент Renfe AVE на маршруте Мадрид-Барселона; премиум-сегмент с Wi-Fi на бортах.',
    },
    en: {
      label: 'Iryo',
      definition: 'A Spanish-Italian high-speed operator (Trenitalia + Air Nostrum), launched in 2022. Renfe AVE\'s rival on Madrid-Barcelona; premium positioning with onboard Wi-Fi.',
    },
  },
  'ouigo': {
    category: 'transit',
    ru: {
      label: 'Ouigo',
      definition: 'Low-cost высокоскоростной оператор Испании, дочка французской SNCF, с 2021 года. Билеты от €9 при заблаговременной покупке; без бесплатного багажа, ограниченные маршруты.',
    },
    en: {
      label: 'Ouigo',
      definition: 'A low-cost high-speed operator in Spain, an SNCF subsidiary, launched in 2021. Fares from €9 booked ahead; no free luggage allowance and a limited route map.',
    },
  },
  'cercanias': {
    category: 'transit',
    ru: {
      label: 'Cercanías',
      definition: 'Пригородные поезда Renfe в крупных испанских городах. В Мадриде 9 линий C, в Барселоне аналог называется Rodalies (совместно с Generalitat). Включены в Abono Transportes и T-Usual.',
    },
    en: {
      label: 'Cercanías',
      definition: 'Renfe\'s commuter trains in major Spanish cities. Madrid runs 9 C-lines; Barcelona\'s equivalent is Rodalies (with the Generalitat). Bundled into Abono Transportes and T-Usual.',
    },
  },
  'alsa': {
    category: 'transit',
    ru: {
      label: 'ALSA',
      definition: 'Главный национальный автобусный перевозчик Испании, дочка британской National Express. Покрывает почти все 8 100 муниципалитетов. Билеты в приложении ALSA или на терминалах.',
    },
    en: {
      label: 'ALSA',
      definition: 'Spain\'s main national coach operator, a UK National Express subsidiary. Covers almost all 8,100 municipalities. Tickets in the ALSA app or at terminals.',
    },
  },
  'flixbus': {
    category: 'transit',
    ru: {
      label: 'FlixBus',
      definition: 'Немецкий low-cost автобусный оператор. В Испании на 20-30 % дешевле ALSA, расширяется с 2024 года. Сильнее на международных маршрутах Мадрид-Париж, Мадрид-Лиссабон.',
    },
    en: {
      label: 'FlixBus',
      definition: 'A German low-cost coach operator. In Spain runs 20-30 % cheaper than ALSA on main routes and has expanded fast since 2024. Strongest on cross-border lines Madrid-Paris, Madrid-Lisbon.',
    },
  },
  'aena': {
    category: 'transit',
    ru: {
      label: 'AENA',
      definition: 'Государственная компания, оператор аэропортов Испании (51 % государства, котируется на бирже). Управляет 46 аэропортами включая Мадрид-Барахас, Барселона-Эль-Прат, Пальма, Малагу, Канары.',
    },
    en: {
      label: 'AENA',
      definition: 'Spain\'s listed airport operator (51 % state-owned). Runs 46 airports including Madrid-Barajas, Barcelona-El Prat, Palma, Málaga, Canary Islands hubs.',
    },
  },
  'ivtm': {
    category: 'transit',
    ru: {
      label: 'IVTM',
      definition: 'Impuesto sobre Vehículos de Tracción Mecánica, годовой муниципальный налог на машину. €60-300 в зависимости от мощности двигателя и муниципалитета; в Мадриде дороже, в южных деревнях дешевле.',
    },
    en: {
      label: 'IVTM',
      definition: 'Impuesto sobre Vehículos de Tracción Mecánica, the annual municipal vehicle tax. €60-300 depending on engine power and municipality; Madrid is dearer, southern villages cheaper.',
    },
  },
  'itv': {
    category: 'transit',
    ru: {
      label: 'ITV',
      definition: 'Inspección Técnica de Vehículos, обязательная техническая инспекция машины в Испании. Первая через 4 года для новой, потом каждые 2 года; для машин старше 10 лет ежегодно. €30-50 в авторизованном центре.',
    },
    en: {
      label: 'ITV',
      definition: 'Inspección Técnica de Vehículos, the mandatory vehicle inspection in Spain. First inspection after 4 years for new cars, then every 2 years; yearly for cars over 10 years. €30-50 at an authorised centre.',
    },
  },
  'zbe': {
    category: 'transit',
    ru: {
      label: 'ZBE',
      definition: 'Zona de Bajas Emisiones, испанские зоны ограничений по выбросам. Обязательны во всех городах от 50 000 жителей с 2024 года (закон Cambio Climático 2021). Машины без экологической наклейки DGT блокируются камерами; штраф €200.',
    },
    en: {
      label: 'ZBE',
      definition: 'Zona de Bajas Emisiones, Spain\'s low-emission zones. Mandatory in every city above 50,000 inhabitants since 2024 (2021 Climate Law). Cars without a DGT environmental sticker are blocked by ANPR cameras; €200 fine.',
    },
  },
  'dgt': {
    category: 'transit',
    ru: {
      label: 'DGT',
      definition: 'Dirección General de Tráfico, главное управление дорожного движения Испании. Регистрация машин, экзамен на права, обмен иностранных прав, экологическая наклейка, штрафы.',
    },
    en: {
      label: 'DGT',
      definition: 'Dirección General de Tráfico, Spain\'s national traffic authority. Vehicle registration, driving-licence exams, foreign-licence exchange, environmental stickers, traffic fines.',
    },
  },
  'trasmediterranea': {
    category: 'transit',
    ru: {
      label: 'Trasmediterránea',
      definition: 'Главный оператор паромов Испании (бренд Acciona). Маршруты Балеары и Канары из Барселоны, Валенсии, Дении, Кадиса. Конкуренты Baleària и Naviera Armas.',
    },
    en: {
      label: 'Trasmediterránea',
      definition: 'Spain\'s leading ferry operator (an Acciona brand). Routes to the Balearics and Canaries from Barcelona, Valencia, Dénia, Cádiz. Rivals include Baleària and Naviera Armas.',
    },
  },
  'baleria': {
    category: 'transit',
    ru: {
      label: 'Baleària',
      definition: 'Испанский оператор паромов, конкурент Trasmediterránea. Маршруты в Балеары и Северную Африку (Алхесирас-Танжер). Базируется в Дении (Валенсийское сообщество).',
    },
    en: {
      label: 'Baleària',
      definition: 'A Spanish ferry operator and Trasmediterránea\'s main rival. Routes to the Balearics and North Africa (Algeciras-Tangier). Based in Dénia (Valencian Community).',
    },
  },
  'naviera-armas': {
    category: 'transit',
    ru: {
      label: 'Naviera Armas',
      definition: 'Главный оператор межостровных паромов в Канарах. Маршруты Тенерифе, Гран-Канария, Лансароте, Фуэртевентура, Ла-Пальма. Конкурент Fred. Olsen Express.',
    },
    en: {
      label: 'Naviera Armas',
      definition: 'The main inter-island ferry operator in the Canaries. Routes between Tenerife, Gran Canaria, Lanzarote, Fuerteventura, La Palma. Rival to Fred. Olsen Express.',
    },
  },
  'tib': {
    category: 'transit',
    ru: {
      label: 'TIB',
      definition: 'Transports de les Illes Balears, межгородской и островной транспорт Балеар. Автобусы и поезда на Майорке (Палма-Сольер, Палма-Инка), сеть на Менорке и Ибице.',
    },
    en: {
      label: 'TIB',
      definition: 'Transports de les Illes Balears, the intercity and island transit network for the Balearics. Buses and trains on Mallorca (Palma-Sóller, Palma-Inca), plus networks on Menorca and Ibiza.',
    },
  },
  'mossos': {
    category: 'admin',
    ru: {
      label: 'Mossos d\'Esquadra',
      definition: 'Региональная полиция Каталонии, с 2005-2008 заменила Nacional и Guardia Civil в большинстве функций (кроме иммиграции). Красно-синяя форма, подчинены Generalitat.',
    },
    en: {
      label: 'Mossos d\'Esquadra',
      definition: 'Catalonia\'s regional police; since 2005-2008 replaced Nacional and Guardia Civil across most functions (immigration excepted). Red-and-blue uniform, reporting to the Generalitat.',
    },
  },
  'ertzaintza': {
    category: 'admin',
    ru: {
      label: 'Ertzaintza',
      definition: 'Региональная полиция Страны Басков. Красно-чёрная форма, подчинена Eusko Jaurlaritza (правительству Басков). Заменила Nacional и Guardia Civil в большинстве функций с 1980-х.',
    },
    en: {
      label: 'Ertzaintza',
      definition: 'The Basque regional police. Red-and-black uniform, reporting to the Eusko Jaurlaritza (Basque government). Replaced Nacional and Guardia Civil across most functions from the 1980s.',
    },
  },
  'telefono-016': {
    category: 'misc',
    ru: {
      label: 'Línea 016',
      definition: 'Бесплатная государственная горячая линия Испании для жертв гендерного насилия. 24/7 на 53 языках включая русский. Не отображается в счёте телефона; также через WhatsApp и приложение ALERTCOPS.',
    },
    en: {
      label: 'Línea 016',
      definition: 'Spain\'s free state-run hotline for victims of gender-based violence. 24/7 in 53 languages including Russian. Invisible on the phone bill; also reachable via WhatsApp and the ALERTCOPS app.',
    },
  },
  'catalan': {
    category: 'misc',
    ru: {
      label: 'каталанский',
      definition: 'Романский язык западной ветви, сооф. в Каталонии, Валенсии (там «валенсийский»), на Балеарах. Около 9,2 млн носителей. В Каталонии 70 % школьных программ ведутся на каталанском.',
    },
    en: {
      label: 'Catalan',
      definition: 'A western-Romance language, co-official in Catalonia, Valencia (where it is called Valencian) and the Balearics. About 9.2 M speakers. In Catalonia 70 % of school instruction runs in Catalan.',
    },
  },
  'euskera': {
    category: 'misc',
    ru: {
      label: 'Euskera (баскский)',
      definition: 'Неиндоевропейский, изолированный язык без известных родственников. Около 750 000 носителей в Стране Басков и северной Наварре. Грамматика радикально отличается от любого европейского языка; обязательная программа в баскских школах со 2-3 классов.',
    },
    en: {
      label: 'Euskera (Basque)',
      definition: 'A non-Indo-European isolated language with no known relatives. About 750,000 speakers in the Basque Country and northern Navarra. Grammar and vocabulary differ radically from any European language; compulsory in Basque schools from year 2-3.',
    },
  },
  'galego': {
    category: 'misc',
    ru: {
      label: 'Galego (галисийский)',
      definition: 'Романский язык, близкий к португальскому. Около 2 млн носителей в Галисии (северо-запад Испании). Сооф. с кастильским; в сельской Галисии доминирует, в городах кастильский.',
    },
    en: {
      label: 'Galego (Galician)',
      definition: 'A Romance language closely related to Portuguese. About 2 M speakers in Galicia (north-west Spain). Co-official with Castilian; dominant in rural Galicia, with Castilian leading in cities.',
    },
  },
  'dele': {
    category: 'education',
    ru: {
      label: 'DELE',
      definition: 'Diplomas de Español como Lengua Extranjera, государственный сертификат испанского для иностранцев от Instituto Cervantes. Уровни A1-C2; A2 обязателен для гражданства с 2015 года. Стандартная плата за A2 €124.',
    },
    en: {
      label: 'DELE',
      definition: 'Diplomas de Español como Lengua Extranjera, the state Spanish-as-foreign-language certificate from the Cervantes Institute. Levels A1-C2; A2 mandatory for citizenship since 2015. Standard A2 fee €124.',
    },
  },
  'ccse': {
    category: 'education',
    ru: {
      label: 'CCSE',
      definition: 'Conocimientos Constitucionales y Socioculturales de España, обязательный экзамен на знание Конституции и культуры Испании для гражданства с 2015 года. 25 вопросов, проходной 15/25, плата €85.',
    },
    en: {
      label: 'CCSE',
      definition: 'Conocimientos Constitucionales y Socioculturales de España, the mandatory exam on the Spanish Constitution and culture, required for citizenship since 2015. 25 questions, pass mark 15/25, fee €85.',
    },
  },
  'eoi': {
    category: 'education',
    ru: {
      label: 'EOI',
      definition: 'Escuela Oficial de Idiomas, государственная сеть языковых школ Испании при Министерстве образования. 300+ центров. Курсы испанского как иностранного и других языков, €150-350/год для не-EU; сертификат принимается для гражданства.',
    },
    en: {
      label: 'EOI',
      definition: 'Escuela Oficial de Idiomas, Spain\'s state language-school network under the Ministry of Education. 300+ centres. Courses in Spanish-as-foreign-language and other languages at €150-350/year for non-EU residents; certificates accepted for citizenship.',
    },
  },
  'cervantes-instituto': {
    category: 'education',
    ru: {
      label: 'Instituto Cervantes',
      definition: 'Государственное культурное агентство Испании. Центры в Мадриде и около 90 за рубежом. Курсы испанского, экзамены DELE и CCSE, стипендии для студентов испанистики.',
    },
    en: {
      label: 'Instituto Cervantes',
      definition: 'Spain\'s state cultural agency. A centre in Madrid and around 90 abroad. Spanish-language courses, DELE and CCSE exams, scholarships for Spanish-studies students.',
    },
  },
  'consorci-normalitzacio': {
    category: 'education',
    ru: {
      label: 'CPNL',
      definition: 'Consorci per a la Normalització Lingüística, государственная сеть курсов каталанского в Каталонии. Бесплатно для всех резидентов, A1-C2 за 80-120 часов на уровень.',
    },
    en: {
      label: 'CPNL',
      definition: 'Consorci per a la Normalització Lingüística, the state Catalan-language course network across Catalonia. Free for all residents, A1-C2 in 80-120 hours per level.',
    },
  },
  'fallas': {
    category: 'misc',
    ru: {
      label: 'Las Fallas',
      definition: 'Главный валенсийский фестиваль 15-19 марта. Огромные сатирические монументы из папье-маше выставляются по всему городу; финал La Cremà в ночь на 19 марта, когда все fallas одновременно сжигаются. ЮНЕСКО Нематериальное наследие.',
    },
    en: {
      label: 'Las Fallas',
      definition: 'Valencia\'s main festival, 15-19 March. Massive satirical papier-mâché monuments stand across the city; the finale La Cremà on the night of 19 March burns every falla simultaneously. UNESCO Intangible Heritage.',
    },
  },
  'san-fermin': {
    category: 'misc',
    ru: {
      label: 'San Fermín',
      definition: 'Памплонский фестиваль 6-14 июля. Encierro (бег быков по улицам), 8-минутная пробежка перед быками. Hemingway увековечил в «И восходит солнце» (1926).',
    },
    en: {
      label: 'San Fermín',
      definition: 'Pamplona\'s festival, 6-14 July. The encierro (running of the bulls), an 8-minute run before bulls along a fenced route. Hemingway immortalised it in "The Sun Also Rises" (1926).',
    },
  },
  'la-merce': {
    category: 'misc',
    ru: {
      label: 'La Mercè',
      definition: 'Главный фестиваль Барселоны, 24 сентября в честь покровительницы города Девы Марии Милосердной. Castellers (человеческие башни), correfocs (огненные шествия), концерты, бесплатный музейный день.',
    },
    en: {
      label: 'La Mercè',
      definition: 'Barcelona\'s main festival on 24 September, honouring the city\'s patroness Our Lady of Mercy. Castellers (human towers), correfocs (fire runs), concerts and a free museum day.',
    },
  },
  'movistar': {
    category: 'misc',
    ru: {
      label: 'Movistar',
      definition: 'Бренд Telefónica España, крупнейший оператор связи Испании (бывший монополист, ~35-40 % розничного рынка). Покрытие FTTH одно из лучших в стране; triple-play бандлы от €30-45/мес.',
    },
    en: {
      label: 'Movistar',
      definition: 'The retail brand of Telefónica España, Spain\'s largest telecoms operator (the former monopolist, around 35-40 % of retail). Among the best FTTH footprints in the country; triple-play bundles from €30-45/month.',
    },
  },
  'vodafone-es': {
    category: 'misc',
    ru: {
      label: 'Vodafone España',
      definition: 'Второй по величине оператор связи Испании. Активное развёртывание FTTH; конкурент Movistar и Orange. Triple-play бандлы от €30-45/мес.',
    },
    en: {
      label: 'Vodafone España',
      definition: 'Spain\'s second-largest telecoms operator. Active FTTH rollout; rival to Movistar and Orange. Triple-play bundles from €30-45/month.',
    },
  },
  'orange-es': {
    category: 'misc',
    ru: {
      label: 'Orange España',
      definition: 'Третий по величине оператор связи Испании, после слияния с MásMóvil в 2024 году усилился. Triple-play бандлы; владеет low-cost брендами Yoigo и Pepephone.',
    },
    en: {
      label: 'Orange España',
      definition: 'Spain\'s third-largest telecoms operator, strengthened after the 2024 merger with MásMóvil. Triple-play bundles; owns the low-cost brands Yoigo and Pepephone.',
    },
  },
  'masmovil': {
    category: 'misc',
    ru: {
      label: 'MásMóvil',
      definition: 'Четвёртый оператор связи Испании, с 2024 года часть Orange. Управляет low-cost брендами Yoigo и Pepephone. Сильнее всех в бюджетном сегменте.',
    },
    en: {
      label: 'MásMóvil',
      definition: 'Spain\'s fourth telecoms operator, part of Orange since 2024. Runs the low-cost Yoigo and Pepephone brands. The strongest player in the budget segment.',
    },
  },
  'iberdrola': {
    category: 'misc',
    ru: {
      label: 'Iberdrola',
      definition: 'Крупнейший поставщик электричества в Испании (~35 % розницы). Лидер в возобновляемой энергии (ветер, солнце, гидро). Свободный рынок и регулируемый PVPC через дочку Iberdrola Clientes.',
    },
    en: {
      label: 'Iberdrola',
      definition: 'Spain\'s largest electricity supplier (around 35 % of retail). Renewables leader (wind, solar, hydro). Free-market and PVPC regulated tariffs via the Iberdrola Clientes subsidiary.',
    },
  },
  'endesa': {
    category: 'misc',
    ru: {
      label: 'Endesa',
      definition: 'Второй по величине поставщик электричества Испании, дочка итальянской Enel. Свободный рынок и PVPC через Endesa Energía. Сильна в Каталонии и Андалусии.',
    },
    en: {
      label: 'Endesa',
      definition: 'Spain\'s second-largest electricity supplier, a subsidiary of Italy\'s Enel. Free-market and PVPC tariffs via Endesa Energía. Strong in Catalonia and Andalusia.',
    },
  },
  'naturgy': {
    category: 'misc',
    ru: {
      label: 'Naturgy',
      definition: 'Третий поставщик электричества Испании и крупнейший в природном газе (бывший Gas Natural Fenosa). Dual-fuel бандлы свет + газ со скидкой 5-10 %.',
    },
    en: {
      label: 'Naturgy',
      definition: 'Spain\'s third-largest electricity supplier and the leader in natural gas (formerly Gas Natural Fenosa). Dual-fuel bundles for power + gas at a 5-10 % discount.',
    },
  },
  'correos': {
    category: 'misc',
    ru: {
      label: 'Correos',
      definition: 'Sociedad Estatal Correos y Telégrafos, государственный почтовый оператор Испании (не приватизирован, отличие от португальских CTT). Доставка письма 2-5 дней, заказной 3-7 дней.',
    },
    en: {
      label: 'Correos',
      definition: 'Sociedad Estatal Correos y Telégrafos, Spain\'s state-owned postal operator (not privatised, in contrast to Portugal\'s CTT). Standard letter delivery 2-5 days, registered 3-7 days.',
    },
  },
  'clave': {
    category: 'admin',
    ru: {
      label: 'Cl@ve',
      definition: 'Бесплатная цифровая идентификация Испании. Уровни eIDAS substantial (Cl@ve PIN, Permanente) или high (Certificado Digital). Открывает Agencia Tributaria, Seguridad Social, SEPE, ayuntamiento, региональные SNS-порталы.',
    },
    en: {
      label: 'Cl@ve',
      definition: 'Spain\'s free digital ID. eIDAS substantial (Cl@ve PIN, Permanente) or high (Certificado Digital) level. Unlocks Agencia Tributaria, Seguridad Social, SEPE, the ayuntamiento, and regional SNS portals.',
    },
  },
  'certificado-energetico-es': {
    category: 'property',
    ru: {
      label: 'Certificado Energético',
      definition: 'Сертификат энергоэффективности жилья, обязателен с 2013 года для продажи и долгосрочной аренды. Шкала A-G. Старые квартиры в исторических центрах обычно E-G, новостройки B-A.',
    },
    en: {
      label: 'Certificado Energético',
      definition: 'Spain\'s energy-efficiency certificate, mandatory since 2013 for sale and long-term rental. A-G scale. Old flats in historic centres usually score E-G; new builds B-A.',
    },
  },

  // ── Portugal: banking + education ──────────────────────────────────
  'multibanco': {
    category: 'misc',
    ru: {
      label: 'Multibanco',
      definition: 'Единая португальская сеть банкоматов и POS-терминалов, общая для всех банков страны. Снятие в «своём» банке бесплатно; в «чужом», 1-2 € после первых нескольких операций в месяц. Принимает референсы (referência Multibanco) для оплаты налогов, штрафов, коммуналки.',
    },
    en: {
      label: 'Multibanco',
      definition: 'Portugal\'s shared ATM and POS network, common to every Portuguese bank. Cash withdrawals are free at your own bank, €1-2 elsewhere after the first few per month. Accepts payment references (referência Multibanco) for taxes, fines and utility bills.',
    },
  },
  'mb-way': {
    category: 'misc',
    ru: {
      label: 'MB Way',
      definition: 'Мгновенный платежный сервис P2P поверх Multibanco. Работает по номеру телефона, привязанному к карте; перевод проходит за секунды бесплатно. Доминирующий способ разделить счёт в ресторане, заплатить мастеру, перевести аренду между жильцами.',
    },
    en: {
      label: 'MB Way',
      definition: 'Instant P2P payment service running on top of Multibanco. Tied to a phone number, transfers settle in seconds free of charge. The dominant way to split a restaurant bill, pay a tradesman, or move rent between flatmates.',
    },
  },
  'chave-movel-digital': {
    category: 'admin',
    ru: {
      label: 'Chave Móvel Digital',
      definition: 'Португальская мобильная электронная подпись (CMD). Привязана к NIF и номеру телефона. Бесплатна, оформляется в любом Loja do Cidadão по permesso за 15-30 минут. Без CMD недоступны Portal das Finanças, Segurança Social, регистрация авто.',
    },
    en: {
      label: 'Chave Móvel Digital',
      definition: 'Portugal\'s mobile e-signature (CMD). Bound to the NIF and a phone number. Free; activated at any Loja do Cidadão on the residence permit in 15-30 minutes. Without CMD the Portal das Finanças, Segurança Social and vehicle registration portals are unreachable.',
    },
  },
  'enes': {
    category: 'education',
    ru: {
      label: 'ENES',
      definition: 'Exames Nacionais do Ensino Secundário, общенациональные выпускные экзамены португальского лицея и одновременно вступительный балл (Provas de Acesso) в государственные университеты. Сдаются в 12-м классе по нескольким профильным предметам. Для иностранных абитуриентов есть отдельная процедура признания диплома без сдачи ENES.',
    },
    en: {
      label: 'ENES',
      definition: 'Exames Nacionais do Ensino Secundário, Portugal\'s national secondary-school finals and the entrance score (Provas de Acesso) for public universities. Sat in the 12th grade across several subject papers. Foreign-credentialed applicants have a parallel recognition route that bypasses ENES.',
    },
  },
  'anqep': {
    category: 'education',
    ru: {
      label: 'ANQEP',
      definition: 'Agência Nacional para a Qualificação e o Ensino Profissional, португальское агентство по квалификациям и профобразованию. Ведёт каталог национальных квалификаций (CNQ), сертифицирует курсы CET и CTeSP, выдаёт уровни EQF 1-5 по итогам обучения.',
    },
    en: {
      label: 'ANQEP',
      definition: 'Agência Nacional para a Qualificação e o Ensino Profissional, Portugal\'s vocational qualifications agency. Keeps the National Qualifications Catalogue (CNQ), accredits CET and CTeSP courses, and issues EQF levels 1-5 against completed training.',
    },
  },
  'iefp': {
    category: 'education',
    ru: {
      label: 'IEFP',
      definition: 'Instituto do Emprego e Formação Profissional, государственная служба занятости и профобучения Португалии. Бесплатные курсы португальского для иностранцев (PPT), переподготовка взрослых, субсидии работодателям за наём безработных.',
    },
    en: {
      label: 'IEFP',
      definition: 'Instituto do Emprego e Formação Profissional, Portugal\'s public employment and training agency. Runs free Portuguese-language courses for foreigners (PPT), adult retraining, and wage subsidies for employers hiring registered job-seekers.',
    },
  },
  'ciple': {
    category: 'education',
    ru: {
      label: 'CIPLE',
      definition: 'Certificado Inicial de Português Língua Estrangeira, экзамен по португальскому языку на уровне A2. Сертификат CIPLE на A2, минимальное языковое требование для подачи на гражданство Португалии. Сдаётся в Centro de Avaliação de Português Língua Estrangeira (CAPLE) при университете Лиссабона.',
    },
    en: {
      label: 'CIPLE',
      definition: 'Certificado Inicial de Português Língua Estrangeira, the A2-level Portuguese-language exam. CIPLE at A2 is the minimum language proof for a Portuguese citizenship application. Administered by the Centro de Avaliação de Português Língua Estrangeira (CAPLE) at the University of Lisbon.',
    },
  },
  'cmvm': {
    category: 'misc',
    ru: {
      label: 'CMVM',
      definition: 'Comissão do Mercado de Valores Mobiliários, регулятор фондового рынка Португалии. Лицензирует брокеров, управляющие компании, фонды и инвестиционных консультантов; ведёт реестр публичных компаний.',
    },
    en: {
      label: 'CMVM',
      definition: 'Comissão do Mercado de Valores Mobiliários, Portugal\'s securities regulator. Licenses brokers, asset managers, funds and investment advisers; maintains the register of listed companies.',
    },
  },
  'junta-de-freguesia': {
    category: 'admin',
    ru: {
      label: 'Junta de Freguesia',
      definition: 'Совет прихода, самый низкий уровень местного самоуправления Португалии. Выдаёт справку о проживании (atestado de residência), регистрирует кошек и собак, занимается мелким благоустройством. Школьная запись и часть административных процедур привязаны к фрегезии адреса.',
    },
    en: {
      label: 'Junta de Freguesia',
      definition: 'Parish council, the lowest tier of Portuguese local government. Issues the proof-of-residence letter (atestado de residência), registers pets, runs minor public works. School enrolment and several admin processes route through the parish of the address.',
    },
  },
  'ppr': {
    category: 'tax',
    ru: {
      label: 'PPR',
      definition: 'Plano Poupança Reforma, португальский частный пенсионный продукт. Взносы дают налоговый вычет до €400/год (зависит от возраста). Снятие до пенсии облагается штрафной ставкой, на пенсии, льготная.',
    },
    en: {
      label: 'PPR',
      definition: 'Plano Poupança Reforma, Portugal\'s private pension product. Contributions earn an IRS deduction of up to €400/year (age-dependent). Pre-retirement withdrawals face a penalty rate; retirement-age withdrawals are taxed at a reduced rate.',
    },
  },
  'kyc': {
    category: 'misc',
    ru: {
      label: 'KYC',
      definition: 'Know Your Customer, обязательная процедура идентификации клиента в банке или у регулируемого финансового посредника. Включает паспорт, адрес, источник средств, иногда вопросник о профессиональной деятельности. Для не-EU паспортов часто расширяется.',
    },
    en: {
      label: 'KYC',
      definition: 'Know Your Customer, the mandatory client identification carried out by any bank or regulated financial intermediary. Covers passport, address, source of funds and sometimes a questionnaire on professional activity. The process is typically deeper for non-EU passports.',
    },
  },
  'cp-trains': {
    category: 'transit',
    ru: {
      label: 'CP',
      definition: 'Comboios de Portugal, государственный железнодорожный оператор Португалии. Управляет Alfa Pendular (быстрый поезд Лиссабон-Порту-Фару), Intercidades, региональными и пригородными линиями. Билеты через сайт cp.pt или приложение CP.',
    },
    en: {
      label: 'CP',
      definition: 'Comboios de Portugal, the state-owned railway operator. Runs Alfa Pendular (fast service Lisbon-Porto-Faro), Intercidades, regional and suburban lines. Tickets via cp.pt or the CP app.',
    },
  },
  'via-verde': {
    category: 'transit',
    ru: {
      label: 'Via Verde',
      definition: 'Электронная система оплаты дорожных сборов в Португалии. Транспондер на лобовое стекло автоматически списывает плату с привязанного счёта при проезде через шлагбаум или арку SCUT (электронных платных трасс без касс). Без транспондера на трассах SCUT камеры выставляют штраф.',
    },
    en: {
      label: 'Via Verde',
      definition: 'Portugal\'s electronic toll-payment system. A transponder mounted on the windscreen debits the linked account when the car crosses a barrier or a SCUT arch (cashless electronic highways). Without it, cameras on SCUT roads issue penalties.',
    },
  },
  'tap-air': {
    category: 'transit',
    ru: {
      label: 'TAP',
      definition: 'Transportes Aéreos Portugueses, национальный авиаперевозчик Португалии. Главный хаб Лиссабон Humberto Delgado, второй Порту OPO. Сильная сеть в Бразилию и Африку (Ангола, Мозамбик, Кабо-Верде); приватизация частично запланирована на 2026-2027.',
    },
    en: {
      label: 'TAP',
      definition: 'Transportes Aéreos Portugueses, the flag carrier. Main hub Lisbon Humberto Delgado, second hub Porto OPO. Strong network to Brazil and Lusophone Africa (Angola, Mozambique, Cape Verde); partial privatisation scheduled for 2026-2027.',
    },
  },
  'psp': {
    category: 'admin',
    ru: {
      label: 'PSP',
      definition: 'Polícia de Segurança Pública, городская гражданская полиция Португалии. Подчинена МВД, отвечает за общественный порядок, уголовные расследования и регистрацию иностранцев в крупных городах. Форма тёмно-синяя; номер 112 переключает на PSP в городе.',
    },
    en: {
      label: 'PSP',
      definition: 'Polícia de Segurança Pública, Portugal\'s urban civilian police. Under the Interior Ministry; responsible for public order, criminal investigation and foreigner registration in larger cities. Dark-blue uniform; dialling 112 routes to PSP inside city limits.',
    },
  },
  'gnr': {
    category: 'admin',
    ru: {
      label: 'GNR',
      definition: 'Guarda Nacional Republicana, военизированная жандармерия Португалии. Покрывает сельские муниципалитеты, шоссе, побережье и таможню. Тёмно-зелёная форма; функциональный аналог итальянских Carabinieri.',
    },
    en: {
      label: 'GNR',
      definition: 'Guarda Nacional Republicana, Portugal\'s gendarmerie. Covers rural municipalities, motorways, the coast and customs. Dark-green uniform; functionally close to Italy\'s Carabinieri.',
    },
  },
  'ctt': {
    category: 'misc',
    ru: {
      label: 'CTT',
      definition: 'Correios de Portugal, бывший государственный, ныне частный почтовый оператор Португалии. Доставка письма внутри страны 2-5 дней, заказной с уведомлением (correio registado) 3-7 дней. Также пункты приёма посылок, оплата счетов и юридические операции по доверенности.',
    },
    en: {
      label: 'CTT',
      definition: 'Correios de Portugal, the formerly state-owned postal operator (now privatised). Standard domestic letter 2-5 days; registered with confirmation (correio registado) 3-7 days. Also serves as a counter for parcel pick-up, bill payment and certified delivery.',
    },
  },
  'edp': {
    category: 'misc',
    ru: {
      label: 'EDP',
      definition: 'Energias de Portugal, ведущий поставщик электроэнергии страны и владелец большей части распределительной сети. Конкуренты Galp Power, Endesa Portugal, Iberdrola, Repsol Eletricidade на свободном рынке. Расчёты по индексу OMIE или фиксированному тарифу.',
    },
    en: {
      label: 'EDP',
      definition: 'Energias de Portugal, the leading power supplier and owner of most of the distribution grid. Free-market competitors include Galp Power, Endesa Portugal, Iberdrola and Repsol Eletricidade. Pricing follows the OMIE index or a fixed tariff.',
    },
  },
  'meo-altice': {
    category: 'misc',
    ru: {
      label: 'MEO',
      definition: 'Бренд оператора Altice Portugal (бывшая Portugal Telecom). Один из трёх главных провайдеров фиксированной связи, интернета и мобильной. Конкуренты NOS и Vodafone Portugal. Пакеты triple-play (интернет, ТВ, мобильная) от €30-45/мес.',
    },
    en: {
      label: 'MEO',
      definition: 'The retail brand of Altice Portugal (the former Portugal Telecom). One of the three main fixed-line, internet and mobile providers, alongside NOS and Vodafone Portugal. Triple-play bundles (internet, TV, mobile) from €30-45/month.',
    },
  },
  'cidadania-pt': {
    category: 'admin',
    ru: {
      label: 'cidadania portuguesa',
      definition: 'Португальское гражданство. С реформы апреля 2026 года минимальный срок легального проживания для натурализации вырос с 5 до 7-10 лет в зависимости от категории ВНЖ. Требуется уровень португальского A2 (CIPLE), отсутствие судимостей, доказательство интеграции.',
    },
    en: {
      label: 'cidadania portuguesa',
      definition: 'Portuguese citizenship. After the April 2026 reform, the minimum legal residency for naturalisation rose from 5 to 7-10 years depending on the permit category. Requires A2 Portuguese (CIPLE), a clean record and proof of integration.',
    },
  },
  'camoes-institute': {
    category: 'education',
    ru: {
      label: 'Instituto Camões',
      definition: 'Государственное агентство Португалии по продвижению португальского языка и культуры за рубежом. Сертифицирует экзамен CIPLE (A2 для гражданства), финансирует кафедры португалистики в университетах мира, выдаёт стипендии иностранным студентам.',
    },
    en: {
      label: 'Instituto Camões',
      definition: 'Portugal\'s state agency for promoting the Portuguese language and culture abroad. Certifies the CIPLE exam (A2 for citizenship), funds Portuguese-studies chairs at universities worldwide and awards scholarships to foreign students.',
    },
  },
  'fado': {
    category: 'misc',
    ru: {
      label: 'fado',
      definition: 'Португальский музыкальный жанр городской меланхолии. Возник в начале XIX века в Лиссабоне (Алфама, Моурария) и Коимбре (студенческий fado). Под ЮНЕСКО с 2011 года как нематериальное наследие. Главный голос ХХ века, Amália Rodrigues; современная сцена, Mariza, Ana Moura, Carminho.',
    },
    en: {
      label: 'fado',
      definition: 'A Portuguese musical genre of urban melancholy. Born in early 19th-century Lisbon (Alfama, Mouraria) and Coimbra (student fado). UNESCO intangible heritage since 2011. The defining 20th-century voice is Amália Rodrigues; the current scene features Mariza, Ana Moura, Carminho.',
    },
  },
  'bacalhau': {
    category: 'misc',
    ru: {
      label: 'bacalhau',
      definition: 'Солёная сушёная треска, основной национальный продукт Португалии со времён эпохи открытий. По локальной поговорке, рецептов «1 001»; общеизвестных «365», по одному на каждый день. Bacalhau à brás, à gomes de sá, à minhota, classic blueprints.',
    },
    en: {
      label: 'bacalhau',
      definition: 'Salt-cured dried codfish, Portugal\'s defining national ingredient since the Age of Discoveries. Local lore claims "a thousand and one" recipes; folk wisdom puts it at "365", one per day. Bacalhau à brás, à gomes de sá, à minhota are the canonical blueprints.',
    },
  },
  'pastel-de-nata': {
    category: 'misc',
    ru: {
      label: 'pastel de nata',
      definition: 'Заварной пирожок из слоёного теста с обожжённой сверху корочкой. Рецепт восходит к монахам Hieronymite в Belém (Лиссабон) XVIII века; оригинальная Pastéis de Belém хранит рецепт с 1837 года. Подаётся тёплым с корицей и сахарной пудрой по желанию.',
    },
    en: {
      label: 'pastel de nata',
      definition: 'A custard tart in laminated puff pastry with a scorched top. The recipe goes back to Hieronymite monks in 18th-century Belém (Lisbon); the original Pastéis de Belém has held the formula since 1837. Served warm with cinnamon and icing sugar to taste.',
    },
  },
  'humberto-delgado': {
    category: 'transit',
    ru: {
      label: 'LIS Humberto Delgado',
      definition: 'Главный аэропорт Лиссабона (код LIS). Единственный аэропорт столицы, пропускная способность исчерпана; проект нового аэропорта Алкошете перенесён к 2030+ годам. Хаб TAP, обслуживает 35 млн пассажиров в год.',
    },
    en: {
      label: 'LIS Humberto Delgado',
      definition: 'Lisbon\'s main airport (IATA: LIS). The capital\'s only commercial airport; runway capacity is saturated, and the proposed new Alcochete airport is delayed to 2030+. TAP hub, handles roughly 35 million passengers a year.',
    },
  },
  'opo-airport': {
    category: 'transit',
    ru: {
      label: 'OPO Porto',
      definition: 'Аэропорт Francisco Sá Carneiro в Порту (IATA: OPO). Второй по пассажиропотоку аэропорт Португалии после Лиссабона. Хаб TAP, Ryanair и easyJet; прямые рейсы в Бразилию, Африку, Северную Америку. Менее перегружен, чем Лиссабон.',
    },
    en: {
      label: 'OPO Porto',
      definition: 'Francisco Sá Carneiro Airport in Porto (IATA: OPO). Portugal\'s second-busiest airport after Lisbon. Hub for TAP, Ryanair and easyJet; direct flights to Brazil, Africa, North America. Less congested than Lisbon.',
    },
  },
  'iuc': {
    category: 'tax',
    ru: {
      label: 'IUC',
      definition: 'Imposto Único de Circulação, ежегодный налог на владение автомобилем в Португалии. Зависит от объёма двигателя, выбросов CO₂ и года регистрации; €30-300 для большинства легковушек. Платится владельцем в месяц регистрации машины через Portal das Finanças.',
    },
    en: {
      label: 'IUC',
      definition: 'Imposto Único de Circulação, Portugal\'s annual vehicle ownership tax. Banded by engine size, CO₂ emissions and year of registration; €30-300 for most passenger cars. Paid by the owner in the registration month via the Portal das Finanças.',
    },
  },
  'isv': {
    category: 'tax',
    ru: {
      label: 'ISV',
      definition: 'Imposto sobre Veículos, разовый налог при первой регистрации машины в Португалии. Считается по объёму двигателя и выбросам CO₂; для обычной легковушки €1 500-7 000. Импорт б/у из ЕС облагается ISV со скидкой по возрасту.',
    },
    en: {
      label: 'ISV',
      definition: 'Imposto sobre Veículos, the one-off tax payable at the first Portuguese registration of a car. Computed on engine size and CO₂ emissions; €1,500-7,000 for a typical passenger car. Used imports from the EU receive a depreciation-linked discount.',
    },
  },
  'sns-24': {
    category: 'health',
    ru: {
      label: 'SNS 24',
      definition: 'Государственная горячая линия здоровья Португалии (номер 808 24 24 24) и приложение SNS 24. Триаж по телефону медсестрой 24/7, запись к семейному врачу, выписка результатов анализов, электронная справка. Бесплатно для держателей número de utente.',
    },
    en: {
      label: 'SNS 24',
      definition: 'Portugal\'s state-run health hotline (808 24 24 24) and SNS 24 app. Nurse-led phone triage round the clock, GP booking, lab-result download, electronic medical letters. Free for número de utente holders.',
    },
  },
  'codigo-rosa': {
    category: 'admin',
    ru: {
      label: 'APAV',
      definition: 'Associação Portuguesa de Apoio à Vítima, главная сеть поддержки жертв преступлений и домашнего насилия Португалии. Бесплатная горячая линия 116 006, юридическая и психологическая помощь, временное жильё, координация с PSP/GNR.',
    },
    en: {
      label: 'APAV',
      definition: 'Associação Portuguesa de Apoio à Vítima, the main Portuguese network for crime victims and domestic-abuse survivors. Free hotline 116 006, legal and psychological support, emergency shelter, coordination with PSP/GNR.',
    },
  },
  'mirandes': {
    category: 'misc',
    ru: {
      label: 'mirandês',
      definition: 'Мирандский, второй официальный язык Португалии (с 1999 года). Романский язык астуро-леонской ветви, около 10 000 носителей в районе Miranda do Douro на северо-востоке. Двуязычные таблички, школьные программы, локальная литература.',
    },
    en: {
      label: 'mirandês',
      definition: 'Mirandese, Portugal\'s second official language (since 1999). A Romance language of the Asturleonese branch, with roughly 10,000 speakers around Miranda do Douro in the northeast. Bilingual signage, school programmes and a local literary scene.',
    },
  },
  'festas-populares': {
    category: 'misc',
    ru: {
      label: 'Festas dos Santos Populares',
      definition: 'Июньские народные праздники в честь святых: Santo António (13 июня) в Лиссабоне, São João (24 июня) в Порту, São Pedro (29 июня) в Сетубале и других прибрежных городах. Уличные сардины на гриле, шествия, бумажные шары, всю ночь.',
    },
    en: {
      label: 'Festas dos Santos Populares',
      definition: 'The June popular-saint festivals: Santo António (13 June) in Lisbon, São João (24 June) in Porto, São Pedro (29 June) in Setúbal and other coastal towns. Street sardine grills, processions, paper-lantern balloons, the whole night.',
    },
  },
  'imt-portugal': {
    category: 'transit',
    ru: {
      label: 'IMT (PT)',
      definition: 'Instituto da Mobilidade e dos Transportes, регулятор португальского транспорта. Выдаёт и обменивает водительские права, регистрирует автомобили, ведёт реестр перевозчиков. Запись на экзамен и подачу документов через imt-ip.pt или автошколу.',
    },
    en: {
      label: 'IMT (PT)',
      definition: 'Instituto da Mobilidade e dos Transportes, Portugal\'s transport regulator. Issues and exchanges driving licences, registers vehicles, keeps the haulier register. Appointments and exam bookings run through imt-ip.pt or via a driving school.',
    },
  },
  'carris': {
    category: 'transit',
    ru: {
      label: 'Carris',
      definition: 'Лиссабонский муниципальный оператор поверхностного транспорта: 700+ автобусов, четыре трамвайные линии (включая туристическую 28), фуникулёры Bica и Glória, лифт Santa Justa. Тариф интегрирован с Navegante.',
    },
    en: {
      label: 'Carris',
      definition: 'Lisbon\'s municipal surface-transport operator: 700+ buses, four tram lines (including the tourist line 28), the Bica and Glória funiculars, and the Santa Justa lift. Fares are integrated into the Navegante pass.',
    },
  },
  'loja-cidadao': {
    category: 'admin',
    ru: {
      label: 'Loja do Cidadão',
      definition: 'Многофункциональный центр госуслуг Португалии. Под одной крышей AIMA, Finanças, Segurança Social, Conservatória, IMT, регистрация Chave Móvel Digital. Запись через сайт epontoja.gov.pt; в Лиссабоне и Порту несколько отделений с разной нагрузкой.',
    },
    en: {
      label: 'Loja do Cidadão',
      definition: 'Portugal\'s one-stop government-services centre. AIMA, Finanças, Segurança Social, Conservatória, IMT and Chave Móvel Digital activation under one roof. Appointments through epontoja.gov.pt; Lisbon and Porto have several branches with different wait times.',
    },
  },

  // ── Cross-file abbreviations added during 2026-05 audit ─────────────
  'tgss': {
    category: 'admin',
    ru: {
      label: 'TGSS',
      definition: 'Tesorería General de la Seguridad Social — испанская казначейство соцстрахования. Принимает регистрацию в RETA (для самозанятых), выдаёт номер NUSS, собирает социальные взносы. Сайт sede.seg-social.gob.es.',
    },
    en: {
      label: 'TGSS',
      definition: 'Tesorería General de la Seguridad Social — Spain\'s social-security treasury. Handles RETA registration (for self-employed), issues the NUSS number, collects social contributions. Online at sede.seg-social.gob.es.',
    },
  },
  'reta': {
    category: 'tax',
    ru: {
      label: 'RETA',
      definition: 'Régimen Especial de Trabajadores Autónomos — испанский режим соцстрахования для самозанятых. Обязательная регистрация в TGSS. Льготная Tarifa Plana €230/мес первый год; со второго года шкала по доходу €230-1 270/мес.',
    },
    en: {
      label: 'RETA',
      definition: 'Régimen Especial de Trabajadores Autónomos — Spain\'s self-employed social-security regime. Mandatory TGSS enrolment. Year-one Tarifa Plana of €230/month; from year two an income-tier scale of €230-1,270/month.',
    },
  },
  'sepe': {
    category: 'admin',
    ru: {
      label: 'SEPE',
      definition: 'Servicio Público de Empleo Estatal — испанская служба занятости. Оформляет рабочие визы (Lucrativa), управляет пособиями по безработице, ведёт реестр Certificados de Profesionalidad (профессиональных сертификатов).',
    },
    en: {
      label: 'SEPE',
      definition: 'Servicio Público de Empleo Estatal — Spain\'s public employment service. Processes work visas (Lucrativa), administers unemployment benefits, maintains the Certificados de Profesionalidad register.',
    },
  },
  'nuss': {
    category: 'admin',
    ru: {
      label: 'NUSS',
      definition: 'Número de Afiliación a la Seguridad Social — испанский номер социального страхования (11 цифр). Выдаётся TGSS один раз на всю жизнь. Нужен для трудового контракта, regularan medical, autónomo-регистрации.',
    },
    en: {
      label: 'NUSS',
      definition: 'Número de Afiliación a la Seguridad Social — Spain\'s social-security ID (11 digits). Issued once for life by TGSS. Required for any employment contract, public-healthcare registration, autónomo enrolment.',
    },
  },
  'ebau': {
    category: 'education',
    ru: {
      label: 'EBAU',
      definition: 'Evaluación de Bachillerato para el Acceso a la Universidad — испанский экзамен после Bachillerato для поступления в университет. До 2017 года назывался Selectividad. Балл EBAU + средний школьный = nota de corte для приёма.',
    },
    en: {
      label: 'EBAU',
      definition: 'Evaluación de Bachillerato para el Acceso a la Universidad — Spain\'s post-Bachillerato university-entrance exam. Until 2017 known as Selectividad. The EBAU score plus the school average gives the nota de corte for admission.',
    },
  },
  'tsu-pt': {
    category: 'tax',
    ru: {
      label: 'TSU',
      definition: 'Taxa Social Única — португальский социальный взнос. Наёмные: 11 % с зарплаты + 23,75 % с работодателя. Самозанятые: 21,4 % с 70 % выручки. Покрывает пенсию, медицину (SNS), пособия.',
    },
    en: {
      label: 'TSU',
      definition: 'Taxa Social Única — Portugal\'s social-security contribution. Salaried: 11 % from the employee plus 23.75 % from the employer. Self-employed: 21.4 % on 70 % of revenue. Covers pension, healthcare (SNS), benefits.',
    },
  },

  // ── UAE — visas + civil registry ────────────────────────────────────
  'golden-visa-ae': {
    category: 'admin',
    ru: {
      label: 'Golden Visa',
      definition: '10-летний ВНЖ ОАЭ с продлением. Маршруты: недвижимость от AED 2 000 000, инвестор с капиталом от AED 2 млн, талант (учёные, врачи, отличники, креативщики, спортсмены), пенсионер 55+ (AED 1 млн недвижимости или сбережений или AED 20 тыс пенсии в месяц). Самоспонсируемый, не привязан к работодателю.',
    },
    en: {
      label: 'Golden Visa',
      definition: 'The UAE 10-year residency, renewable. Routes: real estate from AED 2,000,000, investor with AED 2M capital, talent (scientists, doctors, top students, creatives, athletes), pensioner aged 55+ (AED 1M property or savings or AED 20K/month pension). Self-sponsored, employer-independent.',
    },
  },
  'green-visa-ae': {
    category: 'admin',
    ru: {
      label: 'Green Visa',
      definition: '5-летний ВНЖ ОАЭ для квалифицированных наёмников (бакалавр + зарплата от AED 15 000) и фрилансеров (разрешение MoHRE + бакалавр или 3 года опыта). Без работодателя-спонсора, с возможностью спонсировать семью.',
    },
    en: {
      label: 'Green Visa',
      definition: 'The UAE 5-year residency for skilled employees (bachelor\'s + salary above AED 15,000) and freelancers (MoHRE permit + bachelor\'s or 3 years\' experience). No employer sponsor required; dependants can be sponsored on top.',
    },
  },
  'emirates-id': {
    category: 'admin',
    ru: {
      label: 'Emirates ID',
      definition: 'Удостоверение личности резидента ОАЭ, выпускается ICP. Обязателен для всех проживающих в стране: банк, аренда, медицина, школа, выезд-въезд. Срок действия совпадает с визой.',
    },
    en: {
      label: 'Emirates ID',
      definition: 'The mandatory UAE residence ID, issued by ICP. Required for every resident: banking, tenancy, healthcare, schooling, border crossings. Validity tracks the underlying visa.',
    },
  },
  'icp': {
    category: 'admin',
    ru: {
      label: 'ICP',
      definition: 'Federal Authority for Identity, Citizenship, Customs and Port Security — федеральный орган ОАЭ по вопросам идентификации, гражданства, таможни и пограничной безопасности. Заявления на визы и Emirates ID подаются через портал ICP.',
    },
    en: {
      label: 'ICP',
      definition: 'The UAE\'s Federal Authority for Identity, Citizenship, Customs and Port Security. Handles visa applications, Emirates ID issuance, and immigration records via the ICP portal.',
    },
  },
  'mohre': {
    category: 'admin',
    ru: {
      label: 'MoHRE',
      definition: 'Ministry of Human Resources and Emiratisation — министерство ОАЭ по труду. Регулирует контракты найма, разрешения на работу, фриланс-пермиты, систему WPS (защита зарплат), классификацию навыков.',
    },
    en: {
      label: 'MoHRE',
      definition: 'The UAE\'s Ministry of Human Resources and Emiratisation. Regulates employment contracts, work permits, freelance permits, the Wage Protection System (WPS), and skill-level classification.',
    },
  },
  'amer': {
    category: 'admin',
    ru: {
      label: 'AMER',
      definition: 'Сеть сервисных центров (Дубай) под GDRFA для подачи документов на резидентство, продление визы и Emirates ID офлайн. В Абу-Даби аналог — Tasheel.',
    },
    en: {
      label: 'AMER',
      definition: 'A network of in-person service centres in Dubai operated under GDRFA for residency applications, visa renewals, and Emirates ID processing. Abu Dhabi\'s equivalent is Tasheel.',
    },
  },
  'gdrfa': {
    category: 'admin',
    ru: {
      label: 'GDRFA',
      definition: 'General Directorate of Residency and Foreigners Affairs (Дубай) — эмиратский орган, выдающий разрешения на въезд, продлевающий визы, оформляющий резидентство. Работает поверх ICP по эмиратским вопросам.',
    },
    en: {
      label: 'GDRFA',
      definition: 'The General Directorate of Residency and Foreigners Affairs in Dubai. Issues entry permits, processes visa renewals, and handles residency at the emirate level above the federal ICP.',
    },
  },

  // ── UAE — taxes ─────────────────────────────────────────────────────
  'corporate-tax-ae': {
    category: 'tax',
    ru: {
      label: 'Corporate Tax (UAE)',
      definition: 'Федеральный корпоративный налог ОАЭ, введён 1 июня 2023: 9 % на прибыль свыше AED 375 000 для mainland-структур и неквалифицированных free-zone компаний. Ниже порога — 0 %. Free-zone-структуры с QFZP-статусом сохраняют 0 % на квалифицированный доход.',
    },
    en: {
      label: 'Corporate Tax (UAE)',
      definition: 'The UAE federal corporate tax, in force since 1 June 2023: 9 % on taxable profit above AED 375,000 for mainland and non-qualifying free zone entities. Below the threshold: 0 %. Free zone entities with Qualifying Free Zone Person status remain at 0 % on qualifying income.',
    },
  },
  'qfzp': {
    category: 'tax',
    ru: {
      label: 'QFZP',
      definition: 'Qualifying Free Zone Person — статус для свободно-зонной компании, дающий 0 % корпоративного налога на "qualifying income" (внутризоновая торговля, экспорт услуг, производство). Требует существенного присутствия, аудита, transfer-pricing-документации; non-qualifying доход облагается 9 %.',
    },
    en: {
      label: 'QFZP',
      definition: 'Qualifying Free Zone Person — the status that keeps a free-zone company at 0 % corporate tax on qualifying income (intra-zone trade, export of services, manufacturing). Requires substance, audit, and transfer-pricing documentation; non-qualifying income falls under the 9 % rate.',
    },
  },
  'free-zone': {
    category: 'tax',
    ru: {
      label: 'free zone',
      definition: 'Свободные экономические зоны ОАЭ — обособленные юрисдикции (DMCC, DIFC, JAFZA, ADGM и ~45 других) с собственной регуляторикой, 100 % иностранным владением, упрощённой регистрацией. Налоговая льгота через QFZP-статус.',
    },
    en: {
      label: 'free zone',
      definition: 'UAE free zones — special economic jurisdictions (DMCC, DIFC, JAFZA, ADGM and roughly 45 others) with their own regulators, 100 % foreign ownership, and streamlined incorporation. Corporate tax relief flows through the QFZP route.',
    },
  },
  'fta': {
    category: 'tax',
    ru: {
      label: 'FTA',
      definition: 'Federal Tax Authority — налоговый орган ОАЭ. Регистрирует плательщиков НДС, корпоративного налога, акциза; выдаёт TRC (сертификат налогового резидентства); ведёт портал EmaraTax для подачи отчётности и уплаты.',
    },
    en: {
      label: 'FTA',
      definition: 'The UAE Federal Tax Authority. Registers taxpayers for VAT, corporate tax, and excise; issues the Tax Residency Certificate (TRC); runs the EmaraTax portal for filings and payments.',
    },
  },
  'vat-ae': {
    category: 'tax',
    ru: {
      label: 'VAT (UAE)',
      definition: 'Федеральный НДС ОАЭ — 5 %, действует с 1 января 2018. Обязательная регистрация при налогооблагаемой выручке от AED 375 000, добровольная — от AED 187 500. Zero-rated: экспорт, международный транспорт, часть образования и медицины. Exempt: финансовые услуги, перепродажа жилья, местный транспорт.',
    },
    en: {
      label: 'VAT (UAE)',
      definition: 'The UAE federal VAT: 5 %, in force since 1 January 2018. Mandatory registration above AED 375,000 taxable supply; voluntary from AED 187,500. Zero-rated: exports, international transport, some education and healthcare. Exempt: financial services, residential resale, local transport.',
    },
  },
  'trc': {
    category: 'tax',
    ru: {
      label: 'TRC',
      definition: 'Tax Residency Certificate — справка о налоговом резидентстве, выдаваемая FTA. Подтверждает право на льготы по СоИДН для физлица или компании. Требует прохождения 183-дневного теста физического присутствия или альтернативных тестов (центр жизненных интересов).',
    },
    en: {
      label: 'TRC',
      definition: 'The Tax Residency Certificate issued by the FTA. Evidences treaty entitlement under the UAE\'s DTT network for an individual or a company. Requires passing the 183-day physical-presence test or the alternative centre-of-vital-interests tests.',
    },
  },
  'emaratax': {
    category: 'tax',
    ru: {
      label: 'EmaraTax',
      definition: 'Цифровой портал Федерального налогового органа ОАЭ. Через него идут регистрация, подача деклараций (НДС, корпоративный, акциз), уплата налогов и оформление TRC.',
    },
    en: {
      label: 'EmaraTax',
      definition: 'The UAE Federal Tax Authority\'s digital portal. Handles registration, VAT and corporate-tax filings, excise returns, tax payments, and TRC requests.',
    },
  },
  'small-business-relief': {
    category: 'tax',
    ru: {
      label: 'Small Business Relief',
      definition: 'Льгота по корпоративному налогу ОАЭ для компаний с выручкой до AED 3 000 000: элективное освобождение от налога на период до конца 2026 года. Не применяется к QFZP и MNE-группам.',
    },
    en: {
      label: 'Small Business Relief',
      definition: 'UAE corporate tax relief for entities with revenue up to AED 3,000,000: an elective exemption from taxable profit through the end of 2026. Not available to QFZPs or MNE groups.',
    },
  },
  'pillar-two': {
    category: 'tax',
    ru: {
      label: 'Pillar Two',
      definition: 'OECD-механизм глобальной минимальной ставки 15 % для крупных МНК-групп (консолидированная выручка от EUR 750 млн). ОАЭ ввели Domestic Minimum Top-up Tax (DMTT) с 1 января 2025 года; малых и средних бизнесов не затрагивает.',
    },
    en: {
      label: 'Pillar Two',
      definition: 'The OECD\'s global minimum 15 % rate for large MNE groups (consolidated revenue from EUR 750M). The UAE introduced a Domestic Minimum Top-up Tax (DMTT) on 1 January 2025; it does not affect small or mid-sized businesses.',
    },
  },

  // ── UAE — property / utilities / transit ────────────────────────────
  'dld': {
    category: 'property',
    ru: {
      label: 'DLD',
      definition: 'Dubai Land Department — земельный департамент Дубая. Регистрирует сделки с недвижимостью, выдаёт title deed, держит реестр Oqood для off-plan. Transfer fee 4 % от цены платится при регистрации.',
    },
    en: {
      label: 'DLD',
      definition: 'The Dubai Land Department. Registers real estate transactions, issues the title deed, and runs the Oqood off-plan registry. The 4 % transfer fee is paid on registration.',
    },
  },
  'rera': {
    category: 'property',
    ru: {
      label: 'RERA',
      definition: 'Real Estate Regulatory Agency (Дубай) — регулятор рынка недвижимости. Отвечает за RERA Rental Index (потолки повышения аренды), эскроу-счета застройщиков, лицензии брокеров.',
    },
    en: {
      label: 'RERA',
      definition: 'Dubai\'s Real Estate Regulatory Agency. Maintains the RERA Rental Index (rent-increase caps), supervises developer escrow accounts, and licenses brokers.',
    },
  },
  'ejari': {
    category: 'property',
    ru: {
      label: 'Ejari',
      definition: 'Реестр аренды в Дубае. Регистрация договора через Ejari обязательна, выдаётся номер контракта, без которого DEWA не подключит коммуналку, школа не возьмёт ребёнка по адресу, RTA не зарегистрирует машину. Сбор AED 220.',
    },
    en: {
      label: 'Ejari',
      definition: 'Dubai\'s tenancy registry. Registering a lease through Ejari is mandatory; the issued contract number is required to activate DEWA utilities, enrol a child at a local school, or register a car with RTA. Fee AED 220.',
    },
  },
  'oqood': {
    category: 'property',
    ru: {
      label: 'Oqood',
      definition: 'Реестр off-plan-сделок Дубая, ведомый DLD. Регистрирует право требования по строящейся недвижимости до выдачи title deed на готовый объект.',
    },
    en: {
      label: 'Oqood',
      definition: 'Dubai\'s off-plan registry, maintained by DLD. Records the buyer\'s claim on an under-construction unit until the title deed is issued at handover.',
    },
  },
  'freehold-ae': {
    category: 'property',
    ru: {
      label: 'freehold',
      definition: 'В ОАЭ freehold — право полного владения для иностранцев в специально обозначенных зонах каждого эмирата (в Дубае — широкая карта, в Абу-Даби — выборочно, в Шардже — 100-летний leasehold для не-арабов).',
    },
    en: {
      label: 'freehold',
      definition: 'In the UAE, freehold is full foreign ownership available in designated zones per emirate (broad in Dubai, selective in Abu Dhabi, 100-year leasehold in Sharjah for non-Arab nationals).',
    },
  },
  'eibor': {
    category: 'property',
    ru: {
      label: 'EIBOR',
      definition: 'Emirates Interbank Offered Rate — базовая межбанковская ставка ОАЭ. Большинство ипотек экспата привязаны к 3- или 6-месячному EIBOR + банковская маржа.',
    },
    en: {
      label: 'EIBOR',
      definition: 'The Emirates Interbank Offered Rate — the UAE\'s benchmark interbank rate. Most expat mortgages are 3- or 6-month-EIBOR-linked plus the bank\'s margin.',
    },
  },
  'noc': {
    category: 'property',
    ru: {
      label: 'NOC',
      definition: 'No Objection Certificate — справка от застройщика об отсутствии возражений на продажу/передачу собственности. Обязательна для перерегистрации в DLD; обычно стоит AED 500-5 000.',
    },
    en: {
      label: 'NOC',
      definition: 'A No Objection Certificate from the developer, confirming no objection to a sale or transfer of property. Mandatory for DLD re-registration; typical cost AED 500-5,000.',
    },
  },
  'tabreed': {
    category: 'property',
    ru: {
      label: 'Tabreed',
      definition: 'Один из крупных провайдеров централизованного охлаждения (district cooling) в ОАЭ. Жильцы платят отдельный счёт за охлаждение поверх DEWA; тариф измеряется в AED за RT (refrigeration ton).',
    },
    en: {
      label: 'Tabreed',
      definition: 'A major UAE district-cooling provider. Residents pay a separate chiller bill on top of DEWA; tariff is measured in AED per refrigeration ton (RT).',
    },
  },
  'empower': {
    category: 'property',
    ru: {
      label: 'Empower',
      definition: 'Крупнейший провайдер district cooling в Дубае. Обслуживает Marina, JLT, Business Bay, Downtown; счёт за охлаждение отделён от DEWA и пропорционален площади + потреблению.',
    },
    en: {
      label: 'Empower',
      definition: 'The largest district-cooling provider in Dubai. Serves Marina, JLT, Business Bay, and Downtown; the chiller bill is separate from DEWA and scales with floor area and consumption.',
    },
  },
  'dewa': {
    category: 'admin',
    ru: {
      label: 'DEWA',
      definition: 'Dubai Electricity and Water Authority — коммунальный оператор Дубая. Счёт включает электричество, воду, канализацию и Housing Fee 5 % от арендной стоимости.',
    },
    en: {
      label: 'DEWA',
      definition: 'Dubai Electricity and Water Authority. Its monthly bill bundles electricity, water, sewerage, and the 5 % municipality housing fee on rental value.',
    },
  },
  'salik': {
    category: 'transit',
    ru: {
      label: 'Salik',
      definition: 'Электронная система платных дорог Дубая (RTA). AED 4 за проезд через большинство гейтов; AED 6 в часы пик на ключевых развязках с января 2025. Без бумажных квитанций — счёт списывается с привязанного аккаунта.',
    },
    en: {
      label: 'Salik',
      definition: 'Dubai\'s electronic toll system (RTA). AED 4 per passage at most gates; AED 6 at peak times on key crossings since January 2025. No paper tickets — the fee debits from a registered account.',
    },
  },
  'nol-card': {
    category: 'transit',
    ru: {
      label: 'Nol card',
      definition: 'Карта оплаты общественного транспорта Дубая (Метро, Трамвай, RTA-автобусы, водный транспорт). Силовая (silver) для регулярного использования AED 25 + баланс.',
    },
    en: {
      label: 'Nol card',
      definition: 'Dubai\'s contactless transit card for Metro, Tram, RTA buses, and water transport. The Silver tier (everyday) costs AED 25 plus stored balance.',
    },
  },
  'mulkiya': {
    category: 'transit',
    ru: {
      label: 'Mulkiya',
      definition: 'Свидетельство о регистрации автомобиля в ОАЭ. Продлевается ежегодно (AED 400-800) с обязательным техосмотром (для машин старше 3 лет) и оплатой штрафов.',
    },
    en: {
      label: 'Mulkiya',
      definition: 'The UAE vehicle registration certificate. Renewed annually (AED 400-800) with a mandatory inspection for cars over 3 years old and all outstanding fines cleared.',
    },
  },
  'mmi': {
    category: 'misc',
    ru: {
      label: 'MMI',
      definition: 'Maritime and Mercantile International — лицензированная сеть алкогольных магазинов Дубая. Конкурирует с African+Eastern. С 2023 года личная алкогольная лицензия не требуется для покупки в магазине, но действуют возрастные и эмиратские ограничения.',
    },
    en: {
      label: 'MMI',
      definition: 'Maritime and Mercantile International — one of Dubai\'s two licensed alcohol retailers (competing with African+Eastern). Since 2023 a personal alcohol licence is no longer required to buy at the store, though age and emirate restrictions still apply.',
    },
  },
  'ramadan': {
    category: 'misc',
    ru: {
      label: 'Ramadan',
      definition: 'Священный месяц мусульманского поста с рассвета до заката. В ОАЭ: укороченный рабочий день (на 2 часа), большинство ресторанов открыты с послаблениями с 2024 года, государственные часы работы сдвинуты, шумовые ограничения.',
    },
    en: {
      label: 'Ramadan',
      definition: 'The Islamic month of dawn-to-dusk fasting. In the UAE: working hours shortened by 2 hours, most restaurants open with relaxed rules from 2024 onward, government schedules shifted, noise restrictions in force.',
    },
  },

  // ── UAE — healthcare ────────────────────────────────────────────────
  'dha': {
    category: 'health',
    ru: {
      label: 'DHA',
      definition: 'Dubai Health Authority — регулятор здравоохранения эмирата Дубай. Лицензирует клиники и медиков, ведёт публичную сеть госпиталей (Rashid, Latifa, Hatta), управляет обязательной страховой схемой для жителей Дубая.',
    },
    en: {
      label: 'DHA',
      definition: 'Dubai Health Authority, the healthcare regulator for the Emirate of Dubai. Licenses facilities and practitioners, runs the public hospital network (Rashid, Latifa, Hatta), and operates the emirate-wide mandatory insurance scheme.',
    },
  },
  'doh-ad': {
    category: 'health',
    ru: {
      label: 'DoH',
      definition: 'Department of Health — Abu Dhabi (бывший HAAD) — регулятор здравоохранения эмирата Абу-Даби. Лицензирует медиков, держит ABM-страховую схему для жителей эмирата, курирует государственный оператор SEHA.',
    },
    en: {
      label: 'DoH',
      definition: 'Department of Health, Abu Dhabi (formerly HAAD), the healthcare regulator for the Emirate of Abu Dhabi. Licenses practitioners, runs the emirate insurance scheme, and oversees the SEHA public hospital network.',
    },
  },
  'haad': {
    category: 'health',
    ru: {
      label: 'HAAD',
      definition: 'Health Authority Abu Dhabi, прежнее название регулятора здравоохранения эмирата. С 2017 года переименован в DoH (Department of Health, Abu Dhabi); сертификаты HAAD для медиков формально приравнены к DoH-лицензиям.',
    },
    en: {
      label: 'HAAD',
      definition: 'Health Authority Abu Dhabi, the former name of the emirate\'s healthcare regulator. Renamed DoH (Department of Health, Abu Dhabi) in 2017; legacy HAAD professional licences map to current DoH credentials.',
    },
  },
  'mohap': {
    category: 'health',
    ru: {
      label: 'MoHAP',
      definition: 'Ministry of Health and Prevention — федеральное министерство здравоохранения ОАЭ. Регулирует Северные эмираты (Шарджа, Аджман, Умм-эль-Кайвайн, Рас-эль-Хайма, Эль-Фуджайра), задаёт федеральный каркас, контролирует ввоз лекарств и национальный календарь прививок.',
    },
    en: {
      label: 'MoHAP',
      definition: 'The UAE Ministry of Health and Prevention. Regulates the Northern Emirates (Sharjah, Ajman, Umm Al Quwain, Ras Al Khaimah, Fujairah), sets the federal healthcare framework, and controls drug imports and the national vaccination schedule.',
    },
  },
  'seha': {
    category: 'health',
    ru: {
      label: 'SEHA',
      definition: 'Abu Dhabi Health Services Company — государственная сеть госпиталей эмирата Абу-Даби. Включает Sheikh Khalifa Medical City, Tawam, Al Mafraq и сеть амбулаторий. Подотчётна DoH.',
    },
    en: {
      label: 'SEHA',
      definition: 'Abu Dhabi Health Services Company, the public hospital network for the Emirate of Abu Dhabi. Operates Sheikh Khalifa Medical City, Tawam, Al Mafraq and a network of clinics; reports to DoH.',
    },
  },
  'dataflow': {
    category: 'health',
    ru: {
      label: 'DataFlow',
      definition: 'Платформа фоновой проверки документов для медиков в ОАЭ и Заливе. Подтверждает дипломы, лицензии, опыт работы, рекомендации; обязательный этап перед допуском к Prometric-экзамену и лицензированию у DHA, DoH или MoHAP.',
    },
    en: {
      label: 'DataFlow',
      definition: 'The primary credentials-verification platform for healthcare professionals in the UAE and the Gulf. Verifies diplomas, licences, employment history and references; mandatory before the Prometric exam and licensure by DHA, DoH or MoHAP.',
    },
  },
  'prometric': {
    category: 'health',
    ru: {
      label: 'Prometric',
      definition: 'Стандартизированный профильный экзамен для иностранных медиков, желающих практиковать в ОАЭ. Сдаётся за компьютером в авторизованных центрах; результат подтверждает квалификацию для DHA, DoH или MoHAP. Без него лицензия не выдаётся.',
    },
    en: {
      label: 'Prometric',
      definition: 'The computer-based licensure exam taken by foreign-trained healthcare professionals applying to practise in the UAE. Required by DHA, DoH and MoHAP; without a passing score no professional licence is issued.',
    },
  },

  // ── Austria — administrative / immigration ─────────────────────────
  'meldezettel': {
    category: 'admin',
    ru: {
      label: 'Meldezettel',
      definition: 'Свидетельство о регистрации по месту жительства в Австрии. Оформляется (Anmeldung) в местном Meldeamt в течение трёх дней после заселения; нужно для банка, карты ВНЖ, SIM и почти любых услуг.',
    },
    en: {
      label: 'Meldezettel',
      definition: 'Austria’s proof of registered address. Filed (Anmeldung) at the local Meldeamt within three days of moving in; required for a bank account, the residence card, a SIM and almost every service.',
    },
  },
  'rot-weiss-rot-karte': {
    category: 'admin',
    ru: {
      label: 'Rot-Weiß-Rot-Karte',
      definition: 'Австрийская «красно-бело-красная» карта: балльное разрешение на работу и проживание для квалифицированных специалистов вне ЕС. Действует 24 месяца, затем переходит в RWR Card Plus с полным доступом к рынку труда.',
    },
    en: {
      label: 'Red-White-Red Card',
      definition: 'Austria’s points-based work-and-residence permit for qualified non-EU workers. Valid for 24 months, then converts to a Red-White-Red Card Plus with full labour-market access.',
    },
  },
  'id-austria': {
    category: 'admin',
    ru: {
      label: 'ID Austria',
      definition: 'Национальная цифровая идентичность Австрии (преемник Handy-Signatur): электронная подпись и ключ к госуслугам — подача налогов через FinanzOnline, регистрация, цифровые права.',
    },
    en: {
      label: 'ID Austria',
      definition: 'Austria’s national digital identity (successor to Handy-Signatur): an e-signature and the key to e-government, including tax filing via FinanzOnline, registration, and the digital driving licence.',
    },
  },
  'ausgleichszulagenrichtsatz': {
    category: 'admin',
    ru: {
      label: 'Ausgleichszulagenrichtsatz',
      definition: 'Базовый прожиточный минимум в Австрии, по которому проверяют достаточность дохода для ВНЖ (особенно settlement для финансово независимых). В 2026: около €1 308/мес на одного, €2 064 на пару.',
    },
    en: {
      label: 'Ausgleichszulagenrichtsatz',
      definition: 'Austria’s reference subsistence rate, used to test income sufficiency for residence permits (especially the settlement permit for the financially independent). 2026: about €1,308/mo single, €2,064 couple.',
    },
  },
  'integrationsvereinbarung': {
    category: 'admin',
    ru: {
      label: 'Integrationsvereinbarung',
      definition: 'Интеграционное соглашение Австрии: обязательство выучить немецкий до A2 (Модуль 1) и B1 (Модуль 2). Уровень B1 — условие для постоянного проживания.',
    },
    en: {
      label: 'Integration Agreement',
      definition: 'Austria’s Integrationsvereinbarung: a duty to reach German A2 (Module 1) and B1 (Module 2) over the residence cycle. B1 is a precondition for permanent residence.',
    },
  },

  // ── Austria — health ───────────────────────────────────────────────
  'oegk': {
    category: 'health',
    ru: {
      label: 'ÖGK',
      definition: 'Österreichische Gesundheitskasse — единая государственная больничная касса Австрии (с 2020). Доступ к медицине через e-card; страховка привязана к занятости, члены семьи со-застрахованы.',
    },
    en: {
      label: 'ÖGK',
      definition: 'The Österreichische Gesundheitskasse, Austria’s single public health-insurance fund (since 2020). Access runs through the e-card; cover is tied to employment, with family members co-insured.',
    },
  },

  // ── Austria — tax ──────────────────────────────────────────────────
  'kest': {
    category: 'tax',
    ru: {
      label: 'KESt',
      definition: 'Kapitalertragsteuer — австрийский налог на доход с капитала: 27,5 % на дивиденды и прирост по ценным бумагам (25 % на банковские проценты), удерживается у источника как окончательный.',
    },
    en: {
      label: 'KESt',
      definition: 'Kapitalertragsteuer, Austria’s capital-income tax: 27.5 % on dividends and securities gains (25 % on bank interest), withheld at source as a final tax.',
    },
  },
  'immoest': {
    category: 'tax',
    ru: {
      label: 'ImmoESt',
      definition: 'Immobilienertragsteuer — австрийский налог 30 % на прибыль от продажи частной недвижимости, без срока давности. Действует освобождение для основного жилья (Hauptwohnsitzbefreiung).',
    },
    en: {
      label: 'ImmoESt',
      definition: 'Immobilienertragsteuer, Austria’s 30 % tax on gains from selling private real estate, with no holding-period escape. A main-residence exemption (Hauptwohnsitzbefreiung) applies.',
    },
  },
  'zuzugsbeguenstigung': {
    category: 'tax',
    ru: {
      label: 'Zuzugsbegünstigung',
      definition: 'Узкая налоговая льгота Австрии (§103 EStG) для переезжающих учёных и исследователей: пониженная ставка на зарубежный доход и 30 % необлагаемая надбавка на 5 лет. Не общий режим для экспатов.',
    },
    en: {
      label: 'Zuzugsbegünstigung',
      definition: 'A narrow Austrian tax relief (§103 EStG) for incoming scientists and researchers: a capped rate on foreign income plus a 30 % tax-free allowance for five years. Not a general expat regime.',
    },
  },

  // ── Austria — property ─────────────────────────────────────────────
  'grunderwerbsteuer': {
    category: 'property',
    ru: {
      label: 'Grunderwerbsteuer',
      definition: 'Налог на переход права собственности на недвижимость в Австрии: стандартно 3,5 % от цены покупки, плюс 1,1 % сбор за внесение в земельную книгу (Grundbuch).',
    },
    en: {
      label: 'Grunderwerbsteuer',
      definition: 'Austria’s real-estate transfer tax: 3.5 % of the purchase price as standard, plus a 1.1 % land-registry (Grundbuch) registration fee.',
    },
  },
  'grundverkehr': {
    category: 'property',
    ru: {
      label: 'Grundverkehr',
      definition: 'Контроль сделок с недвижимостью в Австрии. Покупателям вне ЕС нужно одобрение земельного органа (Grundverkehrsbehörde) до регистрации; строгость зависит от земли (Тироль, Зальцбург и Вена особенно строги).',
    },
    en: {
      label: 'Grundverkehr',
      definition: 'Austria’s land-transfer control. Non-EU buyers need approval from the regional authority (Grundverkehrsbehörde) before registration; strictness varies by state (Tyrol, Salzburg and Vienna are notably strict).',
    },
  },

  // ── Austria — transit ──────────────────────────────────────────────
  'nova-austria': {
    category: 'transit',
    ru: {
      label: 'NoVA',
      definition: 'Normverbrauchsabgabe — разовый регистрационный налог на авто в Австрии, зависит от выбросов CO2 (до 80 %). Электромобили освобождены.',
    },
    en: {
      label: 'NoVA',
      definition: 'Normverbrauchsabgabe, Austria’s one-off CO2-based car registration tax (up to 80 %). Electric vehicles are exempt.',
    },
  },
  'autobahnvignette': {
    category: 'transit',
    ru: {
      label: 'Vignette',
      definition: 'Виньетка для проезда по австрийским автобанам: обязательна, около €106,80 на год (2026). Есть цифровая версия; отдельные туннели и перевалы оплачиваются дополнительно.',
    },
    en: {
      label: 'Vignette',
      definition: 'The toll sticker for Austria’s motorways: mandatory, about €106.80 for the year (2026). A digital version exists; some tunnels and alpine passes cost extra.',
    },
  },
  'klimaticket': {
    category: 'transit',
    ru: {
      label: 'KlimaTicket',
      definition: 'Единый годовой проездной на весь общественный транспорт Австрии: около €1 400 (2026), льготный около €1 050. Покрывает поезда ÖBB, региональный и городской транспорт.',
    },
    en: {
      label: 'KlimaTicket',
      definition: 'Austria’s nationwide annual public-transport pass: about €1,400 (2026), reduced about €1,050. Covers ÖBB trains plus regional and city transport.',
    },
  },

  // ── Estonia — banking ──────────────────────────────────────────────
  'finantsinspektsioon': {
    category: 'misc',
    ru: {
      label: 'Finantsinspektsioon',
      definition: 'Орган финансового надзора Эстонии: лицензирует и контролирует банки, страховщиков, платёжные и инвестиционные учреждения. По его открытому реестру проверяют, действительно ли компания, называющая себя эстонским банком, имеет лицензию кредитного учреждения.',
    },
    en: {
      label: 'Finantsinspektsioon',
      definition: 'Estonia’s financial supervisor, which licenses and oversees banks, insurers, payment and e-money institutions and investment firms. Its public register is the only reliable way to check whether a company calling itself an Estonian bank actually holds a credit institution licence.',
    },
  },
  'krediidiasutus': {
    category: 'misc',
    ru: {
      label: 'krediidiasutus',
      definition: 'Кредитное учреждение, эстонский юридический термин для банка в строгом смысле: организация с лицензией на приём вкладов от населения и выдачу кредитов. Только такие учреждения участвуют в эстонской системе гарантирования вкладов.',
    },
    en: {
      label: 'krediidiasutus',
      definition: 'Credit institution, the Estonian legal term for a bank in the strict sense: licensed to take deposits from the public and grant credit. Only credit institutions authorised in Estonia take part in the Estonian deposit guarantee scheme.',
    },
  },
  'makseasutus': {
    category: 'misc',
    ru: {
      label: 'makseasutus',
      definition: 'Платёжное учреждение: лицензировано и поднадзорно, но не банк. Может проводить платежи и открывать счета, но не вправе принимать вклады и кредитовать; деньги клиентов хранятся обособленно и не покрыты гарантией вкладов в €100 000.',
    },
    en: {
      label: 'makseasutus',
      definition: 'Payment institution: licensed and supervised, but not a bank. It may move money and issue accounts, yet cannot take deposits or lend, and client funds are safeguarded separately rather than covered by the €100,000 deposit guarantee.',
    },
  },
  'tagatisfond': {
    category: 'misc',
    ru: {
      label: 'Tagatisfond',
      definition: 'Гарантийный фонд Эстонии: при банкротстве банка компенсирует вкладчику до €100 000 в одном кредитном учреждении, выплата в течение семи рабочих дней. Филиалы иностранных банков покрываются системой гарантирования своей страны.',
    },
    en: {
      label: 'Tagatisfond',
      definition: 'The Estonian Guarantee Fund, which compensates depositors up to €100,000 per depositor per credit institution if a bank fails, payable within seven working days. Branches of foreign banks are covered by their home country’s scheme instead.',
    },
  },
  'rahapts': {
    category: 'misc',
    ru: {
      label: 'RahaPTS',
      definition: 'Эстонский закон о противодействии отмыванию денег и финансированию терроризма. Обязывает банк идентифицировать клиента, установить бенефициарных владельцев, выяснить происхождение средств и оценить обороты до открытия счёта, поэтому цифровая личность не отменяет очную проверку.',
    },
    en: {
      label: 'RahaPTS',
      definition: 'Estonia’s anti-money-laundering act. It obliges a bank to identify the customer, establish beneficial owners, understand the source of funds and forecast turnover before opening an account, which is why a digital identity does not remove the in-person check.',
    },
  },
  'e-resident-digi-id': {
    category: 'misc',
    ru: {
      label: 'e-resident digi-ID',
      definition: 'Цифровое удостоверение личности по программе e-Residency. Позволяет из-за рубежа подписывать документы, вести эстонскую компанию и пользоваться уже открытым счётом, но это не виза и не вид на жительство, и ни один банк не обязан открывать по нему счёт.',
    },
    en: {
      label: 'e-resident digi-ID',
      definition: 'The digital identity card issued under the e-Residency programme. It lets a holder abroad sign documents, run an Estonian company and operate an existing Estonian bank account, but it is not a visa or a residence permit and obliges no bank to open an account.',
    },
  },
  'sepa-kiirmakse': {
    category: 'misc',
    ru: {
      label: 'SEPA kiirmakse',
      definition: 'Мгновенный перевод в евро: деньги зачисляются получателю за секунды и круглосуточно. С октября 2025 года каждый банк еврозоны обязан не только принимать, но и отправлять такие переводы и не вправе брать за них больше, чем за обычный.',
    },
    en: {
      label: 'SEPA kiirmakse',
      definition: 'A euro credit transfer that reaches the payee’s account within seconds, around the clock. Since October 2025 every euro-area bank must be able to send as well as receive them, and may not charge more than for an ordinary transfer.',
    },
  },
  'iban-ee': {
    category: 'misc',
    ru: {
      label: 'эстонский IBAN',
      definition: 'Номер счёта, начинающийся с EE, выданный работающим в Эстонии учреждением. Работодатели, Налогово-таможенный департамент и арендодатели обычно принимают любой IBAN зоны SEPA, поэтому эстонский нужен для удобства, а не по закону.',
    },
    en: {
      label: 'Estonian IBAN',
      definition: 'A bank account number beginning EE, issued by an institution operating in Estonia. Employers, the Tax and Customs Board and landlords generally accept any SEPA IBAN, so an Estonian one is a convenience rather than a legal requirement.',
    },
  },

  // ── Estonia — state bodies, digital identity, status ────────────────
  'ppa': {
    category: 'admin',
    ru: {
      label: 'PPA',
      definition: 'Департамент полиции и погранохраны: единое ведомство, отвечающее и за полицию, и за границу, и за все документы, связанные с личностью и миграцией. Выдаёт ID-карты, виды на жительство и паспорта, принимает ходатайства о гражданстве.',
    },
    en: {
      label: 'PPA',
      definition: 'The Police and Border Guard Board, Estonia’s single agency for policing, the border and all identity and migration paperwork. It issues ID cards, residence permits and passports, and receives citizenship applications.',
    },
  },
  'rahvastikuregister-transport': {
    category: 'admin',
    ru: {
      label: 'rahvastikuregister',
      definition: 'Регистр народонаселения Эстонии, фиксирующий официальное место жительства. Именно эта запись определяет школу по месту жительства, право обменять иностранные права на эстонские и бесплатный городской транспорт в Таллинне.',
    },
    en: {
      label: 'rahvastikuregister',
      definition: 'Estonia’s population register, which records a person’s official place of residence. That entry decides the school of residence, the right to exchange a foreign driving licence and free city transport in Tallinn.',
    },
  },
  'id-kaart': {
    category: 'admin',
    ru: {
      label: 'ID-kaart',
      definition: 'Обязательный документ, удостоверяющий личность жителя, и одновременно криптографический ключ: даёт вход в государственные и банковские сервисы и создаёт юридически значимую цифровую подпись. Обладателю ВНЖ ту же роль играет карта вида на жительство.',
    },
    en: {
      label: 'ID-kaart',
      definition: 'The compulsory identity document of a resident, and also a cryptographic key: it authenticates you to state and bank services and produces a legally binding digital signature. Residence-permit holders use their permit card for the same purpose.',
    },
  },
  'mobiil-id': {
    category: 'admin',
    ru: {
      label: 'Mobiil-ID',
      definition: 'Цифровая идентификация на базе SIM-карты: вход и подпись с телефона без картридера. На выборах в Европарламент 2024 года через неё подано 31,4 % всех электронных голосов.',
    },
    en: {
      label: 'Mobiil-ID',
      definition: 'A SIM-based digital identity that authenticates and signs from a phone without a card reader. It carried 31.4% of all internet votes at the 2024 European Parliament election.',
    },
  },
  'smart-id': {
    category: 'admin',
    ru: {
      label: 'Smart-ID',
      definition: 'Электронная идентификация в виде приложения, распространённая по всей Балтии: вход в банк и госуслуги, подписание документов. В отличие от Mobiil-ID не требует особой SIM-карты, в отличие от ID-карты не требует картридера.',
    },
    en: {
      label: 'Smart-ID',
      definition: 'An app-based electronic identity used across the Baltics for bank and state logins and for signing. Unlike Mobiil-ID it needs no special SIM, and unlike the ID card no reader.',
    },
  },
  'e-resident': {
    category: 'admin',
    ru: {
      label: 'e-residentsus',
      definition: 'Выданная государством цифровая идентификация: нерезидент может входить в сервисы, подписывать документы и дистанционно открывать и вести эстонскую компанию. Это не вид на жительство, не виза и не путь к гражданству.',
    },
    en: {
      label: 'e-residentsus',
      definition: 'A state-issued digital identity that lets a non-resident authenticate online, sign documents and found and run an Estonian company remotely. It is not a residence permit, not a visa and not a route to citizenship.',
    },
  },
  'x-tee': {
    category: 'admin',
    ru: {
      label: 'X-tee',
      definition: 'Зашифрованный слой обмена данными, работающий в Эстонии с 2001 года и связывающий сотни государственных и частных баз. Благодаря ему житель сообщает государству сведения один раз, а последующие службы забирают их оттуда, а не спрашивают заново.',
    },
    en: {
      label: 'X-tee',
      definition: 'Estonia’s encrypted data-exchange layer, running since 2001, which lets hundreds of state and private databases query one another. It is why a resident gives the state a piece of information once and later services read it instead of asking again.',
    },
  },
  'i-haaletamine': {
    category: 'admin',
    ru: {
      label: 'i-hääletamine',
      definition: 'Голосование через интернет с помощью ID-карты, Mobiil-ID или Smart-ID; применяется с местных выборов 2005 года. На парламентских выборах 2023 года так подан 51,1 % всех голосов, причём электронный голос можно перекрыть бумажным бюллетенем.',
    },
    en: {
      label: 'i-hääletamine',
      definition: 'Voting over the internet with an ID card, Mobiil-ID or Smart-ID, in use since the 2005 local elections. At the 2023 parliamentary election 51.1% of votes were cast online, and a voter may still override an online vote with a paper ballot.',
    },
  },
  'riigi-teataja': {
    category: 'admin',
    ru: {
      label: 'Riigi Teataja',
      definition: 'Официальное издание Эстонии и единственный авторитетный источник действующих текстов законов, с консолидированными редакциями и англоязычным разделом. Английские тексты информативны; юридическую силу имеет эстонский текст.',
    },
    en: {
      label: 'Riigi Teataja',
      definition: 'The official gazette of Estonia and the single authoritative source for the text of laws in force, with consolidated versions and an English section. The English texts are informative translations; the Estonian text governs.',
    },
  },
  'pikaajalise-elaniku-elamisluba': {
    category: 'admin',
    ru: {
      label: 'pikaajalise elaniku elamisluba',
      definition: 'Вид на жительство долгосрочного жителя: бессрочный статус для граждан третьих стран после пяти лет непрерывного проживания по временному ВНЖ. Требуются зарегистрированное место жительства, стабильный легальный доход, медицинская страховка и эстонский на B1.',
    },
    en: {
      label: 'pikaajalise elaniku elamisluba',
      definition: 'Estonia’s permanent residence status for non-EU nationals, issued with unlimited validity after five continuous years on a temporary permit. It requires registered residence, stable legal income, health insurance and Estonian at B1.',
    },
  },
  'kodakondsuse-eksam': {
    category: 'admin',
    ru: {
      label: 'kodakondsuse eksam',
      definition: 'Экзамен на знание Конституции Эстонской Республики и Закона о гражданстве, обязательный при натурализации наряду с языковым экзаменом на B1. Бесплатная подготовка занимает 18 академических часов и ведётся на эстонском, то есть требует уровня не ниже A2.',
    },
    en: {
      label: 'kodakondsuse eksam',
      definition: 'The exam on the Constitution of Estonia and the Citizenship Act, required for naturalisation alongside the B1 language exam. Free preparation runs 18 academic hours and is taught in Estonian, so it assumes at least A2.',
    },
  },
  'maaratlemata-kodakondsus': {
    category: 'admin',
    ru: {
      label: 'määratlemata kodakondsus',
      definition: 'Статус жителя, не имеющего гражданства ни одного государства: 58 309 человек на 1 января 2026 года. Это наследие 1991 года, касающееся приехавших в советское время и их потомков; новоприбывший иностранец в этот статус попасть не может.',
    },
    en: {
      label: 'määratlemata kodakondsus',
      definition: 'The status of a resident who holds no citizenship of any state, 58,309 people at 1 January 2026. It is a legacy of 1991 affecting Soviet-era arrivals and their descendants, and a newly arriving foreigner cannot fall into it.',
    },
  },
  'hall-pass': {
    category: 'admin',
    ru: {
      label: 'hall pass',
      definition: '«Серый» паспорт иностранца, который PPA выдаёт жителям с неопределённым гражданством или без гражданства. Удостоверяет личность и право проживания, но не является паспортом гражданина и не даёт права голоса на выборах в Рийгикогу.',
    },
    en: {
      label: 'hall pass',
      definition: 'The alien’s passport the PPA issues to residents of undetermined citizenship or to stateless residents. It certifies identity and the right to reside, but is not a citizen’s passport and confers no vote in national elections.',
    },
  },
  'ajateenistus': {
    category: 'admin',
    ru: {
      label: 'ajateenistus',
      definition: 'Срочная военная служба, привязанная к гражданству Эстонии, а не к проживанию. Мужчины-граждане проходят её в возрасте от 18 до 27 лет, женщины могут пойти добровольно, обладателей вида на жительство призыв не касается.',
    },
    en: {
      label: 'ajateenistus',
      definition: 'Compulsory military service, which attaches to Estonian citizenship rather than to residence. Male citizens serve between 18 and 27, women may volunteer, and holders of a residence permit are not conscripted.',
    },
  },
  'kaitseressursside-amet': {
    category: 'admin',
    ru: {
      label: 'Kaitseressursside Amet',
      definition: 'Департамент оборонных ресурсов: ведёт регистр воинской обязанности, проводит медицинские освидетельствования, призывает срочников, оформляет отсрочки и альтернативную службу.',
    },
    en: {
      label: 'Kaitseressursside Amet',
      definition: 'The Defence Resources Agency, which keeps the military service register, runs conscript health assessments, calls up conscripts and administers deferrals and alternative service.',
    },
  },
  'riigiinfo-1247': {
    category: 'admin',
    ru: {
      label: '1247',
      definition: 'Государственная информационная линия Эстонии для неэкстренных вопросов: работает круглосуточно и служит основным каналом информирования населения в кризис. Экстренный вызов идёт по номеру 112.',
    },
    en: {
      label: '1247',
      definition: 'Estonia’s non-emergency state information line, staffed around the clock and used as the public channel during crises. Emergencies go to 112.',
    },
  },
  'integratsiooni-sihtasutus': {
    category: 'admin',
    ru: {
      label: 'Integratsiooni Sihtasutus',
      definition: 'Государственный Фонд интеграции, отвечающий за услуги адаптации: бесплатные курсы эстонского, консультации по гражданству, подготовка к экзамену на гражданство, компенсация учебного отпуска, поддержка национально-культурных обществ.',
    },
    en: {
      label: 'Integratsiooni Sihtasutus',
      definition: 'The state Integration Foundation, responsible for adaptation services: free Estonian courses, citizenship counselling, preparation for the citizenship exam, study-leave compensation and support for minority cultural associations.',
    },
  },
  'konkurentsiamet': {
    category: 'admin',
    ru: {
      label: 'Konkurentsiamet',
      definition: 'Департамент конкуренции: одновременно антимонопольный орган и регулятор цен, согласующий тарифы электрических и газовых сетей, плату за воду и предельную цену централизованного тепла.',
    },
    en: {
      label: 'Konkurentsiamet',
      definition: 'The Estonian Competition Authority, which both enforces competition law and approves regulated prices: electricity and gas network tariffs, water charges and the maximum price of district heat.',
    },
  },
  'ttja': {
    category: 'admin',
    ru: {
      label: 'TTJA',
      definition: 'Департамент защиты прав потребителей и технического надзора, он же регулятор связи. Анализирует рынки связи, рассматривает заявки операторов на роуминговые надбавки и распоряжается господдержкой строительства скоростного интернета.',
    },
    en: {
      label: 'TTJA',
      definition: 'The Consumer Protection and Technical Regulatory Authority, which doubles as Estonia’s telecom regulator. It analyses communications markets, handles roaming surcharge requests and administers state support for broadband rollout.',
    },
  },

  // ── Estonia — tax ──────────────────────────────────────────────────
  'sotsiaalmaks': {
    category: 'tax',
    ru: {
      label: 'sotsiaalmaks',
      definition: 'Социальный налог: 33 % с зарплат и предпринимательского дохода, финансирует пенсионное и государственное медицинское страхование. Именно он «покупает» медицинскую страховку, и у него есть нижняя граница: в 2026 году минимум составляет 292,38 евро в месяц.',
    },
    en: {
      label: 'sotsiaalmaks',
      definition: 'Social tax, 33% on employment and business income, funding pension and state health insurance. It is what actually buys health coverage, and it has a floor: in 2026 the minimum obligation is €292.38 a month even for a founder whose business earned little.',
    },
  },

  // ── Estonia — healthcare ───────────────────────────────────────────
  'tervisekassa': {
    category: 'health',
    ru: {
      label: 'Tervisekassa',
      definition: 'Больничная касса Эстонии, единственный государственный закупщик медицинских услуг; в 2023 году переименована из Haigekassa. Страховка не появляется автоматически вместе с ВНЖ: она следует из уплаты социального налога, из приравненной группы или из добровольного договора.',
    },
    en: {
      label: 'Tervisekassa',
      definition: 'The Estonian Health Insurance Fund, the single public purchaser of healthcare, renamed from Haigekassa in 2023. Cover is not automatic with residence: it follows from social tax paid for you, from an equivalent group such as children and students, or from a voluntary contract.',
    },
  },
  'perearst': {
    category: 'health',
    ru: {
      label: 'perearst',
      definition: 'Семейный врач, «привратник» эстонской медицины и первая точка обращения с любой жалобой. Приём бесплатен для застрахованных; в список конкретного врача записываются письменным заявлением, и врач вправе отказать, если список полон или ваш адрес вне зоны обслуживания.',
    },
    en: {
      label: 'perearst',
      definition: 'The family doctor, gatekeeper of Estonian healthcare and first point of contact for any complaint. Visits are free for insured patients; you join a doctor’s list by written application, and a doctor may refuse if the list is full or your registered address lies outside their service area.',
    },
  },
  'vabatahtlik-kindlustus': {
    category: 'health',
    ru: {
      label: 'vabatahtlik ravikindlustus',
      definition: 'Добровольный договор страхования с Больничной кассой для тех, кто зарегистрирован как проживающий в Эстонии, но не попадает ни в одну льготную группу, чаще всего для неработающего супруга. В 2026 году стоит 272 евро в месяц, заключается на год и начинает действовать через месяц.',
    },
    en: {
      label: 'vabatahtlik ravikindlustus',
      definition: 'A voluntary insurance contract with Tervisekassa for people registered as living in Estonia who fall into no eligible group, typically a non-working spouse. In 2026 it costs €272 a month, runs for a full year and takes effect one month after signing.',
    },
  },
  'visiiditasu': {
    category: 'health',
    ru: {
      label: 'visiiditasu',
      definition: 'Визитная плата, которую поставщик услуги вправе взять с застрахованного пациента сверх оплаты Больничной кассы. Семейный врач бесплатен; специалист, стоматолог или отделение неотложной помощи могут взять до 20 евро, а с детей, пенсионеров и ряда других групп не больше 5.',
    },
    en: {
      label: 'visiiditasu',
      definition: 'The visit fee a provider may charge an insured patient on top of what Tervisekassa pays. The family doctor is free; a specialist, dentist or emergency department may charge up to €20, reduced to €5 for children, pensioners and several other groups.',
    },
  },
  'terviseamet': {
    category: 'health',
    ru: {
      label: 'Terviseamet',
      definition: 'Департамент здоровья, регулятор в отличие от плательщика. Лицензирует медицинские учреждения, регистрирует медработников, включая получивших образование вне ЕС и ЕЭЗ, надзирает за качеством услуг и ведёт публичный реестр семейных врачей и зон их обслуживания.',
    },
    en: {
      label: 'Terviseamet',
      definition: 'The Health Board, the regulator as distinct from the payer. It licenses healthcare providers, registers health professionals including those trained outside the EU and EEA, supervises service quality and keeps the public register of family doctors and their service areas.',
    },
  },
  'terviseportaal': {
    category: 'health',
    ru: {
      label: 'terviseportaal',
      definition: 'Национальная электронная медкарта на terviseportaal.ee, преемник digilugu.ee: история болезни, цифровые рецепты, направления и оплаченные за вас счета. Здесь же проверяют действие страховки и заказывают европейскую карту; вход по ID-карте, Mobiil-ID или Smart-ID.',
    },
    en: {
      label: 'terviseportaal',
      definition: 'The national patient health record at terviseportaal.ee, successor to digilugu.ee: medical history, digital prescriptions, referrals and the invoices paid on your behalf. It is also where you check insurance validity and order a European Health Insurance Card, using an ID card, Mobiil-ID or Smart-ID.',
    },
  },
  'ravijarjekord': {
    category: 'health',
    ru: {
      label: 'ravijärjekord',
      definition: 'Лист ожидания специализированной помощи, выстроенный по тяжести состояния, а не по дате обращения. Законный максимум составляет шесть недель до амбулаторного приёма специалиста и восемь месяцев до плановой госпитализации, но срок может быть превышен по ряду причин.',
    },
    en: {
      label: 'ravijärjekord',
      definition: 'The waiting list for specialist care, ordered by clinical severity rather than by date of request. The statutory maximum is six weeks for an outpatient specialist appointment and eight months for planned inpatient treatment, though the limit may be exceeded if you insist on a particular doctor or clinic.',
    },
  },
  'digiretsept': {
    category: 'health',
    ru: {
      label: 'digiretsept',
      definition: 'Цифровой рецепт: рецепты существуют только как записи в национальной информационной системе здоровья, поэтому лекарство получают в любой аптеке по документу, удостоверяющему личность. В кассе платят 3,50 евро с каждого льготного рецепта плюс долю сверх скидки Больничной кассы.',
    },
    en: {
      label: 'digiretsept',
      definition: 'The digital prescription: prescriptions exist only as records in the national health information system, so medicine is collected at any pharmacy against an identity document. At the counter you pay a flat €3.50 per reimbursed prescription plus the share above the Tervisekassa discount rate.',
    },
  },
  'ehic-ee': {
    category: 'health',
    ru: {
      label: 'Euroopa ravikindlustuskaart (EHIC)',
      definition: 'Европейская карта медицинского страхования: бесплатна и выдаётся, только если до конца эстонской страховки остаётся не менее трёх месяцев. Покрывает необходимую помощь в государственных учреждениях ЕС на тех же условиях, что и для местных, то есть их визитные платы вы платите сами.',
    },
    en: {
      label: 'Euroopa ravikindlustuskaart (EHIC)',
      definition: 'The European Health Insurance Card, free and issued only while your Estonian cover has at least three months left to run. It pays for medically necessary care in public institutions on the same terms as locals, which means you still pay their visit fees, and it covers neither repatriation nor private doctors.',
    },
  },

  // ── Estonia — education and language ───────────────────────────────
  'eestikeelsele-oppele-uleminek': {
    category: 'education',
    ru: {
      label: 'eestikeelsele õppele üleminek',
      definition: 'Переход на эстоноязычное обучение, реформа, закрывающая параллельную русскоязычную школьную систему. Началась в сентябре 2024 года в детских садах и в 1-м и 4-м классах, идёт примерно по два класса в год и завершится к 2030 году; исключение для класса даёт только правительство.',
    },
    en: {
      label: 'eestikeelsele õppele üleminek',
      definition: 'The transition to Estonian-language education, the reform closing Estonia’s parallel Russian-medium school system. It began in September 2024 in kindergartens and grades 1 and 4, advances by about two grades a year and is due to finish in 2030; only the government can exempt a grade.',
    },
  },
  'keelekumblus': {
    category: 'education',
    ru: {
      label: 'keelekümblus',
      definition: 'Языковое погружение, применяемое в Эстонии с 1998 года для обучения детей, чей родной язык не эстонский: предметное содержание преподаётся по-эстонски, а не язык отдельным курсом. Это главный педагогический инструмент школьной реформы.',
    },
    en: {
      label: 'keelekümblus',
      definition: 'Language immersion, used in Estonia since 1998 to teach children whose home language is not Estonian by delivering subject content in Estonian rather than teaching the language separately. It is the main pedagogical tool of the school transition.',
    },
  },
  'elukohajargne-kool': {
    category: 'education',
    ru: {
      label: 'elukohajärgne kool',
      definition: 'Школа по месту жительства, которую самоуправление назначает ребёнку по адресу в регистре народонаселения. Отказать такому ребёнку она не вправе и зачисляет без вступительных испытаний, поэтому регистрация адреса и есть первый настоящий шаг к зачислению.',
    },
    en: {
      label: 'elukohajärgne kool',
      definition: 'The school of residence, the municipal school the local government assigns to a child based on the address in the population register. It cannot refuse that child and admits without entry tests, which makes registering your address the real first step of enrolment.',
    },
  },
  'lasteaia-kohatasu': {
    category: 'education',
    ru: {
      label: 'lasteaia kohatasu',
      definition: 'Ежемесячная плата за место в детском саду, которую вправе брать самоуправление. По закону она не может превышать 20 % минимальной зарплаты, около 189 евро в месяц в 2026 году, и большинство самоуправлений берёт меньше; питание оплачивается отдельно и в лимит не входит.',
    },
    en: {
      label: 'lasteaia kohatasu',
      definition: 'The monthly kindergarten place fee a municipality may charge. By law it cannot exceed 20% of the minimum wage, roughly €189 a month in 2026, and most municipalities charge well below that; meals are billed separately and fall outside the cap.',
    },
  },
  'koolikohustus': {
    category: 'education',
    ru: {
      label: 'koolikohustus',
      definition: 'Школьная обязанность, с 2025/2026 учебного года заменённая обязанностью учиться до 18 лет вместо 17. В школу идут в тот год, когда ребёнку исполняется семь лет к 1 октября; норма распространяется и на иностранных граждан, живущих в Эстонии.',
    },
    en: {
      label: 'koolikohustus',
      definition: 'Compulsory schooling, replaced from the 2025/2026 academic year by an obligation to learn that runs to 18 rather than 17. Children start school in the year they turn seven by 1 October, and it applies to foreign citizens living in Estonia.',
    },
  },
  'harno': {
    category: 'education',
    ru: {
      label: 'HARNO',
      definition: 'Департамент образования и молодёжи, реализующий образовательную политику Эстонии. Проводит государственные экзамены и экзамены на уровень владения эстонским, содержит эстонский ENIC/NARIC и ведёт информационные системы EHIS, SAIS и EIS.',
    },
    en: {
      label: 'HARNO',
      definition: 'The Education and Youth Board, which implements Estonia’s education policy. It runs the national exams and the Estonian language proficiency exams, hosts the Estonian ENIC/NARIC, and operates the EHIS, SAIS and EIS information systems.',
    },
  },
  'enic-naric-ee': {
    category: 'education',
    ru: {
      label: 'Eesti ENIC/NARIC',
      definition: 'Эстонский центр академического признания в составе HARNO: бесплатно оценивает иностранные квалификации и выдаёт письменное заключение в течение 30 дней. Признание квалификации не даёт права работать по регулируемой профессии, это отдельная процедура.',
    },
    en: {
      label: 'Eesti ENIC/NARIC',
      definition: 'The Estonian academic recognition centre within HARNO, which assesses foreign qualifications free of charge and issues a written statement within 30 days. Recognition of a qualification is not a licence to practise a regulated profession, which is a separate procedure.',
    },
  },
  'ehis': {
    category: 'education',
    ru: {
      label: 'EHIS',
      definition: 'Инфосистема образования Эстонии, государственный регистр школ, учебных программ, учителей и учеников. Здесь родитель проверяет, есть ли у частного сада или школы лицензия именно на продаваемую программу; расходы на обучение вычитаются только для зарегистрированных в EHIS.',
    },
    en: {
      label: 'EHIS',
      definition: 'The Estonian Education Information System, the national register of schools, curricula, teachers and pupils. It is where a parent verifies that a private kindergarten or school holds a licence for the programme it sells, and training expenses are deductible only if the provider is listed there.',
    },
  },
  'gumnaasium': {
    category: 'education',
    ru: {
      label: 'gümnaasium',
      definition: 'Гимназия, трёхлетняя ступень общего образования после девятилетней основной школы (põhikool). Для выпуска нужно не менее 96 курсов, государственные экзамены по эстонскому, математике и иностранному языку, школьный экзамен и исследовательская или практическая работа.',
    },
    en: {
      label: 'gümnaasium',
      definition: 'Upper secondary school, the three-year general stage after the nine-year basic school (põhikool). Graduation requires at least 96 courses, state exams in Estonian, mathematics and a foreign language, a school exam, and a research or practical paper.',
    },
  },
  'koolivalmiduskaart': {
    category: 'education',
    ru: {
      label: 'koolivalmiduskaart',
      definition: 'Карта школьной готовности, которую дошкольное учреждение выдаёт ребёнку, освоившему дошкольную программу, с описанием его развития; родитель передаёт её в школу. Для детей, не посещавших сад, готовность оценивает региональный консультационный центр.',
    },
    en: {
      label: 'koolivalmiduskaart',
      definition: 'The school readiness card a pre-school issues to a child who has completed the pre-school curriculum, describing their development; the parent submits it to the school. For a child who did not attend a pre-school, a regional counselling centre assesses readiness instead.',
    },
  },
  'tasemeeksam': {
    category: 'education',
    ru: {
      label: 'tasemeeksam',
      definition: 'Государственный экзамен по эстонскому языку на уровнях A2, B1, B2 и C1, который проводит HARNO. Уровень B1 требуется и для вида на жительство долгосрочного жителя, и для гражданства; сессии проходят ежеквартально в Таллинне, Тарту, Нарве и Йыхви.',
    },
    en: {
      label: 'tasemeeksam',
      definition: 'The state Estonian language exam, held at A2, B1, B2 and C1 by HARNO. B1 is the level required both for the long-term resident’s permit and for citizenship; sittings are quarterly in Tallinn, Tartu, Narva and Jõhvi.',
    },
  },
  'eesti-keele-maja': {
    category: 'education',
    ru: {
      label: 'Eesti keele maja',
      definition: 'Дом эстонского языка, бесплатный государственный центр языка и культуры при Фонде интеграции: головной офис в Нарве, отделения в Таллинне, Палдиски и Кивиыли. Программа даёт 500 академических часов бесплатного обучения от A1 до B1, плюс консультации.',
    },
    en: {
      label: 'Eesti keele maja',
      definition: 'The state-funded free Estonian language and culture centre run by the Integration Foundation, with its head office in Narva and branches in Tallinn, Paldiski and Kiviõli. It offers 500 academic hours of free tuition from A1 to B1, plus counselling.',
    },
  },
  'cefr-b1': {
    category: 'education',
    ru: {
      label: 'B1 (CEFR)',
      definition: 'Средний уровень общеевропейской шкалы CEFR: человек справляется с большинством бытовых ситуаций, может описать опыт, планы и коротко обосновать мнение. Именно на этой ступени многие страны ставят языковое условие для постоянного проживания или натурализации — какое именно, смотрите в главе о языке нужной страны.',
    },
    en: {
      label: 'B1 (CEFR)',
      definition: 'The intermediate level of the Common European Framework: able to handle most everyday situations, describe experiences and plans, and give short reasons for an opinion. It is the rung at which many countries set their language condition for permanent residence or naturalisation — which one, and on what evidence, is a question for the country’s own language chapter.',
    },
  },

  // ── Estonia — transport ────────────────────────────────────────────
  'transpordiamet': {
    category: 'transit',
    ru: {
      label: 'Transpordiamet',
      definition: 'Транспортный департамент Эстонии: выдаёт водительские удостоверения, обменивает иностранные, регистрирует транспортные средства и устанавливает требования технической исправности. Именно в его бюро обслуживания сдают иностранные права и получают эстонские.',
    },
    en: {
      label: 'Transpordiamet',
      definition: 'The Estonian Transport Administration, which issues driving licences, exchanges foreign ones, registers vehicles and sets roadworthiness rules. Its service bureaus are where a newcomer surrenders a foreign licence and collects an Estonian one.',
    },
  },
  'tehnoulevaatus': {
    category: 'transit',
    ru: {
      label: 'tehnoülevaatus',
      definition: 'Периодический технический осмотр. Легковой автомобиль впервые проходит его через 48 месяцев после первой регистрации, далее раз в 24 месяца, а после десяти лет ежегодно; при успешном прохождении месяц следующего осмотра вписывают в свидетельство о регистрации.',
    },
    en: {
      label: 'tehnoülevaatus',
      definition: 'The periodic roadworthiness test. A passenger car is first tested 48 months after its first registration, then every 24 months, and annually once it passes ten years of age; passing writes the next due month onto the registration certificate.',
    },
  },
  'liikluskindlustus': {
    category: 'transit',
    ru: {
      label: 'liikluskindlustus',
      definition: 'Обязательное страхование гражданской ответственности владельца транспортного средства: каждое ТС в регистре обязано иметь его постоянно. Незастрахованный зарегистрированный автомобиль покрывается автоматически через фонд LKF, но при ущербе владелец платит франшизу 640 евро.',
    },
    en: {
      label: 'liikluskindlustus',
      definition: 'Compulsory motor third-party liability insurance, which every vehicle in the motor register must carry at all times. An uninsured registered vehicle is covered automatically through the Motor Insurance Fund, but its owner owes a €640 excess if it causes damage.',
    },
  },
  'lkf': {
    category: 'transit',
    ru: {
      label: 'LKF',
      definition: 'Эстонский фонд страхования гражданской ответственности: ведёт национальный регистр автострахования, выступает гарантийным органом при ДТП с незастрахованными или неустановленными машинами, выдаёт «зелёные карты» и разбирает досудебные споры водителей со страховщиками.',
    },
    en: {
      label: 'LKF',
      definition: 'The Estonian Motor Insurance Fund, which keeps the national motor insurance register, acts as the guarantee body for accidents involving uninsured or unidentified vehicles, issues Green Cards and runs out-of-court disputes between drivers and insurers.',
    },
  },
  'uhiskaart': {
    category: 'transit',
    ru: {
      label: 'ühiskaart',
      definition: 'Бесконтактная транспортная карта, используемая в городах Эстонии. Персонализация по эстонскому личному коду превращает её в подтверждение права на льготу: для зарегистрированного жителя Таллинна поездка становится бесплатной.',
    },
    en: {
      label: 'ühiskaart',
      definition: 'The contactless public transport card used across Estonian cities. Personalising it against an Estonian personal identification code turns it into proof of entitlement: for a registered Tallinn resident the ride becomes free.',
    },
  },
  'elron': {
    category: 'transit',
    ru: {
      label: 'Elron',
      definition: 'Пассажирский железнодорожный оператор Эстонии, юридически AS Eesti Liinirongid. Обслуживает внутреннюю сеть с центром в Таллинне, главные направления на Тарту и Нарву, и начал продлевать маршруты на юг, в Латвию и Литву.',
    },
    en: {
      label: 'Elron',
      definition: 'Estonia’s passenger rail operator, legally AS Eesti Liinirongid. It runs the domestic network out of Tallinn, the main corridors being those to Tartu and Narva, and has begun extending services south into Latvia and Lithuania.',
    },
  },
  'rail-baltica': {
    category: 'transit',
    ru: {
      label: 'Rail Baltica',
      definition: 'Строящаяся электрифицированная железная дорога европейской колеи, которая свяжет Таллинн через Пярну, Ригу и Каунас с польской границей и общеевропейской сетью, заменив советскую ориентацию региона на колею 1520 мм. Заявленный срок открытия 2030 год.',
    },
    en: {
      label: 'Rail Baltica',
      definition: 'The standard-gauge, fully electrified railway being built to link Tallinn through Pärnu, Riga and Kaunas to the Polish border and the wider European network, replacing the region’s Soviet-era 1520 mm gauge orientation. The stated opening target is 2030.',
    },
  },
  'talverehvid': {
    category: 'transit',
    ru: {
      label: 'talverehvid',
      definition: 'Зимние шины: обязательны в Эстонии с 1 декабря, ставить их разрешено с 15 октября, остаточная глубина протектора должна превышать 3 мм. Нешипованные обязаны иметь маркировку «три пика и снежинка», шипованные разрешены до 31 марта.',
    },
    en: {
      label: 'talverehvid',
      definition: 'Winter tyres, mandatory in Estonia from 1 December and permitted from 15 October, with more than 3 mm of tread. Studless winter tyres must carry the three-peak mountain and snowflake symbol; studded ones are allowed until 31 March.',
    },
  },

  // ── Estonia — utilities and regions ────────────────────────────────
  'kaugkute': {
    category: 'misc',
    ru: {
      label: 'kaugküte',
      definition: 'Централизованное теплоснабжение, основной способ отопления эстонских квартир. Предельная цена не формируется рынком, а утверждается Департаментом конкуренции отдельно примерно для 170 сетевых районов, поэтому зимний счёт зависит от того, к какой сети вы подключены.',
    },
    en: {
      label: 'kaugküte',
      definition: 'District heating, the dominant way Estonian flats are heated. Its maximum price is not set by the market but approved by the Competition Authority for each of about 170 network areas, so the winter bill depends on which town’s network you live on.',
    },
  },
  'elering': {
    category: 'misc',
    ru: {
      label: 'Elering',
      definition: 'Оператор системы передачи электроэнергии и газа Эстонии. Провёл отсоединение балтийской энергосистемы от российской частотной зоны 8 февраля 2025 года и подключение к синхронной зоне континентальной Европы на следующий день.',
    },
    en: {
      label: 'Elering',
      definition: 'Estonia’s electricity and gas transmission system operator. It ran the desynchronisation of the Baltic grid from the Russian frequency area on 8 February 2025 and its connection to the Continental Europe Synchronous Area the following day.',
    },
  },
  'ida-virumaa': {
    category: 'misc',
    ru: {
      label: 'Ida-Virumaa',
      definition: 'Северо-восточный уезд Эстонии на границе с Россией: Нарва, Кохтла-Ярве, Силламяэ. Этнические эстонцы составляют лишь 20,9 % населения, а уровень насильственной преступности (104 на 10 000 жителей) примерно вдвое выше среднего по стране.',
    },
    en: {
      label: 'Ida-Virumaa',
      definition: 'Estonia’s north-eastern county on the Russian border, containing Narva, Kohtla-Järve and Sillamäe. Only 20.9% of its residents are ethnic Estonians, and its violent-crime rate of 104 per 10,000 is roughly twice the national figure.',
    },
  },
  // ── Greece — state bodies and civil registry ───────────────────────
  'aade': {
    category: 'admin',
    ru: {
      label: 'ΑΑΔΕ (AADE)',
      definition: 'Независимое управление государственных доходов — налоговая администрация Греции. Выдаёт ΑΦΜ, ведёт реестр объектов краткосрочной аренды, публикует таблицы объективной стоимости недвижимости, а с 2026 года устанавливает решением своего главы сроки, которые раньше стояли в самом законе, — например, срок подачи заявления на режимы ст. 5Α и 5Β.',
    },
    en: {
      label: 'ΑΑΔΕ / AADE',
      definition: 'The Independent Authority for Public Revenue, Greece’s tax administration. It issues the ΑΦΜ, runs the short-term-rental property registry, publishes the objective-value tables that floor property tax bases, and since 2026 fixes by its Governor’s decision deadlines that used to sit in statute — the article 5Α and 5Β application dates among them.',
    },
  },
  'elstat': {
    category: 'admin',
    ru: {
      label: 'ΕΛΣΤΑΤ (ELSTAT)',
      definition: 'Статистическая служба Греции. Ведёт месячный индекс потребительских цен, квартальное обследование рабочей силы, примерно раз в четыре года — обследование структуры заработков, а также перепись и ряды по зарегистрированным преступлениям. Практически любая греческая цифра в евро или процентах, у которой есть первоисточник, восходит сюда.',
    },
    en: {
      label: 'ΕΛΣΤΑΤ / ELSTAT',
      definition: 'The Hellenic Statistical Authority. It publishes the monthly consumer price index, the quarterly Labour Force Survey, the roughly four-yearly Structure of Earnings Survey, the census, and the recorded-offence series — the origin of most Greek figures that have a first-party source at all.',
    },
  },
  'fek': {
    category: 'admin',
    ru: {
      label: 'ΦΕΚ (Εφημερίδα της Κυβερνήσεως)',
      definition: 'Правительственный вестник Греции. Ничто не вступает в силу, пока не опубликовано в нём, и каждый закон или министерское решение цитируется по серии, номеру выпуска и дате — например ΦΕΚ Α΄ 17/06.02.2026. Греческое правило без ссылки на ΦΕΚ — это правило, происхождение которого вам не показали.',
    },
    en: {
      label: 'ΦΕΚ (Government Gazette)',
      definition: 'The Greek Government Gazette. Nothing takes effect until it appears there, and every law and ministerial decision is cited by its series, issue and date — ΦΕΚ Α΄ 17/06.02.2026, for instance. A Greek rule quoted with no ΦΕΚ reference is a rule whose origin you have not been shown.',
    },
  },
  'kya': {
    category: 'admin',
    ru: {
      label: 'ΚΥΑ (κοινή υπουργική απόφαση)',
      definition: 'Совместное решение двух или более министров — подзаконный акт, публикуемый в ΦΕΚ. Греческое миграционное право устроено так, что закон называет условие, а конкретную сумму передаёт в ΚΥΑ: пороги дохода живут в решении, а не в законе, и меняются без нового акта парламента.',
    },
    en: {
      label: 'ΚΥΑ (joint ministerial decision)',
      definition: 'Secondary legislation signed by two or more ministers and published in the Gazette. Greek immigration law leans on it structurally: the statute names a condition and delegates the number to a ΚΥΑ, so income thresholds live in the decision rather than the act and can move without parliament.',
    },
  },
  'govgr-wallet': {
    category: 'admin',
    ru: {
      label: 'Gov.gr Wallet',
      definition: 'Государственное приложение-кошелёк, выпускающее цифровые версии удостоверения личности, водительских прав, студенческого билета и ряда других документов; внутри Греции они юридически равны бумажным. Для активации нужны учётные данные TaxisNet и номер телефона, подтверждённый в национальном реестре, — цепочка, которой у только что приехавшего ещё нет. Цифровые ID и права не годятся как проездной документ за границу.',
    },
    en: {
      label: 'Gov.gr Wallet',
      definition: 'The state digital-wallet app, issuing digital versions of the ID card, driving licence, academic ID and other documents that are legally equivalent to their paper originals inside Greece. Activation needs TaxisNet tax credentials and a mobile number confirmed in the national communication registry — a prerequisite chain a new arrival has not yet built. Neither the digital ID nor the digital licence is valid as a travel document.',
    },
  },
  'e-paravolo': {
    category: 'admin',
    ru: {
      label: 'e-παράβολο',
      definition: 'Электронная государственная пошлина: оплачивается онлайн, а её код указывается в заявлении. Для видов на жительство παράβολο не возвращается и берётся и при выдаче, и при продлении; продление статуса долгосрочного резидента от него освобождено. Не всякий государственный сбор является παράβολο — ΔΟΑΤΑΠ, например, принимает свой отдельно.',
    },
    en: {
      label: 'e-παράβολο',
      definition: 'The electronic state fee receipt: bought online, then quoted by its code on the application it pays for. For residence permits the παράβολο is non-refundable and falls due on grant and again on renewal, with renewals of long-term-resident status exempt. Not every state charge is one — ΔΟΑΤΑΠ, for example, collects its fee outside the system.',
    },
  },
  'emy-hnms': {
    category: 'misc',
    ru: {
      label: 'ΕΜΥ / HNMS',
      definition: 'Национальная метеорологическая служба Греции, официальный источник по погоде и климату. Публикует нормали по отдельным станциям за стандартные периоды, включая 1961-1990 и 1991-2020, — то есть цифру всегда можно привязать к конкретной станции и конкретному базовому периоду, а не к «среднему по стране».',
    },
    en: {
      label: 'ΕΜΥ / HNMS',
      definition: 'The Hellenic National Meteorological Service, Greece’s official weather and climate authority. It publishes normal values station by station for standard baselines including 1961-1990 and 1991-2020, which is what makes a Greek climate figure attributable to a named station and period rather than to a national average.',
    },
  },

  // ── Greece — tax ───────────────────────────────────────────────────
  'afm': {
    category: 'tax',
    ru: {
      label: 'ΑΦΜ (АФМ)',
      definition: 'Налоговый номер Греции, выдаётся ΑΑΔΕ. Получить его можно до переезда — онлайн или через греческого налогового представителя. Формально банк может открыть счёт и по иностранному TIN, но на практике ΑΦΜ спрашивают всегда; на нём же завязана «профессиональная» регистрация в системе мгновенных платежей IRIS.',
    },
    en: {
      label: 'ΑΦΜ / AFM',
      definition: 'The Greek tax registration number, issued by ΑΑΔΕ and obtainable before arrival, online or through a Greek tax representative. The letter of the rules allows a bank to work from a foreign TIN; in practice Greek banks ask for the ΑΦΜ, and professional registration in the IRIS instant-payment scheme is keyed to it.',
    },
  },
  'arthro-5a': {
    category: 'tax',
    ru: {
      label: 'άρθρο 5Α (non-dom)',
      definition: 'Режим фиксированного налога для состоятельных людей, переносящих налоговое резидентство в Грецию: единый платёж €100 000 в год закрывает греческий налог на весь зарубежный доход, каким бы он ни был, на срок до пятнадцати налоговых лет, плюс €20 000 за каждого включённого родственника. Требует отсутствия греческого резидентства в семи годах из восьми и инвестиции от €500 000, от которой держатели инвесторского ВНЖ освобождены.',
    },
    en: {
      label: 'article 5Α (non-dom)',
      definition: 'Greece’s lump-sum regime for high-net-worth arrivals: a single €100,000 a year discharges Greek tax on the whole of a person’s foreign-source income for up to fifteen tax years, with €20,000 more per relative extended into it. Entry requires no Greek tax residence in seven of the previous eight years and a €500,000 investment, waived for holders of the investor residence permit.',
    },
  },
  'arthro-5b': {
    category: 'tax',
    ru: {
      label: 'άρθρο 5Β (пенсионный режим)',
      definition: 'Плоские 7 % для получателя иностранной пенсии, перенёсшего налоговое резидентство в Грецию. Ставка применяется не только к пенсии, а ко всему зарубежному доходу и полностью закрывает греческое обязательство по нему. Два условия сразу: не быть греческим резидентом в пяти годах из шести и приехать из государства, с которым у Греции действует соглашение об административном сотрудничестве по налогам. Срок — пятнадцать налоговых лет.',
    },
    en: {
      label: 'article 5Β (pensioner regime)',
      definition: 'A flat 7% for someone drawing a foreign pension who moves their tax residence to Greece. The rate covers not merely the pension but the whole of their foreign-source income, and payment settles the Greek liability on it. Both conditions bind: no Greek tax residence in five of the previous six years, and arrival from a state with a tax administrative-cooperation agreement in force with Greece. It runs fifteen tax years.',
    },
  },
  'arthro-5c': {
    category: 'tax',
    ru: {
      label: 'άρθρο 5Γ (льгота при переезде)',
      definition: 'Освобождение половины греческого трудового дохода — или дохода индивидуального предпринимателя — специалиста, перенёсшего налоговое резидентство в Грецию. Семь налоговых лет без продления. Условия: не быть греческим резидентом в пяти годах из шести, приехать из ЕС, ЕЭЗ или страны с соглашением об административном сотрудничестве, работать на греческое юрлицо или греческое постоянное представительство иностранной компании и заявить намерение остаться минимум на два года. Это не режим для удалёнщика на иностранного работодателя.',
    },
    en: {
      label: 'article 5Γ (relocation relief)',
      definition: 'A relief exempting half the Greek employment or sole-trader income of a professional who moves their tax residence to Greece, for seven tax years with no extension. It requires no Greek tax residence in five of the previous six years, arrival from the EU, the EEA or a state with an administrative-cooperation agreement, and — the condition that rules out remote work for a foreign employer — a Greek employer or the Greek permanent establishment of a foreign one.',
    },
  },
  'kentro-zotikon-symferonton': {
    category: 'tax',
    ru: {
      label: 'κέντρο ζωτικών συμφερόντων',
      definition: 'Центр жизненных интересов — тест, который делает человека налоговым резидентом Греции без всяких 183 дней. Статья 4 Кодекса о подоходном налоге признаёт резидентом того, у кого в Греции постоянное или основное жильё, обычное место пребывания либо центр личных и экономических связей; счёт дней в следующем пункте работает «без ущерба» для этого теста, то есть как дополнительный путь, а не единственный. Спор двух резидентств разрешает налоговое соглашение.',
    },
    en: {
      label: 'κέντρο ζωτικών συμφερόντων',
      definition: 'Centre of vital interests — the test that can make someone a Greek tax resident without 183 days in the country. Article 4 of the Income Tax Code catches a person whose permanent or main home, habitual abode, or centre of personal and economic ties is in Greece; the day count in the next paragraph operates expressly without prejudice to it, as an additional route rather than the only one. A treaty breaks the tie where two states both claim you.',
    },
  },
  'kodikas-forologias-periousias': {
    category: 'tax',
    ru: {
      label: 'Κώδικας Φορολογίας Περιουσίας (ν. 5219/2025)',
      definition: 'Кодекс налогообложения имущества, действующий с 18 июля 2025 года: свёл в одну рамку ΕΝΦΙΑ, налог на переход права собственности и налоги на наследство, дарение и родительское предоставление. Заменил ν. 4223/2013 как основу ΕΝΦΙΑ и ν. 1587/1950 как основу налога на переход, так что путеводитель, всё ещё ссылающийся на эти законы, описывает положение до середины 2025 года.',
    },
    en: {
      label: 'Κώδικας Φορολογίας Περιουσίας (n. 5219/2025)',
      definition: 'The Property Tax Code, in force since 18 July 2025, which brought ΕΝΦΙΑ, real-estate transfer tax and inheritance, gift and parental-grant tax into a single framework. It displaced n. 4223/2013 as the basis of ΕΝΦΙΑ and n. 1587/1950 as the basis of transfer tax, so any guide still citing those is describing the pre-mid-2025 position.',
    },
  },

  // ── Greece — immigration and status ────────────────────────────────
  'kodikas-metanastefsis': {
    category: 'admin',
    ru: {
      label: 'Κώδικας Μετανάστευσης (ν. 5038/2023)',
      definition: 'Миграционный кодекс, действующий с 1 января 2024 года. Свёл греческие виды на жительство в буквенные семейства: B — инвестиции, E — работа, H — учёба, I — прочие основания, M — долгосрочное проживание, Z — национальные визовые титулы. Существенно переписан законом ν. 5275/2026.',
    },
    en: {
      label: 'Κώδικας Μετανάστευσης (ν. 5038/2023)',
      definition: 'The Immigration Code, in force since 1 January 2024, which consolidated Greek residence permits into lettered families — B for investment, E for work, H for study, I for other grounds, M for long-term residence, Z for national-visa titles. Substantially rewritten by ν. 5275/2026.',
    },
  },
  'nomos-5275-2026': {
    category: 'admin',
    ru: {
      label: 'ν. 5275/2026',
      definition: 'Закон, переписавший Миграционный кодекс с 6 февраля 2026 года (ΦΕΚ Α΄ 17/06.02.2026): перенёс в греческое право директиву ЕС 2024/1233, продлил Голубую карту ЕС с двух лет до трёх, добавил Tech-визу «Ζ.13Α» и Talent-визу «Ζ.15» и заново сформулировал языковое условие статьи 160. Обзоры, написанные по кодексу образца 2023 года, устарели именно здесь.',
    },
    en: {
      label: 'ν. 5275/2026',
      definition: 'The act that rewrote the Immigration Code with effect from 6 February 2026 (ΦΕΚ Α΄ 17/06.02.2026): it transposed Directive (EU) 2024/1233, took the EU Blue Card from two years to three, added the Tech visa «Ζ.13Α» and the Talent visa «Ζ.15», and restated the language condition in article 160. Guides written against the 2023 code are out of date at exactly these points.',
    },
  },
  'golden-visa-gr': {
    category: 'admin',
    ru: {
      label: 'Golden Visa (μόνιμη άδεια διαμονής επενδυτή)',
      definition: 'Постоянный вид на жительство инвестора по статье 100 Миграционного кодекса, выдаваемый гражданину третьей страны за покупку подпадающей под условия греческой недвижимости. Действует пять лет и продлевается пятилетиями, требований к сроку пребывания нет — и, что упускают чаще всего, он не даёт права работать в Греции ни в какой форме.',
    },
    en: {
      label: 'Golden Visa (μόνιμη άδεια διαμονής επενδυτή)',
      definition: 'The permanent investor residence permit under article 100 of the Immigration Code, granted to a third-country national who buys qualifying Greek property. It runs five years and renews in five-year blocks, carries no minimum-stay requirement, and — the point most often missed — confers no right to work in Greece in any form.',
    },
  },
  'psifiakoi-nomades': {
    category: 'admin',
    ru: {
      label: 'ψηφιακοί νομάδες (τίτλος «Ζ.1»)',
      definition: 'Маршрут для цифровых кочевников, статья 68 Миграционного кодекса: национальная виза до двенадцати месяцев для того, кто работает удалённо на работодателей или заказчиков вне Греции, затем двухлетний вид на жительство «Ι.8». Заявитель подписывает декларацию о том, что не будет работать на греческого работодателя, а сопровождающий супруг работать в Греции не вправе вовсе.',
    },
    en: {
      label: 'ψηφιακοί νομάδες (title «Ζ.1»)',
      definition: 'The digital-nomad route, article 68 of the Immigration Code: a national visa of up to twelve months for someone working remotely for employers or clients outside Greece, followed by a two-year «Ι.8» residence permit. The applicant signs a declaration never to work for a Greece-based employer, and an accompanying spouse may not work in Greece at all.',
    },
  },
  'epi-makron-diamenon': {
    category: 'admin',
    ru: {
      label: 'επί μακρόν διαμένων (άδεια «Μ.1»)',
      definition: 'Статус долгосрочного резидента ЕС — основной греческий путь к постоянному проживанию: пять лет непрерывного законного пребывания, доход не ниже годовой минимальной зарплаты с надбавкой 10 % на иждивенцев вместе, полная медицинская страховка и греческий на уровне B1 либо, как альтернатива, двенадцать непрерывных лет в стране. После выдачи статус бессрочен, а продление разрешения бесплатно.',
    },
    en: {
      label: 'επί μακρόν διαμένων (permit «Μ.1»)',
      definition: 'EU long-term resident status, Greece’s main permanent-residence route: five years of continuous lawful residence, income of at least the annual minimum wage plus 10% for dependants together, full health insurance, and Greek at B1 — or, as an alternative to the language bar, twelve consecutive years in the country. Once granted the status is permanent and renewals of the permit are free.',
    },
  },
  'eparkeis-poroi': {
    category: 'admin',
    ru: {
      label: 'επαρκείς πόροι',
      definition: 'Достаточные средства — доход или капитал, который заявитель должен показать, чтобы не лечь бременем на греческую систему социальной поддержки. Суммы для каждой категории разрешения заданы одним совместным министерским решением, ΚΥΑ 225679/2024, и считаются нетто, а не брутто.',
    },
    en: {
      label: 'επαρκείς πόροι',
      definition: 'Sufficient means — the income or capital an applicant must evidence to be admitted without burdening the Greek welfare system. The amounts are fixed for every permit category by a single joint ministerial decision, ΚΥΑ 225679/2024, and are net figures, not gross.',
    },
  },
  'pegp': {
    category: 'admin',
    ru: {
      label: 'Π.Ε.Γ.Π.',
      definition: 'Πιστοποιητικό Επάρκειας Γνώσεων για Πολιτογράφηση — письменный экзамен, введённый законом ν. 4735/2020: греческий на уровне B1 плюс разделы по географии, истории, культуре и государственному устройству. Проводится дважды в год Генеральным секретариатом по гражданству МВД совместно с Министерством образования. Освобождены те, кто отучился установленное число лет в греческой школе или получил греческий вузовский диплом. Это не сертификат ελληνομάθεια.',
    },
    en: {
      label: 'Π.Ε.Γ.Π.',
      definition: 'Πιστοποιητικό Επάρκειας Γνώσεων για Πολιτογράφηση, the naturalisation knowledge certificate introduced by ν. 4735/2020: a written exam covering Greek at B1 plus geography, history, culture and constitutional institutions, held twice a year by the Interior Ministry’s General Secretariat for Citizenship with the Ministry of Education. Greek schooling for a set number of years, or a Greek university degree, exempts you. It is not the ελληνομάθεια certificate.',
    },
  },
  'omogeneis': {
    category: 'admin',
    ru: {
      label: 'ομογενείς',
      definition: 'Иностранные граждане греческого происхождения. Греческая статистика считает их отдельно от граждан третьих стран, и по ряду процедур для них действуют другие правила: держатель Ειδικό Δελτίο Ταυτότητας Ομογενούς — специального удостоверения ομογενής — полностью освобождён от языкового условия для долгосрочного проживания, а требования к сроку проживания при натурализации к ομογενείς не применяются.',
    },
    en: {
      label: 'ομογενείς',
      definition: 'Foreign nationals of Greek descent. Greek statistics count them apart from third-country nationals, and several procedures treat them differently: a holder of the Ειδικό Δελτίο Ταυτότητας Ομογενούς, the special identity card for ομογενείς, is outright exempt from the language condition for long-term residence, and the residence-period requirement for naturalisation does not apply to them.',
    },
  },
  'elevate-greece': {
    category: 'admin',
    ru: {
      label: 'Elevate Greece (Εθνικό Μητρώο Νεοφυών Επιχειρήσεων)',
      definition: 'Национальный реестр стартапов — под этим брендом работает организация, названная в самом законе. От записи в реестре зависят три разные вещи: инвесторский вид на жительство категории «B.6» за вклад в капитал стартапа, Tech-виза «Ζ.13Α» для нанятых такой компанией, и вычет из налогооблагаемого дохода для бизнес-ангела.',
    },
    en: {
      label: 'Elevate Greece (Εθνικό Μητρώο Νεοφυών Επιχειρήσεων)',
      definition: 'The National Startup Registry, the body the statutes name and the brand everyone uses. Three separate things hang off being listed in it: the «B.6» investor residence permit for a capital contribution to a startup, the «Ζ.13Α» Tech visa for someone such a company employs, and the angel investor’s deduction from taxable income.',
    },
  },

  // ── Greece — healthcare ────────────────────────────────────────────
  'amka': {
    category: 'health',
    ru: {
      label: 'ΑΜΚΑ (AMKA)',
      definition: 'Номер социального страхования Греции: по нему человека опознают e-ΕΦΚΑ, ΕΟΠΥΥ и государственные медучреждения при проверке права на помощь. Граждане третьих стран получают его только при наличии вида на жительство, гражданам ЕС разрешение не требуется; для приезжающих по форме S1 существует отдельный EU-AMKA.',
    },
    en: {
      label: 'ΑΜΚΑ / AMKA',
      definition: 'The social security registration number that identifies a person to e-ΕΦΚΑ, ΕΟΠΥΥ and public providers when entitlement is checked. Third-country nationals can only obtain it once they hold a residence permit, whereas EU citizens need none; the S1 route has its own equivalent, the EU-AMKA.',
    },
  },
  'e-efka': {
    category: 'health',
    ru: {
      label: 'e-ΕΦΚΑ',
      definition: 'Единый фонд социального страхования, с 2017 года собирающий все социальные взносы — пенсионные, медицинские, по безработице и единовременные — с работодателей, наёмных работников и самозанятых, и передающий медицинскую долю в ΕΟΠΥΥ. Медицинское покрытие в Греции возникает именно из регистрации в e-ΕΦΚΑ через работу или самозанятость, а не из регистрации по месту жительства.',
    },
    en: {
      label: 'e-ΕΦΚΑ',
      definition: 'The unified social insurance fund, which since 2017 has collected every social contribution — pension, health, unemployment, lump-sum — from employers, employees and the self-employed, and passes the health share to ΕΟΠΥΥ. Registering with e-ΕΦΚΑ through work or self-employment, not registering as a resident, is what actually creates Greek health cover.',
    },
  },
  'eopyy': {
    category: 'health',
    ru: {
      label: 'ΕΟΠΥΥ (EOPYY)',
      definition: 'Национальная организация предоставления медицинских услуг, созданная в 2011 году как единственный государственный закупщик медицины в Греции. Заключает договоры и платит и государственным, и частным поставщикам, определяет корзину услуг и финансирует воздушную и морскую эвакуацию ΕΚΑΒ. Подчиняется Министерству здравоохранения.',
    },
    en: {
      label: 'ΕΟΠΥΥ / EOPYY',
      definition: 'The National Organisation for the Provision of Health Services, set up in 2011 as Greece’s single statutory purchaser of healthcare. It contracts and pays both public and private providers, sets the benefits package, and funds ΕΚΑΒ’s air and sea transfers. It answers to the Ministry of Health.',
    },
  },
  'esy': {
    category: 'health',
    ru: {
      label: 'ΕΣΥ (ESY)',
      definition: 'Национальная система здравоохранения Греции — сеть государственных больниц, центров здоровья и сельских амбулаторий, финансируемая из общих налогов и социальных взносов. ΕΣΥ — это сторона, которая лечит; ΕΟΠΥΥ — сторона, которая за это платит, и путать их не стоит.',
    },
    en: {
      label: 'ΕΣΥ / ESY',
      definition: 'The Greek National Health System — the network of public hospitals, health centres and rural surgeries, funded from general taxation and social contributions. ΕΣΥ is the side that provides the care; ΕΟΠΥΥ is the side that buys it, and the two are worth keeping apart.',
    },
  },
  'prosopikos-iatros': {
    category: 'health',
    ru: {
      label: 'προσωπικός ιατρός',
      definition: 'Личный врач — обязательное первичное звено, введённое законом ν. 4931/2022 и переработанное законом ν. 5157/2024. Взрослый обязан прикрепиться: к врачу государственного учреждения, к частному терапевту по договору с ΕΟΠΥΥ или, с 2024 года, к любому частному терапевту за свой счёт. Штрафа за отсутствие прикрепления нет, но с 1 июня 2025 года не выбравшему врача его назначает система по адресу регистрации. Для детей до 16 лет есть отдельная добровольная схема с педиатром.',
    },
    en: {
      label: 'προσωπικός ιατρός',
      definition: 'The personal doctor, the primary-care gateway introduced by ν. 4931/2022 and reworked by ν. 5157/2024. Every adult is meant to register with one — a doctor at a public facility, an ΕΟΠΥΥ-contracted private GP or internist, or since 2024 any private GP paid out of pocket. There is no fine for not choosing, but since 1 June 2025 anyone who has not is assigned one automatically by registered address. Children under 16 have a separate, optional paediatrician scheme.',
    },
  },
  'asfalistiki-ikanotita': {
    category: 'health',
    ru: {
      label: 'ασφαλιστική ικανότητα',
      definition: 'Страховая способность — активный, проверяемый статус, подтверждающий, что у человека или у его иждивенца прямо сейчас есть действующее греческое медицинское покрытие через e-ΕΦΚΑ. Именно его аптека, поликлиника или больница проверяет по ΑΜΚΑ перед приёмом по тарифам ΕΟΠΥΥ; без него человек считается незастрахованным, даже если проживает в стране совершенно законно.',
    },
    en: {
      label: 'ασφαλιστική ικανότητα',
      definition: 'Insurance capacity — the live, checkable status confirming that a person, or their registered dependant, currently holds valid Greek health cover through e-ΕΦΚΑ. A pharmacy, clinic or hospital checks it against the ΑΜΚΑ before treating anyone on ΕΟΠΥΥ terms; without it you count as uninsured for statutory purposes however lawful your residence.',
    },
  },
  'ekav': {
    category: 'health',
    ru: {
      label: 'ΕΚΑΒ (EKAV)',
      definition: 'Национальный центр экстренной помощи — греческая служба скорой и медицинской эвакуации, вызывается через 112 или по отдельной линии 166. Наземная перевозка бесплатна, а воздушная и морская эвакуация при угрозе жизни — включая вывоз с любого из обитаемых островов на материк — финансируется субсидией ΕΟΠΥΥ и для пациента тоже бесплатна.',
    },
    en: {
      label: 'ΕΚΑΒ / EKAV',
      definition: 'The National Centre for Emergency Care, Greece’s ambulance and medevac service, reached through 112 or its own line, 166. Ground transport is free, and so are the air and sea transfers it runs for life-threatening cases — including off any inhabited island to a mainland hospital — which are financed by an ΕΟΠΥΥ subsidy.',
    },
  },

  // ── Greece — property ──────────────────────────────────────────────
  'enfia': {
    category: 'property',
    ru: {
      label: 'ΕΝΦΙΑ (ENFIA)',
      definition: 'Единый налог на владение недвижимостью, взимаемый с 2014 года, а с июля 2025 года — на основании Кодекса налогообложения имущества. Отдельной декларации не подаётся: налог начисляется электронно исходя из состава собственности по декларации E9 на 1 января налогового года и складывается из основного налога по каждому объекту и надбавки, если совокупная стоимость портфеля превышает установленный порог.',
    },
    en: {
      label: 'ΕΝΦΙΑ / ENFIA',
      definition: 'The unified property ownership tax, levied since 2014 and, since July 2025, under the Property Tax Code. No separate return is filed: it is assessed electronically from the holdings shown in the E9 declaration on 1 January of the tax year, as a main tax per property plus a surcharge where the owner’s aggregate property value passes a set threshold.',
    },
  },
  'fma': {
    category: 'property',
    ru: {
      label: 'ΦΜΑ (φόρος μεταβίβασης ακινήτων)',
      definition: 'Налог на переход права собственности на недвижимость, который платит покупатель до подписания договора у нотариуса. База — не цена сделки как таковая, а большее из двух: договорная цена или объективная стоимость объекта. Применяется к сделкам, не облагаемым НДС, то есть практически ко всему вторичному рынку.',
    },
    en: {
      label: 'ΦΜΑ (real-estate transfer tax)',
      definition: 'The transfer tax the buyer pays before the deed is signed at the notary. Its base is not the sale price as such but whichever is higher, the contract price or the property’s objective value. It applies to purchases outside the VAT net — in practice, essentially the whole resale market.',
    },
  },
  'antikeimeniki-axia': {
    category: 'property',
    ru: {
      label: 'αντικειμενική αξία',
      definition: 'Объективная стоимость — заранее установленная государством оценка объекта по местоположению, площади, возрасту и другим фиксированным признакам. Служит нижней границей базы налога на переход: налог берётся с большего из двух значений, поэтому занизить цену в договоре бессмысленно. Это не то же самое, что αξία κτήσης — стоимость приобретения, по которой считается порог инвесторского ВНЖ.',
    },
    en: {
      label: 'αντικειμενική αξία (objective value)',
      definition: 'The state’s pre-set assessed value for a property, derived from location, size, age and other fixed attributes. It acts as the floor under the transfer-tax base — the tax is charged on whichever is higher — so under-declaring the sale price buys nothing. It is a different measure from αξία κτήσης, the acquisition value that the investor-permit threshold is tested against.',
    },
  },
  'ktimatologio': {
    category: 'property',
    ru: {
      label: 'Κτηματολόγιο (Национальный кадастр)',
      definition: 'Национальный кадастр Греции — реестр, построенный вокруг участка, который постепенно вытесняет старые ипотечные бюро (Υποθηκοφυλακεία), выстроенные вокруг имени владельца. Переход идёт по стране неравномерно, и от того, какая из двух систем обслуживает район, зависит и порядок регистрации сделки, и сбор за неё.',
    },
    en: {
      label: 'Κτηματολόγιο (National Cadastre)',
      definition: 'Greece’s National Cadastre, a parcel-based title register that is gradually replacing the older name-based Υποθηκοφυλακεία land registries. The changeover has reached different areas at different times, and which of the two serves a district governs both how a transfer is registered and what the registration fee is.',
    },
  },
  'ama-str': {
    category: 'property',
    ru: {
      label: 'ΑΜΑ (αριθμός Μητρώου Ακινήτων)',
      definition: 'Регистрационный номер объекта краткосрочной аренды, выдаваемый через реестр ΑΑΔΕ. Его обязаны показывать в каждом объявлении на любой платформе. В зонах, объявленных насыщенными, новые номера не выдаются, а существующий при смене собственника не передаётся, а аннулируется — что превращает наличие ΑΜΑ в вопрос due diligence при покупке.',
    },
    en: {
      label: 'ΑΜΑ (short-term rental registry number)',
      definition: 'The registration number a short-let property is issued through ΑΑΔΕ’s registry and must display on every listing on every platform. In zones designated saturated no new numbers are issued, and an existing one is deleted rather than transferred when the property changes hands — which makes it a due-diligence question at purchase.',
    },
  },

  // ── Greece — banking ───────────────────────────────────────────────
  'iris-payments': {
    category: 'misc',
    ru: {
      label: 'IRIS',
      definition: 'Греческая система мгновенных платежей, которой управляет межбанковская компания ΔΙΑΣ; доступна в приложении любого банка-участника. Деньги идут между счетами разных греческих банков круглосуточно и в реальном времени, по номеру мобильного телефона или по ΑΦΜ вместо IBAN. Переводы между физлицами бесплатны, приём платежей в качестве бизнеса — нет.',
    },
    en: {
      label: 'IRIS',
      definition: 'Greece’s instant-payment scheme, run by the interbank company ΔΙΑΣ and built into every participating bank’s app. It moves money between accounts at different Greek banks around the clock and in real time, addressed by mobile number or ΑΦΜ instead of an IBAN. Person-to-person transfers are free; receiving as a business is not.',
    },
  },
  'teke': {
    category: 'misc',
    ru: {
      label: 'ΤΕΚΕ (Ταμείο Εγγύησης Καταθέσεων και Επενδύσεων)',
      definition: 'Фонд гарантирования вкладов и инвестиций Греции. Ведёт схему страхования депозитов и отдельную схему компенсации инвесторам, выплачивая возмещение в течение семи рабочих дней с момента объявления банка неплатёжеспособным. По статусу это юридическое лицо частного права под надзором Министерства финансов, а не государственное ведомство.',
    },
    en: {
      label: 'ΤΕΚΕ (Hellenic Deposit and Investment Guarantee Fund)',
      definition: 'The body running Greece’s deposit guarantee scheme and, separately, its investor compensation scheme, paying out within seven working days of a bank being declared failed. It is a private-law legal person supervised by the Ministry of Finance rather than a government agency.',
    },
  },

  // ── Greece — education and language ────────────────────────────────
  'pistopoiitiko-ellinomatheias': {
    category: 'education',
    ru: {
      label: 'Πιστοποιητικό Ελληνομάθειας',
      definition: 'Официальный сертификат владения греческим языком, который выдаёт Κέντρο Ελληνικής Γλώσσας — единственный государственный орган, уполномоченный на это по всему миру. Существует на шести уровнях CEFR от A1 до C2, плюс отдельные линии для детей и для профессиональных целей. С экзаменом на гражданство Π.Ε.Γ.Π. не совпадает ни по содержанию, ни по органу.',
    },
    en: {
      label: 'Πιστοποιητικό Ελληνομάθειας',
      definition: 'The official certificate of attainment in Greek, awarded by the Centre for the Greek Language, the only state body authorised to issue it anywhere in the world. It runs across six CEFR levels from A1 to C2, with separate tracks for children and for professional purposes. It is neither the same exam nor the same authority as the Π.Ε.Γ.Π. citizenship test.',
    },
  },
  'keg': {
    category: 'education',
    ru: {
      label: 'ΚΕΓ (Κέντρο Ελληνικής Γλώσσας)',
      definition: 'Центр греческого языка — государственный орган, отвечающий за сертификацию владения греческим: он и только он присуждает Πιστοποιητικό Ελληνομάθειας, в Греции и за рубежом. Сводной статистики сдачи по общему экзамену ελληνομάθεια центр не публикует.',
    },
    en: {
      label: 'ΚΕΓ (Centre for the Greek Language)',
      definition: 'The state body responsible for certifying Greek-language proficiency: it alone awards the Πιστοποιητικό Ελληνομάθειας, in Greece and abroad. It publishes no aggregate pass rate for the general ελληνομάθεια sitting.',
    },
  },
  'doatap': {
    category: 'education',
    ru: {
      label: 'ΔΟΑΤΑΠ (DOATAP)',
      definition: 'Греческий орган признания иностранных дипломов о высшем образовании — для работы в госсекторе, доступа к регулируемым профессиям и продолжения учёбы. Заявления подаются через собственную электронную платформу, а пошлина платится напрямую на счёт организации, а не через систему государственных παράβολα.',
    },
    en: {
      label: 'ΔΟΑΤΑΠ / DOATAP',
      definition: 'The Greek authority that recognises foreign higher-education qualifications for public-sector employment, regulated professions and further study. Applications go through its own electronic platform, and its fee is paid directly into the organisation’s account rather than through the state παράβολο system.',
    },
  },
  'panelladikes': {
    category: 'education',
    ru: {
      label: 'Πανελλαδικές Εξετάσεις',
      definition: 'Общегреческие выпускные экзамены в конце лицея, по предметам выбранного направления. Аттестат сам по себе доступа в университет не даёт: именно балл на этих экзаменах распределяет абитуриента по факультетам и направлениям — вокруг них и выстроены и старшая школа, и индустрия репетиторских центров.',
    },
    en: {
      label: 'Πανελλαδικές Εξετάσεις (Panhellenic exams)',
      definition: 'The national matriculation exams sat at the end of lykeio in the candidate’s chosen specialisation subjects. The school-leaving certificate alone does not open a university place — it is the exam score in those subjects that maps a candidate to a department, which is why upper-secondary schooling, and the tutoring-centre industry around it, is organised on them.',
    },
  },
  'zep': {
    category: 'education',
    ru: {
      label: 'ΖΕΠ (Ζώνη Εκπαιδευτικής Προτεραιότητας)',
      definition: 'Зона образовательного приоритета — правовая рамка (ν. 3879/2010), в которой региональные дирекции образования открывают приёмные классы (Τάξεις Υποδοχής) для учеников, чьего греческого не хватает для обычной программы. Вопреки названию, это не географическая зона, а статус, о котором школа ходатайствует отдельно, — поэтому наличие такого класса зависит от конкретной школы, а не от района.',
    },
    en: {
      label: 'ΖΕΠ (Educational Priority Zone)',
      definition: 'The legal framework (ν. 3879/2010) under which regional education directorates may open reception classes, Τάξεις Υποδοχής, for pupils whose Greek is not yet up to the mainstream curriculum. Despite the name it is not a geographic zone but a designation a school applies for, so whether such a class exists is a question about the school, not the neighbourhood.',
    },
  },

  // ── Greece — utilities and transport ───────────────────────────────
  'dei-ppc': {
    category: 'misc',
    ru: {
      label: 'ΔΕΗ / PPC',
      definition: 'Крупнейший поставщик электроэнергии для домохозяйств Греции, исторически государственный, ныне приватизированный. Рынок открыт для конкурирующих частных поставщиков больше десяти лет, так что ΔΕΗ — вариант по умолчанию, а не безальтернативный.',
    },
    en: {
      label: 'ΔΕΗ / PPC',
      definition: 'Greece’s largest household electricity supplier, historically state-owned and now privatised. The retail market has been open to competing private suppliers for over a decade, which makes ΔΕΗ the default rather than the only option.',
    },
  },
  'deddie': {
    category: 'misc',
    ru: {
      label: 'ΔΕΔΔΗΕ / HEDNO',
      definition: 'Оператор распределительных электросетей Греции: владеет и управляет сетью низкого и среднего напряжения — как на материке и крупных островах, так и в двадцати восьми отдельных энергосистемах несоединённых островов, где он вдобавок ведёт местную генерацию и местный рынок. Не путать с ΑΔΜΗΕ, оператором магистральной сети высокого напряжения и подводных перемычек.',
    },
    en: {
      label: 'ΔΕΔΔΗΕ / HEDNO',
      definition: 'Greece’s electricity distribution operator, which owns and runs the low- and medium-voltage grid — both the interconnected mainland and large-island network and the twenty-eight separate non-interconnected island systems, where it also runs local generation and the local market. Distinct from ΑΔΜΗΕ, which operates the high-voltage transmission grid and the undersea interconnectors.',
    },
  },
  'eett': {
    category: 'misc',
    ru: {
      label: 'ΕΕΤΤ (EETT)',
      definition: 'Независимый регулятор Греции в области электронных коммуникаций и почтовой связи. Дважды в год публикует отчёт о широкополосном доступе — первоисточник по числу фиксированных и мобильных линий, доле оптики в сети и долям операторов на рынке.',
    },
    en: {
      label: 'ΕΕΤΤ / EETT',
      definition: 'Greece’s independent regulator for electronic communications and postal services. Its twice-yearly Broadband Report is the first-party source for fixed and mobile line counts, fibre rollout share and operator market shares.',
    },
  },
  'kteo': {
    category: 'transit',
    ru: {
      label: 'ΚΤΕΟ (KTEO)',
      definition: 'Обязательный периодический техосмотр в Греции, который проводят и государственные, и лицензированные частные станции. Новый легковой автомобиль проходит первый осмотр через четыре года после первичной регистрации, дальше — раз в два года.',
    },
    en: {
      label: 'ΚΤΕΟ / KTEO',
      definition: 'Greece’s compulsory periodic roadworthiness test, carried out at both public and licensed private centres. A new passenger car is first tested four years after its initial registration and every two years after that.',
    },
  },
  'oasa': {
    category: 'transit',
    ru: {
      label: 'ΟΑΣΑ (OASA)',
      definition: 'Организация городского транспорта Афин: управляет единой сетью — автобусы, троллейбусы, трамвай, три линии метро и относящийся к ней участок пригородной железной дороги. Устанавливает и публикует тарифы и держит систему бесконтактной оплаты ATH.ENA.',
    },
    en: {
      label: 'ΟΑΣΑ / OASA',
      definition: 'The Athens Urban Transport Organisation, which governs the integrated network — buses, trolleybuses, the tram, the three metro lines and the relevant stretch of suburban rail. It sets and publishes fares and runs the ATH.ENA smart-ticketing system.',
    },
  },
  'athena-card': {
    category: 'transit',
    ru: {
      label: 'ATH.ENA CARD / ATH.ENA TICKET',
      definition: 'Бесконтактные носители проездного в Афинах: CARD — перезаряжаемая карта, именная или анонимная, TICKET — перезаряжаемый билет; оба работают на всей сети ΟΑΣΑ. Льготные тарифы и длительные абонементы записываются только на именную карту, на анонимную — нет.',
    },
    en: {
      label: 'ATH.ENA CARD / ATH.ENA TICKET',
      definition: 'Athens’ contactless ticketing media: the CARD is a reloadable card, personalised or anonymous, the TICKET a reloadable smart ticket, and both work across the whole ΟΑΣΑ network. Concession fares and long-term passes can only be loaded onto a personalised card, never the anonymous one.',
    },
  },

  // ── Greece — citizenship and work routes ───────────────────────────
  'kodikas-ellinikis-ithageneias': {
    category: 'admin',
    ru: {
      label: 'Κώδικας Ελληνικής Ιθαγένειας (ν. 3284/2004)',
      definition: 'Кодекс греческого гражданства — закон, по которому гражданство приобретается и утрачивается. Статья 5 задаёт требование к сроку законного проживания при натурализации, статья 7 — экзамен ΠΕΓΠ по языку, истории, культуре и государственному устройству. Не путать с Кодексом миграции ν. 5038/2023: тот про вид на жительство, этот — про паспорт.',
    },
    en: {
      label: 'Κώδικας Ελληνικής Ιθαγένειας (ν. 3284/2004)',
      definition: 'The Code of Greek Citizenship, the statute under which Greek citizenship is acquired and lost. Article 5 sets the lawful-residence requirement for naturalisation and article 7 the ΠΕΓΠ examination in language, history, culture and the institutions of the state. Not to be confused with the Immigration Code ν. 5038/2023: that one governs the residence permit, this one the passport.',
    },
  },
  'blue-card-gr': {
    category: 'admin',
    ru: {
      label: 'Μπλε κάρτα της ΕΕ (τίτλος «Ε.1»)',
      definition: 'Греческая реализация общеевропейского разрешения для высококвалифицированной занятости — титул «Ε.1» по Кодексу миграции. Нужны диплом или подтверждённая высокая профессиональная квалификация и трудовой договор с оплатой не ниже 1,6 средней валовой годовой зарплаты по стране: множитель закреплён в законе, а сама сумма в евро публикуется административно. С 6 февраля 2026 года карта выдаётся на три года и продлевается каждый раз ещё на три.',
    },
    en: {
      label: 'EU Blue Card (permit «Ε.1»)',
      definition: 'Greece’s implementation of the EU permit for highly qualified employment, issued under the Immigration Code as permit type Ε.1. It takes a degree or certified high professional skills plus an employment contract paying at least 1.6 times the average gross annual salary in Greece — the multiplier sits in the statute, the euro value is published administratively. Since 6 February 2026 the card runs three years and renews for three years at a time.',
    },
  },
  'tech-visa-gr': {
    category: 'admin',
    ru: {
      label: 'Tech Visa (τίτλος «Ζ.13Α»)',
      definition: 'Двенадцатимесячный въездной титул «Ζ.13Α» для гражданина третьей страны, работающего исключительно в компании из Национального реестра стартапов, публичная витрина которого — Elevate Greece; добавлен в Кодекс миграции 6 февраля 2026 года. Дверь быстрее, но не дешевле: зарплатный порог тот же, что у голубой карты ЕС, менять работодателя нельзя, а при прекращении трудовых отношений — кроме случая, когда договор нарушил работодатель, — требуется немедленный выезд. Штатный выход — перейти на голубую карту, ещё оставаясь в той же компании.',
    },
    en: {
      label: 'Tech Visa (permit «Ζ.13Α»)',
      definition: 'A twelve-month entry title for a third-country national employed exclusively by a company on the National Startup Registry that Elevate Greece fronts, added to the Immigration Code on 6 February 2026. It is a faster door rather than a cheaper one: the salary bar matches the EU Blue Card, the holder cannot change employer, and if the job ends — unless the employer broke the contract — departure is immediate. The intended way out is converting to a Blue Card while still with the same company.',
    },
  },
  'ggeekdbmn': {
    category: 'education',
    ru: {
      label: 'ΓΓΕΕΚΔΒΜΝ',
      definition: 'Генеральный секретариат профессионального образования, подготовки, обучения в течение всей жизни и молодёжи при министерстве образования. Выдаёт специальный сертификат по языку, истории и культуре — один из равноправных титулов, закрывающих условие интеграции для статуса долгосрочного резидента ЕС, — и отдельно подтверждает знание греческой истории и культуры владельцам старых языковых сертификатов, которые засчитывают только языковую часть.',
    },
    en: {
      label: 'ΓΓΕΕΚΔΒΜΝ',
      definition: 'The General Secretariat for Vocational Education, Training, Lifelong Learning and Youth, inside the Ministry of Education. It issues the special language-and-civics certificate that counts as one of the alternative titles satisfying the integration condition for EU long-term residence, and separately certifies knowledge of Greek history and culture for holders of older language certificates that prove the language element alone.',
    },
  },

  // ── Greece — health administration ─────────────────────────────────
  'idika': {
    category: 'health',
    ru: {
      label: 'IDIKA',
      definition: 'Центр электронного управления социальным обеспечением — государственная структура, которая ведёт информационные системы греческого здравоохранения и социального страхования. Именно её система автоматически прикрепляет личного врача взрослому, который не выбрал врача сам, — по адресу, привязанному к ΑΜΚΑ.',
    },
    en: {
      label: 'IDIKA',
      definition: 'The e-Government Centre for Social Security, the state body that runs the electronic systems behind Greek healthcare and social-insurance administration. It is the machinery that assigns a personal doctor automatically to an adult who has not chosen one, going by the address held against their ΑΜΚΑ.',
    },
  },
  'ken-drg-gr': {
    category: 'health',
    ru: {
      label: 'ΚΕΝ (тариф DRG)',
      definition: 'Греческий тариф клинико-статистических групп: фиксированная цена за случай определённого типа вместо счёта по отдельным позициям. Пациенту это важно потому, что доплата в частной клинике, работающей по договору с ΕΟΠΥΥ, считается долей от стоимости госпитализации по этому тарифу, а для случаев, оплачиваемых по числу койко-дней, доля другая.',
    },
    en: {
      label: 'ΚΕΝ (DRG tariff)',
      definition: 'Greece’s diagnosis-related-group tariff: a fixed price per case type rather than an itemised bill for a hospital stay. It matters to a patient because the co-payment at an ΕΟΠΥΥ-contracted private clinic is a share of a stay priced under this tariff, with a different share for one billed per day.',
    },
  },
  'emmeso-melos': {
    category: 'health',
    ru: {
      label: 'έμμεσο μέλος',
      definition: 'Непрямой, иждивенческий член страхования — супруг или ребёнок, покрытые по чужой регистрации в e-ΕΦΚΑ без отдельных взносов. Неработающий супруг подтверждает право заявлением под присягу: не работает, не получает пенсию, не имеет другой страховки; ребёнок — пока не состоит в браке и остаётся на иждивении, до возрастного предела, который продлевается на время очной учёбы. Этот механизм оставляет семье с одним работающим одну страховую запись вместо нескольких.',
    },
    en: {
      label: 'έμμεσο μέλος',
      definition: 'Indirect member — a spouse or child carried on someone else’s e-ΕΦΚΑ registration with no separate contribution. A non-working spouse qualifies on a sworn declaration of neither working, nor drawing a pension, nor holding other cover; a child qualifies while unmarried and dependent, up to an age limit that extends for full-time study. It is the mechanism that keeps a single-earner household to one insurance record rather than several.',
    },
  },

  // ── Greece — schooling and university admission ────────────────────
  'frontistirio-gr': {
    category: 'education',
    ru: {
      label: 'φροντιστήριο',
      definition: 'Частное платное учреждение дополнительного образования вне школы; подавляющая часть его работы — подготовка старшеклассников к общегреческим экзаменам. Это не центр иностранных языков (κέντρο ξένων γλωσσών) и не индивидуальные занятия с репетитором (ιδιαίτερα μαθήματα): греческая статистика расходов домохозяйств считает все три категории раздельно.',
    },
    en: {
      label: 'φροντιστήριο',
      definition: 'A private, fee-paying after-school tutoring institution, used overwhelmingly to prepare secondary pupils for the Panhellenic exams. It is a separate thing from a foreign-language centre (κέντρο ξένων γλωσσών) and from one-to-one private lessons (ιδιαίτερα μαθήματα) — Greek household-spending statistics count the three apart.',
    },
  },
  'xps-gr': {
    category: 'education',
    ru: {
      label: 'Ξ.Π.Σ. (Ξενόγλωσσο Πρόγραμμα Σπουδών)',
      definition: 'Платная программа первого цикла на иностранном языке, которую греческий государственный университет вправе вести по закону ν. 4692/2020; доступ — исключительно у иностранных граждан, окончивших среднюю школу за рубежом. Это юридически отдельный канал поступления, а не платная версия бесплатной грекоязычной программы той же кафедры; размер платы закон не устанавливает — его определяет сама программа.',
    },
    en: {
      label: 'Ξ.Π.Σ. (Foreign-Language Study Programme)',
      definition: 'A fee-charging, foreign-language-taught first-cycle degree track that a Greek public university may run under ν. 4692/2020, open exclusively to foreign nationals who finished secondary school at a school based abroad. It is a legally separate admission channel rather than a paid tier of the free Greek-taught degree the same department may also offer, and the law fixes no tuition amount — each programme sets its own.',
    },
  },
  'allodapoi-allogeneis-gr': {
    category: 'education',
    ru: {
      label: 'Αλλοδαποί-Αλλογενείς',
      definition: 'Особая категория поступления (законы ν. 4415/2016 и ν. 4452/2017) на обычные грекоязычные программы государственных вузов Греции для выпускников лицеев вне ЕС: отбор идёт по итоговой оценке зарубежного аттестата, минуя общегреческие экзамены. Условия — хотя бы один родитель не греческого происхождения и последние два класса средней школы, оконченные за рубежом очно. Сертификат греческого языка требуется для зачисления, а не для подачи заявления.',
    },
    en: {
      label: 'Αλλοδαποί-Αλλογενείς',
      definition: 'A special admission category (ν. 4415/2016, ν. 4452/2017) into the regular Greek-taught programmes of Greek public universities for lyceum graduates from outside the EU, selected on the foreign school-leaving grade instead of the Panhellenic exams. It requires that at least one parent is not of Greek origin and that the last two years of secondary school were completed abroad in full-time attendance. A Greek-language certificate is needed to enrol, not to apply.',
    },
  },

  // ── Greece — climate and natural hazard ────────────────────────────
  'meltemi-gr': {
    category: 'misc',
    ru: {
      label: 'μελτέμι (meltemi)',
      definition: 'Мельтеми — сильный сухой северный ветер, греческое имя этезиев, дующий над Эгейским морем в основном с середины июня до начала сентября. У островов он доходит до штормовой силы и на сутки и дольше останавливает паромы, поэтому эгейское лето определяет сильнее, чем среднемесячная температура.',
    },
    en: {
      label: 'μελτέμι (meltemi)',
      definition: 'The strong, dry northerly Etesian wind that blows across the Aegean, mainly from mid-June to early September. Around the islands it reaches gale force and grounds ferries for a day or more at a time, which is why it shapes an Aegean summer far more than a monthly temperature average does.',
    },
  },
  'oasp': {
    category: 'misc',
    ru: {
      label: 'ΟΑΣΠ / OASP',
      definition: 'Организация противосейсмического планирования и защиты — компетентный орган по греческому противосейсмическому кодексу, который она ведёт через собственный постоянный научный комитет. Она же задаёт деление страны на зоны сейсмической опасности — рамку, по которой проверяется расчёт любого здания.',
    },
    en: {
      label: 'ΟΑΣΠ / OASP',
      definition: 'The Earthquake Planning and Protection Organisation, the competent authority for the Greek seismic code, which it maintains through its own standing scientific committee. It defines the country’s seismic hazard zones — the frame against which a building’s design is checked.',
    },
  },

  // ── Greece — utilities, transport and digital government ───────────
  'eydap': {
    category: 'misc',
    ru: {
      label: 'ΕΥΔΑΠ / EYDAP',
      definition: 'Компания водоснабжения Аттики — та, что поит Афины. Её водохранилища, включая Морнос, служат самой наглядной мерой того, сколько запаса прочности остаётся у столичного водоснабжения в засушливый год.',
    },
    en: {
      label: 'ΕΥΔΑΠ / EYDAP',
      definition: 'The Athens water utility, which supplies Attica. Its reservoirs, Mornos among them, are the plainest available measure of how much slack the capital’s water supply has in a dry year.',
    },
  },
  'oasth-oseth': {
    category: 'transit',
    ru: {
      label: 'ΟΑΣΘ / OSETH',
      definition: 'Городской транспорт Салоник: ΟΑΣΘ — транспортная организация города, а OSETH, транспортное управление Салоник, публикует тарифную таблицу; сами тарифы устанавливает совместное министерское решение, а не перевозчик. Базовый билет и срок его действия едины для автобусов ΟΑΣΘ и для метро.',
    },
    en: {
      label: 'ΟΑΣΘ / OSETH',
      definition: 'Thessaloniki’s public transport: ΟΑΣΘ is the city’s transport organisation, while OSETH, the Thessaloniki transport authority, is the body that publishes the fare table — the fares themselves are fixed by joint ministerial decision, not by the operator. The basic ticket and its validity period are the same across ΟΑΣΘ buses and the metro.',
    },
  },
  'teli-kykloforias': {
    category: 'transit',
    ru: {
      label: 'τέλη κυκλοφορίας',
      definition: 'Ежегодный транспортный налог. Автомобиль, впервые зарегистрированный с 2021 года, облагается по граммам CO2 на километр в возрастающих диапазонах, зарегистрированный с ноября 2010 по конец 2020 года — по более ранней и более мягкой шкале CO2, а всё, что старше, — по объёму двигателя; электромобили и водородные машины освобождены полностью. Платят за год вперёд, до его начала, а надбавка за просрочку растёт от месяца к месяцу.',
    },
    en: {
      label: 'τέλη κυκλοφορίας',
      definition: 'The annual circulation tax on a vehicle. A car first registered from 2021 is taxed per gram of CO2 per kilometre across ascending bands, one registered between November 2010 and the end of 2020 on an earlier and gentler CO2 scale, and anything older on engine displacement; electric and hydrogen vehicles are exempt outright. It is paid for the year ahead before that year begins, and the late surcharge steps up month by month.',
    },
  },
  'mycar': {
    category: 'transit',
    ru: {
      label: 'myCAR',
      definition: 'Платформа ΑΑΔΕ, через которую выставляется и оплачивается ежегодный транспортный налог. Квитанция на очередной год появляется до его начала, так что платформа задаёт не только способ оплаты, но и срок.',
    },
    en: {
      label: 'myCAR',
      definition: 'The ΑΑΔΕ platform through which the annual circulation-tax notice is issued and paid. The notice for a given year comes due before that year starts, so the platform is the deadline as much as the payment channel.',
    },
  },
  'taxisnet': {
    category: 'admin',
    ru: {
      label: 'TaxisNet',
      definition: 'Личные реквизиты доступа налоговой службы — логин, к которому обращается большая часть греческого электронного государства, включая Gov.gr Wallet. Их выдают только после регистрации в ΑΑΔΕ, а новоприбывшему без греческой налоговой истории это обычно означает предварительный шаг лично или через бухгалтера: у «цифрового по умолчанию» есть вполне физическое предусловие.',
    },
    en: {
      label: 'TaxisNet',
      definition: 'The tax authority’s personal login credentials, which most of Greek e-government reaches for, Gov.gr Wallet included. They are only issued after registering with ΑΑΔΕ, and for a newcomer with no Greek tax history that usually means an in-person or accountant-mediated step first: the digital-first route has a very physical prerequisite.',
    },
  },

  // ── UAE — banking and financial regulators ─────────────────────────
  'cbuae': {
    category: 'admin',
    ru: {
      label: 'CBUAE',
      definition: 'Центральный банк ОАЭ, базируется в Абу-Даби. Надзирает за национальными банками и филиалами иностранных, устанавливает резервные требования, публикует ставку EIBOR, следит за антиотмывочными нормами и управляет системой мгновенных платежей AANI.',
    },
    en: {
      label: 'CBUAE',
      definition: 'The Central Bank of the UAE, based in Abu Dhabi. It supervises the national banks and the foreign branches, sets reserve requirements, publishes the EIBOR benchmark, enforces anti-money-laundering rules and operates the AANI instant-payment system.',
    },
  },
  'aani': {
    category: 'admin',
    ru: {
      label: 'AANI',
      definition: 'Система мгновенных межбанковских платежей ОАЭ, запущенная Центральным банком в 2023 году. Дирхамы уходят между банками-участниками по номеру телефона, IBAN или QR-коду и приходят за секунды — по роли это местный SEPA Instant или PIX.',
    },
    en: {
      label: 'AANI',
      definition: 'The UAE instant payment rail, launched by the Central Bank in 2023. Dirhams move between participating banks by phone number, IBAN or QR code and land in seconds — the local equivalent of Faster Payments, SEPA Instant or PIX.',
    },
  },
  'wps': {
    category: 'admin',
    ru: {
      label: 'WPS',
      definition: 'Wage Protection System — обязательный федеральный зарплатный канал для работодателей материковой части ОАЭ, под управлением министерства труда MoHRE. Зарплата проходит через подключённый к системе банк и оставляет работнику подтверждаемую историю выплат, которую спрашивают при продлении визы, аренде и ипотеке.',
    },
    en: {
      label: 'WPS',
      definition: 'The Wage Protection System, the mandatory federal payroll channel for mainland UAE employers, run by the labour ministry (MoHRE). Salaries pass through a registered bank, which leaves the employee a verifiable payment record — the thing visa renewals, landlords and mortgage lenders ask to see.',
    },
  },
  'difc': {
    category: 'admin',
    ru: {
      label: 'DIFC',
      definition: 'Dubai International Financial Centre — финансовая свободная зона и деловой квартал в центре Дубая. В ней регистрируют компании и структуры семейных офисов, недвижимость внутри зоны переоформляется по её собственной процедуре, а не через Dubai Land Department, а её Wills Service Centre регистрирует завещания, перекрывающие наследование по умолчанию.',
    },
    en: {
      label: 'DIFC',
      definition: 'The Dubai International Financial Centre, a financial free zone and business district in central Dubai. Companies and family-office vehicles are set up there, property inside it transfers under its own procedure rather than through the Dubai Land Department, and its Wills Service Centre registers wills that override default succession.',
    },
  },
  'adgm': {
    category: 'admin',
    ru: {
      label: 'ADGM',
      definition: 'Abu Dhabi Global Market — финансовая свободная зона Абу-Даби, параллель дубайскому DIFC. Здесь регистрируют компании и структуры семейных офисов и оформляют завещание, перекрывающее наследование по умолчанию; финансовую деятельность внутри зоны лицензирует её собственный регулятор FSRA.',
    },
    en: {
      label: 'ADGM',
      definition: 'The Abu Dhabi Global Market, the emirate’s financial free zone and the counterpart to Dubai’s DIFC. Companies and family-office vehicles are registered there, a will registered there overrides default succession, and financial activity inside the zone is licensed by its own regulator, the FSRA.',
    },
  },
  'fsra': {
    category: 'admin',
    ru: {
      label: 'FSRA',
      definition: 'Financial Services Regulatory Authority — финансовый регулятор свободной зоны Abu Dhabi Global Market. Лицензирует финансовую деятельность внутри зоны, включая операции с цифровыми активами; в Дубае аналогичную работу по крипторынку ведёт VARA.',
    },
    en: {
      label: 'FSRA',
      definition: 'The Financial Services Regulatory Authority, the regulator of the Abu Dhabi Global Market free zone. It licenses financial activity inside the zone, digital-asset business included — the Abu Dhabi parallel to Dubai’s VARA regime.',
    },
  },
  'vara': {
    category: 'admin',
    ru: {
      label: 'VARA',
      definition: 'Virtual Assets Regulatory Authority — дубайский регулятор рынка цифровых активов, работает с марта 2022 года. Лицензирует биржи, OTC-дески, кастодианов и NFT-платформы; легальный путь к покупке криптовалюты в эмирате идёт через лицензированные ею площадки.',
    },
    en: {
      label: 'VARA',
      definition: 'Dubai’s Virtual Assets Regulatory Authority, live since March 2022. It licenses exchanges, OTC desks, custodians and NFT platforms; the regulated route into crypto in the emirate runs through the venues it has licensed.',
    },
  },

  // ── UAE — property finance ─────────────────────────────────────────
  'dbr': {
    category: 'property',
    ru: {
      label: 'DBR',
      definition: 'Debt Burden Ratio — предельная доля месячного дохода, которую банк ОАЭ разрешает тратить на обслуживание всех долгов сразу: ипотека, автокредит, карты, потребительские займы. Вместе с возрастом заёмщика на дату погашения это главный ограничитель при одобрении ипотеки.',
    },
    en: {
      label: 'DBR',
      definition: 'The Debt Burden Ratio: the ceiling a UAE lender puts on the share of monthly income going to service all debt at once — mortgage, car loan, cards, personal loans. Alongside the borrower’s age at maturity, it is the binding constraint on mortgage approval.',
    },
  },

  // ── UAE — climate and weather ──────────────────────────────────────
  'ncm': {
    category: 'misc',
    ru: {
      label: 'NCM',
      definition: 'Национальный центр метеорологии ОАЭ. Ведёт прогноз, рассылает SMS-предупреждения о пыли и штормах и держит приложение Mausam, ставшее стандартом у жителей; ему же принадлежит программа засева облаков для усиления осадков.',
    },
    en: {
      label: 'NCM',
      definition: 'The UAE National Center of Meteorology. It forecasts, pushes SMS alerts for dust and storm conditions, and maintains the Mausam app that residents rely on. It also runs the country’s cloud-seeding rain-enhancement programme.',
    },
  },
  'haboob': {
    category: 'misc',
    ru: {
      label: 'haboob',
      definition: 'Пыльная буря: плотная стена поднятого песка, которую несёт ветер с пустыни, с падением видимости ниже километра и скачком индекса качества воздуха. Явление свойственно аридным регионам вообще; в ОАЭ хабубы формируются летними послеполуденными часами во внутренних районах, а пик пыльного сезона приходится на весну.',
    },
    en: {
      label: 'haboob',
      definition: 'A dust storm: a dense wall of suspended sand carried on desert winds, dropping visibility below a kilometre and spiking air-quality readings. The phenomenon belongs to arid regions generally; in the UAE haboobs form on inland summer afternoons, with spring the peak dust season.',
    },
  },
  'mid-day-ban': {
    category: 'misc',
    ru: {
      label: 'запрет уличных работ в полуденные часы',
      definition: 'Федеральный запрет на работу под открытым небом в самые жаркие часы дня, действует каждое лето с середины июня до середины сентября (Ministerial Decree 401/2015). Под него подпадают прежде всего стройка, дорожные работы, портовая погрузка и озеленение, а штраф за нарушение платит работодатель, за каждого работника.',
    },
    en: {
      label: 'mid-day work ban',
      definition: 'The federal ban on outdoor work during the hottest hours of the day, in force every summer from mid-June to mid-September under Ministerial Decree 401/2015. It covers construction, road works, port handling and landscaping above all, and the fine for breaching it falls on the employer, per worker.',
    },
  },
  'addc': {
    category: 'admin',
    ru: {
      label: 'ADDC',
      definition: 'Коммунальный оператор эмирата Абу-Даби: выставляет счета за электричество и воду по ступенчатому тарифу. Это абу-дабийский аналог дубайской DEWA, а в Шардже и северных эмиратах ту же роль играют SEWA и FEWA.',
    },
    en: {
      label: 'ADDC',
      definition: 'The utility that bills electricity and water in the emirate of Abu Dhabi, on tiered slab tariffs. It is the Abu Dhabi counterpart of Dubai’s DEWA, with SEWA and FEWA filling the same role in Sharjah and the Northern Emirates.',
    },
  },

  // ── UAE — schooling regulators and universities ────────────────────
  'moe-ae': {
    category: 'education',
    ru: {
      label: 'Министерство образования ОАЭ',
      definition: 'Федеральное министерство образования ОАЭ. На федеральном уровне отвечает за государственные школы, а частные школы северных эмиратов регулирует напрямую — там нет собственного эмиратского регулятора, в отличие от Дубая, Абу-Даби и Шарджи.',
    },
    en: {
      label: 'Ministry of Education',
      definition: 'The UAE federal education ministry. It carries the public-school system at federal level and regulates private schools in the Northern Emirates directly — those emirates have no regulator of their own, unlike Dubai, Abu Dhabi and Sharjah.',
    },
  },
  'khda': {
    category: 'education',
    ru: {
      label: 'KHDA',
      definition: 'Knowledge and Human Development Authority — регулятор частного образования Дубая. Ежегодно инспектирует каждую частную школу эмирата, публикует отчёт и оценку по шестибалльной шкале от Outstanding до Very Weak и держит потолок повышения платы.',
    },
    en: {
      label: 'KHDA',
      definition: 'The Knowledge and Human Development Authority, Dubai’s private-education regulator. It inspects every private school in the emirate annually, publishes the report and a six-band rating from Outstanding to Very Weak, and caps how far fees may rise.',
    },
  },
  'adek': {
    category: 'education',
    ru: {
      label: 'ADEK',
      definition: 'Регулятор образования эмирата Абу-Даби. Инспектирует частные школы эмирата по рамке Irtiqaa с той же шестибалльной шкалой, что и дубайская KHDA, и ведёт реформированную программу государственных школ Абу-Даби.',
    },
    en: {
      label: 'ADEK',
      definition: 'The education regulator for the emirate of Abu Dhabi. It inspects the emirate’s private schools through the Irtiqaa framework, on the same six-band scale Dubai’s KHDA uses, and oversees the reformed Abu Dhabi public-school curriculum.',
    },
  },
  'spea': {
    category: 'education',
    ru: {
      label: 'SPEA',
      definition: 'Sharjah Private Education Authority — регулятор частных школ эмирата Шарджа. Утверждает плату и требования к программе и инспектирует школы по тем же бэндам, что Дубай и Абу-Даби.',
    },
    en: {
      label: 'SPEA',
      definition: 'The Sharjah Private Education Authority, the regulator of private schools in the emirate of Sharjah. It approves fees and curriculum requirements and inspects schools on the same bands used in Dubai and Abu Dhabi.',
    },
  },
  'irtiqaa': {
    category: 'education',
    ru: {
      label: 'Irtiqaa',
      definition: 'Рамка школьных инспекций Абу-Даби, по которой ADEK оценивает частные школы эмирата. Шесть бэндов и набор критериев близки дубайским, поэтому оценки школ двух эмиратов сопоставимы между собой.',
    },
    en: {
      label: 'Irtiqaa',
      definition: 'The Abu Dhabi school-inspection framework through which ADEK rates the emirate’s private schools. Its six bands and its rubric line up with Dubai’s, which is what makes ratings comparable across the two emirates.',
    },
  },
  'a-level': {
    category: 'education',
    ru: {
      label: 'A-Level',
      definition: 'Выпускной экзамен английской школьной системы, который сдают около 18 лет по нескольким выбранным предметам, следующей ступенью после GCSE. Это основная валюта поступления в британские университеты, и школы британской программы предлагают его по всему миру, не только в Великобритании.',
    },
    en: {
      label: 'A-Level',
      definition: 'The final examination of the English school system, taken at around 18 in a handful of chosen subjects, the stage after GCSE. It is the main admissions currency for British universities and is offered by British-curriculum schools worldwide, not only in Britain.',
    },
  },
  'cbse': {
    category: 'education',
    ru: {
      label: 'CBSE',
      definition: 'Индийская национальная школьная программа с собственными выпускными экзаменами; вместе с ICSE — одна из двух основных индийских систем. По ней работают школы и за пределами Индии, там где есть крупная индийская община, и она даёт прямой вход в индийские университеты.',
    },
    en: {
      label: 'CBSE',
      definition: 'One of India’s two main national school curricula (ICSE is the other), with its own board examinations. Schools follow it well beyond India wherever there is a large Indian community, and it feeds directly into Indian university admission.',
    },
  },
  'nyu-ad': {
    category: 'education',
    ru: {
      label: 'NYU Abu Dhabi',
      definition: 'Кампус Нью-Йоркского университета на острове Саадият в Абу-Даби: полноценный бакалавриат свободных искусств и научный партнёр нью-йоркского кампуса. Приём need-blind, конкурс один из самых жёстких в мире, а финансовая помощь покрывает полную потребность зачисленного.',
    },
    en: {
      label: 'NYU Abu Dhabi',
      definition: 'New York University’s campus on Saadiyat Island in Abu Dhabi — a full liberal-arts undergraduate college and a research partner of NYU New York. Admission is need-blind, the acceptance rate is among the lowest anywhere, and aid meets the full need of those admitted.',
    },
  },
  'mbzuai': {
    category: 'education',
    ru: {
      label: 'MBZUAI',
      definition: 'Mohamed bin Zayed University of Artificial Intelligence в Masdar City — исследовательский университет только для магистратуры и аспирантуры, сфокусированный на искусственном интеллекте, машинном обучении и компьютерном зрении. Принятым оплачивают обучение и платят стипендию.',
    },
    en: {
      label: 'MBZUAI',
      definition: 'The Mohamed bin Zayed University of Artificial Intelligence in Masdar City: a graduate-only research university built around AI, machine learning and computer vision. Admitted master’s and PhD students are funded — tuition covered and a stipend paid.',
    },
  },

  // ── UAE — telecom, rail and city transit ───────────────────────────
  'etisalat': {
    category: 'misc',
    ru: {
      label: 'e& (Etisalat)',
      definition: 'Крупнейший из двух лицензированных телеком-операторов ОАЭ, контролируется государством; прежнее имя — Etisalat. Даёт оптику до квартиры и мобильную сеть; второй оператор рынка — du.',
    },
    en: {
      label: 'e& (Etisalat)',
      definition: 'The larger of the UAE’s two licensed telecom operators, majority state-owned and formerly named Etisalat. It provides fibre-to-the-home and mobile service; du is the other operator on the market.',
    },
  },
  'du-ae': {
    category: 'misc',
    ru: {
      label: 'du',
      definition: 'Второй лицензированный телеком-оператор ОАЭ, конкурент e&; юридическое лицо — EITC. Держит собственную оптику до квартиры и мобильную сеть с сопоставимым покрытием 5G.',
    },
    en: {
      label: 'du',
      definition: 'The second licensed UAE telecom operator and the competitor to e&; the operating company is EITC. It runs its own fibre-to-the-home and mobile network, with comparable 5G coverage.',
    },
  },
  'etihad-rail': {
    category: 'transit',
    ru: {
      label: 'Etihad Rail',
      definition: 'Общефедеральная железная дорога ОАЭ. Грузовая сеть открылась в 2023 году и связала все семь эмиратов от границы с Саудовской Аравией до побережья Оманского залива, сняв часть потока с автотрасс; пассажирская очередь с тридцатью с лишним станциями ещё строится.',
    },
    en: {
      label: 'Etihad Rail',
      definition: 'The UAE’s federation-wide railway. Its freight network opened in 2023, running from the Saudi border to the Gulf of Oman coast and linking all seven emirates; the passenger stage, with thirty-plus stations, is still under construction.',
    },
  },
  'rta': {
    category: 'transit',
    ru: {
      label: 'RTA',
      definition: 'Транспортное ведомство Дубая. Управляет метро и трамваем, автобусной сетью и регулируемым такси, выпускает карту Nol и занимается водительскими правами, включая обмен иностранного удостоверения. У соседних эмиратов свои ведомства под тем же названием и с более тонкими сетями.',
    },
    en: {
      label: 'RTA',
      definition: 'Dubai’s transport authority. It runs the Metro and tram, the bus network and the regulated taxi fleet, issues the Nol card, and handles driving licences including the exchange of a foreign one. Neighbouring emirates run their own similarly named authorities over thinner networks.',
    },
  },
  'dmt-ad': {
    category: 'transit',
    ru: {
      label: 'DMT',
      definition: 'Department of Municipalities and Transport — транспортное ведомство Абу-Даби. Держит автобусную сеть эмирата: остров Абу-Даби, Реем, Яс, Саадият и материковые пригороды. Метро в Абу-Даби нет.',
    },
    en: {
      label: 'DMT',
      definition: 'The Department of Municipalities and Transport, Abu Dhabi’s transport authority. It runs the emirate’s bus network across Abu Dhabi Island, Reem, Yas, Saadiyat and the inland suburbs. There is no metro in Abu Dhabi.',
    },
  },
  'hafilat': {
    category: 'transit',
    ru: {
      label: 'Hafilat',
      definition: 'Транспортная карта Абу-Даби: ею платят за проезд в автобусах эмирата, есть месячный безлимит с потолком стоимости. Абу-дабийский аналог дубайской карты Nol.',
    },
    en: {
      label: 'Hafilat',
      definition: 'Abu Dhabi’s stored-value transport card, used to pay on the emirate’s buses and available as a capped monthly unlimited pass. It is the Abu Dhabi counterpart of Dubai’s Nol card.',
    },
  },
  'hala': {
    category: 'transit',
    ru: {
      label: 'Hala',
      definition: 'Совместный сервис дубайского RTA и Careem: вызывает регулируемое такси RTA через приложение Careem и считает поездку по счётчику. Полезен, когда динамическая наценка в самом Careem поднимает цену выше тарифа.',
    },
    en: {
      label: 'Hala',
      definition: 'A joint venture between Dubai’s RTA and Careem that dispatches regulated RTA taxis through the Careem app at the metered fare. It is the fallback when surge pricing on Careem itself runs above the meter.',
    },
  },

  // ── UAE — law, policing and civil status ───────────────────────────
  'moi': {
    category: 'admin',
    ru: {
      label: 'MoI',
      definition: 'Министерство внутренних дел ОАЭ. Задаёт федеральную полицейскую политику, ведёт дорожную и миграционную вертикаль и пограничный контроль; полиция каждого эмирата работает под этим зонтиком и разбирает повседневные дела сама.',
    },
    en: {
      label: 'MoI',
      definition: 'The UAE Ministry of Interior. It sets federal policing policy and runs the traffic, immigration and border-control apparatus; each emirate’s own police force sits under that umbrella and handles everyday matters itself.',
    },
  },
  'cybercrime-law-ae': {
    category: 'admin',
    ru: {
      label: 'закон о киберпреступлениях ОАЭ',
      definition: 'Федеральный декрет-закон 34/2021, самый широкий речевой закон федерации. Криминализует онлайн-клевету, оскорбление государства, правящих семей и религии, распространение «слухов» и ложных новостей, выдачу себя за другого и несанкционированный сбор персональных данных; наказания включают крупные штрафы и лишение свободы.',
    },
    en: {
      label: 'Cybercrime Law',
      definition: 'Federal Decree-Law 34/2021, the broadest piece of speech regulation in the federation. It criminalises online defamation, insult to the state, the ruling families and religion, the spreading of rumours or false news, impersonation and the unauthorised collection of personal data, with heavy fines and custodial sentences attached.',
    },
  },
  'personal-status-law': {
    category: 'admin',
    ru: {
      label: 'закон о личном статусе для немусульман',
      definition: 'Федеральный декрет-закон 41/2022 вместе с более ранним законом Абу-Даби 14/2021: гражданская судебная рамка семейного права для немусульман в ОАЭ. Покрывает брак, развод, раздел совместного имущества, наследование и опеку над детьми там, где раньше по умолчанию применялись шариатские принципы.',
    },
    en: {
      label: 'Personal Status Law',
      definition: 'Federal Decree-Law 41/2022, together with the earlier Abu Dhabi Law 14/2021: a civil-court framework of family law for non-Muslims in the UAE. It covers marriage, divorce, division of joint property, inheritance and child custody, in a space where Sharia principles previously applied by default.',
    },
  },
  'abrahamic-family': {
    category: 'misc',
    ru: {
      label: 'Abrahamic Family House',
      definition: 'Комплекс на острове Саадият в Абу-Даби, открытый в 2023 году: церковь, синагога и мечеть на одной площадке. Демонстративный государственный жест в сторону религиозной терпимости.',
    },
    en: {
      label: 'Abrahamic Family House',
      definition: 'The compound on Saadiyat Island in Abu Dhabi, opened in 2023, holding a church, a synagogue and a mosque on one site. A deliberately visible state statement about religious tolerance.',
    },
  },

  // ── UAE — religious calendar and hospitality ───────────────────────
  'eid-al-fitr': {
    category: 'misc',
    ru: {
      label: 'Eid Al Fitr',
      definition: 'Праздник разговения, которым мусульмане во всём мире завершают месяц Рамадан. В ОАЭ это государственные выходные на несколько дней, а точную дату Комитет по наблюдению за луной подтверждает за день-два, поэтому перелёты и отели вокруг праздника бронируют с запасом.',
    },
    en: {
      label: 'Eid Al Fitr',
      definition: 'The festival with which Muslims everywhere close the month of Ramadan. In the UAE it is a public holiday of several days, and the Moon Sighting Committee confirms the exact date only a day or two ahead — which is what makes flights and hotels booked around it a small gamble.',
    },
  },
  'eid-al-adha': {
    category: 'misc',
    ru: {
      label: 'Eid Al Adha',
      definition: 'Праздник жертвоприношения, второй из двух главных мусульманских праздников, отмечается во всём исламском мире. В ОАЭ вместе с предшествующим Днём Арафата даёт несколько выходных подряд, а дата смещается по лунному календарю.',
    },
    en: {
      label: 'Eid Al Adha',
      definition: 'The festival of sacrifice, the second of the two major Muslim festivals and observed across the Islamic world. In the UAE it comes with the preceding Arafat Day as a run of several public holidays, on a date that moves with the lunar calendar.',
    },
  },
  'iftar': {
    category: 'misc',
    ru: {
      label: 'ифтар',
      definition: 'Вечерняя трапеза, которой мусульмане прерывают дневной пост в Рамадан: открывается финиками и водой и разрастается до общего застолья. В ОАЭ ифтар — социальная ось месяца: мечети кормят бесплатно, отели держат платные буфеты, а часы после заката становятся пиком общения.',
    },
    en: {
      label: 'Iftar',
      definition: 'The meal that breaks the daily Ramadan fast at sunset, opening with dates and water and widening into a shared feast. In the UAE it is the social anchor of the month: mosques serve free public meals, hotels run paid buffets, and the hours after sunset are the peak of the day.',
    },
  },
  'mawlid': {
    category: 'misc',
    ru: {
      label: 'Mawlid al-Nabi',
      definition: 'Отмечаемый день рождения пророка Мухаммада. Дата значима во многих мусульманских странах; в ОАЭ это один государственный выходной, смещающийся по лунному календарю.',
    },
    en: {
      label: 'Mawlid al-Nabi',
      definition: 'The observance of the Prophet Muhammad’s birthday. It is marked across much of the Muslim world; in the UAE it is a single public holiday, on a date that moves with the lunar calendar.',
    },
  },
  'majlis': {
    category: 'misc',
    ru: {
      label: 'majlis',
      definition: 'Помещение для приёма гостей в арабской традиции Залива: низкие подушки вдоль стен, открытая дверь, кофе с финиками и неспешный разговор. Посторонний попадает туда по приглашению, но сама модель — сперва кофе и разговор, потом дело — переносится и в деловые встречи.',
    },
    en: {
      label: 'majlis',
      definition: 'The reception space of Arab Gulf hospitality: low cushions along the walls, an open door, coffee and dates, unhurried conversation. An outsider enters by invitation, but the model itself — coffee and family news first, business after — carries over into meeting culture.',
    },
  },
  'khaleeji': {
    category: 'misc',
    ru: {
      label: 'khaleeji',
      definition: 'Разговорный арабский стран Персидского залива; эмиратская речь — его местный вариант. От литературного арабского, на котором пишут законы и ведут новости, он отличается заметно, так что курс «арабского» не обязательно даёт понимание бытового разговора.',
    },
    en: {
      label: 'Khaleeji',
      definition: 'Gulf Arabic, the spoken dialect group of the Gulf states; Emirati speech is its local variety. It differs audibly from the Modern Standard Arabic of law and broadcast, so a course in “Arabic” does not automatically unlock everyday conversation.',
    },
  },

  // ── Cyprus — e-government and residence documents ──────────────────
  'ariadni': {
    category: 'admin',
    ru: {
      label: 'Ariadni.gov.cy',
      definition: 'Центральный портал электронного правительства Кипра, задуманный как единое окно. Через него подают и отслеживают заявления на ВНЖ, регистрируются в налоговой, запрашивают документы гражданского реестра и подают отчётность компаний; чего он не заменяет — первичного личного визита за биометрией.',
    },
    en: {
      label: 'Ariadni.gov.cy',
      definition: 'The Cypriot central e-government portal, built as a single window. Residence-permit applications and their status, tax registration, civil-registry documents and company filings all run through it; what it cannot replace is a first-time in-person biometric appointment.',
    },
  },
  'yellow-slip': {
    category: 'admin',
    ru: {
      label: '«жёлтый документ»',
      definition: 'Обиходное название регистрационного документа о проживании на Кипре, который выдаёт Департамент гражданского реестра и миграции: для граждан ЕС это регистрация MEU1, в третьестрановом треке — временный ВНЖ категории F. Оформляющий впервые обязан прийти лично на биометрию — полностью онлайн процесс не закрывается.',
    },
    en: {
      label: 'Yellow Slip',
      definition: 'The everyday name for the Cypriot residence registration document issued by the Civil Registry and Migration Department: the MEU1 registration for EU nationals, the Category F temporary residence permit on the third-country track. A first-time applicant has to appear in person for biometrics; the portal cannot close the file.',
    },
  },

  // ── Cyprus — vehicles and driving ──────────────────────────────────
  'cy-dep-transport': {
    category: 'transit',
    ru: {
      label: 'Департамент автомобильного транспорта',
      definition: 'Τμήμα Οδικών Μεταφορών — ведомство Кипра по транспортным средствам и водителям. Регистрирует новые и ввезённые машины, обменивает иностранные права в районных офисах и ведёт условия субсидии на покупку электромобиля.',
    },
    en: {
      label: 'Department of Road Transport',
      definition: 'Τμήμα Οδικών Μεταφορών, the Cypriot authority for vehicles and drivers. It registers new and imported cars, exchanges foreign driving licences at its district offices, and administers the terms of the electric-vehicle purchase subsidy.',
    },
  },

  // ── Spain — schooling stages ───────────────────────────────────────
  'eso': {
    category: 'education',
    ru: {
      label: 'ESO',
      definition: 'Educación Secundaria Obligatoria — обязательная средняя ступень испанской школы, с 12 до 16 лет. Завершается школьным дипломом и обозначает точку, где образование перестаёт быть обязательным: дальше развилка на Bachillerato к университету или FP к профессии.',
    },
    en: {
      label: 'ESO',
      definition: 'Educación Secundaria Obligatoria, the compulsory secondary stage of Spanish schooling, from 12 to 16. It ends with a school-issued diploma and marks the point where education stops being mandatory: the fork after it is Bachillerato toward university or FP toward a trade.',
    },
  },
  // ── Thailand ────────────────────────────────────────────────────────
  // Thailand — banking
  'promptpay-thailand': {
    category: 'misc',
    ru: {
      label: 'PromptPay',
      definition: 'Национальная система мгновенных платежей Таиланда под надзором Банка Таиланда. Позволяет переводить баты по номеру телефона, тайскому ID или номеру счёта; на ней работают тайские QR-коды для оплаты в магазинах. Иностранец может подключиться по паспорту при наличии счёта в батах.',
    },
    en: {
      label: 'PromptPay',
      definition: 'Thailand\'s national instant-payment system overseen by the Bank of Thailand. It lets you send baht to a mobile phone number, Thai ID number or account number, and it powers the Thai QR codes used to pay in shops. Foreigners can register with a passport and a Thai baht account.',
    },
  },
  'nrba-thailand': {
    category: 'misc',
    ru: {
      label: 'NRBA',
      definition: 'Специальный счёт в батах для нерезидентов Таиланда. Используется для общих целей: торговли, услуг, прямых инвестиций и покупки недвижимости. В отличие от обычного сберегательного счёта в батах, не застрахован Агентством по защите вкладов.',
    },
    en: {
      label: 'NRBA',
      definition: 'A special Thai baht account for non-residents. It is used for general purposes such as trade, services, direct investment and buying property. Unlike an ordinary baht savings account, it is not covered by Thailand\'s Deposit Protection Agency.',
    },
  },
  'fcd-account-thailand': {
    category: 'misc',
    ru: {
      label: 'FCD-счёт',
      definition: 'Банковский счёт в Таиланде в иностранной валюте, например в долларах США. Открыть его могут и резиденты, и нерезиденты. Наличными на него можно внести не более 15 000 долларов в день, если не доказать, что деньги ввезены в страну или куплены в банке. Остатки на FCD не застрахованы.',
    },
    en: {
      label: 'FCD account',
      definition: 'A bank account in Thailand held in a foreign currency such as US dollars. Residents and non-residents may hold one. Cash deposits into it are capped at USD 15,000 a day unless you can show the cash was brought into the country or bought from a bank. FCD balances are not covered by deposit protection.',
    },
  },
  'mule-account-thailand': {
    category: 'misc',
    ru: {
      label: 'счёт-«мул»',
      definition: 'Банковский счёт, открытый или сданный в аренду, чтобы мошенники гоняли через него украденные деньги (в Таиланде — «бан-чи ма», «счёт-лошадь»). Из-за кампании против таких счетов в 2025 году банки отказывают многим краткосрочным иностранцам, замораживают подозрительные счета и ограничивают дневные переводы в приложениях.',
    },
    en: {
      label: 'mule account',
      definition: 'A bank account opened or rented out so scammers can move stolen money through it. Thailand\'s 2025 crackdown on mule accounts is why banks now refuse accounts to many short-stay foreigners, freeze suspicious accounts and cap daily app transfers.',
    },
  },
  'dpa-thailand': {
    category: 'misc',
    ru: {
      label: 'DPA',
      definition: 'Государственный страховщик вкладов Таиланда. При банкротстве банка выплачивает до 1 млн батов на вкладчика в одном банке в течение 30 дней. Страхует сберегательные, текущие и срочные вклады в батах, одинаково для тайцев и иностранцев. Не страхует валютные вклады, счета нерезидентов в батах, фонды, облигации и криптовалюту.',
    },
    en: {
      label: 'DPA',
      definition: 'Thailand\'s state deposit insurer. If a bank fails, it repays up to THB 1 million per depositor per bank within 30 days. It covers baht savings, current and fixed deposits for Thais and foreigners alike, but not foreign-currency deposits, non-resident baht accounts, funds, bonds or crypto.',
    },
  },
  // Thailand — climate
  'hot-season-thailand': {
    category: 'misc',
    ru: {
      label: 'жаркий сезон',
      definition: 'Жаркий сезон — первый из трёх официальных сезонов Таиланда по классификации Метеорологического департамента (TMD), примерно с середины февраля до середины мая. Самый жаркий месяц почти везде — апрель; TMD называет день «жарким» при 35–39,9 °C и «очень жарким» от 40 °C. Фактическую дату начала сезона департамент объявляет каждый год, и она может сдвигаться на пару недель.',
    },
    en: {
      label: 'hot season',
      definition: 'The first of Thailand\'s three official seasons as defined by the Thai Meteorological Department, running roughly from mid-February to mid-May. April is the hottest month almost everywhere; TMD calls a day "hot" at 35–39.9 °C and "very hot" from 40 °C. TMD announces the actual start date each year, which can be two weeks early or late.',
    },
  },
  'burning-season-thailand': {
    category: 'misc',
    ru: {
      label: 'сезон дымки',
      definition: 'Сезон дымки (сезон палов) — период примерно с февраля по апрель, когда сельскохозяйственные и лесные пожары на севере Таиланда и в соседних Мьянме и Лаосе заполняют дымом горные долины. Департамент контроля загрязнения (PCD) отслеживает его как «кризисное окно» с 1 января по 31 мая; в 2024 году средний уровень PM2.5 по 17 северным провинциям за это окно составил 47 мкг/м³ при тайском суточном нормативе 37,5.',
    },
    en: {
      label: 'burning season',
      definition: 'The February–April period when agricultural and forest fires in northern Thailand and neighbouring Myanmar and Laos fill the northern valleys with smoke. The Pollution Control Department tracks it as a 1 January–31 May "crisis window"; in 2024 the 17 northern provinces averaged 47 µg/m³ of PM2.5 over that window, against a Thai 24-hour standard of 37.5.',
    },
  },
  'hotspot-thailand': {
    category: 'misc',
    ru: {
      label: 'хотспот',
      definition: 'Термоточка (hotspot) — тепловая аномалия, зафиксированная спутником и засчитываемая как вероятный открытый огонь. Тайские ведомства используют накопленное число термоточек как главный показатель палов: PCD насчитал 79 859 точек на севере в январе–мае 2024 года и 55 989 — в 2025-м.',
    },
    en: {
      label: 'hotspot',
      definition: 'A satellite-detected thermal anomaly counted as a likely open fire. Thai agencies use cumulative hotspot counts as the headline measure of burning; PCD reported 79,859 hotspots in the North in January–May 2024 and 55,989 in 2025.',
    },
  },
  'fon-ting-chuang': {
    category: 'misc',
    ru: {
      label: 'фон тинг чуанг',
      definition: '«Провал» сезона дождей — перерыв в муссонных дождях, обычно в конце июня–июле, когда муссонная ложбина уходит на север, к южному Китаю, и осадков над Таиландом становится меньше на одну-две недели, а в отдельные годы — на месяц и дольше, что бьёт по фермам и водохранилищам.',
    },
    en: {
      label: 'fon ting chuang',
      definition: 'A break in the rainy season, usually in late June to July, when the monsoon trough shifts north over southern China and rain over Thailand eases for one to two weeks — in some years for a month or more, causing drought stress on farms and reservoirs.',
    },
  },
  'northeast-monsoon-thailand': {
    category: 'misc',
    ru: {
      label: 'северо-восточный муссон',
      definition: 'Северо-восточный муссон — холодный сухой ветер из сибирско-китайского антициклона, который дует над Таиландом примерно с середины октября до середины февраля. На севере и северо-востоке он приносит ясное небо и холодные ночи, а на побережье Сиамского залива на юге (Самуи, Накхонситхаммарат, Хатъяй) — сильные дожди, поэтому самый дождливый месяц на Самуи — ноябрь.',
    },
    en: {
      label: 'northeast monsoon',
      definition: 'The cool, dry wind from the Siberian–Chinese high that covers Thailand from about mid-October to mid-February. It brings clear skies and cold nights to the North and Northeast, but heavy rain to the Gulf-of-Thailand side of the South (Ko Samui, Nakhon Si Thammarat, Hat Yai), which is why Samui\'s wettest month is November.',
    },
  },
  'air4thai': {
    category: 'misc',
    ru: {
      label: 'Air4Thai',
      definition: 'Air4Thai — официальный сайт и приложение Департамента контроля загрязнения (PCD) с почасовыми данными PM2.5, PM10, озона и других загрязнителей со всех государственных станций мониторинга Таиланда и цветовой шкалой тайского AQI. Именно его жители Чиангмая и Бангкока проверяют во время эпизодов смога.',
    },
    en: {
      label: 'Air4Thai',
      definition: 'The Pollution Control Department\'s official air-quality website and app, reporting hourly PM2.5, PM10, ozone and other pollutants from Thailand\'s government monitoring stations with a colour-coded Thai AQI. It is the reference residents in Chiang Mai and Bangkok check during haze episodes.',
    },
  },
  // Thailand — cost-of-living
  'ft-charge-thailand': {
    category: 'misc',
    ru: {
      label: 'надбавка Ft',
      definition: 'Ft (ค่าเอฟที) — автоматическая топливная надбавка к базовому тарифу, которая есть в каждом тайском счёте за электричество. Регулятор ERC пересматривает её раз в четыре месяца, перекладывая на потребителя изменения стоимости топлива и закупки электроэнергии. На сентябрь–декабрь 2026 года она составляет 16,23 сатанга (0,1623 бата) за кВт·ч.',
    },
    en: {
      label: 'Ft charge',
      definition: 'The automatic fuel adjustment charge added to every Thai electricity bill on top of the base tariff. The Energy Regulatory Commission resets it every four months to pass through changes in fuel and power-purchase costs. For September-December 2026 it is 16.23 satang (0.1623 baht) per unit.',
    },
  },
  'buddhist-era-year': {
    category: 'misc',
    ru: {
      label: 'B.E.',
      definition: 'Официальный год буддийской эры, которым в Таиланде датируются вся госстатистика, законы и бланки. Он опережает григорианский на 543 года: 2568 год буддийской эры — это 2025-й, 2569 — 2026-й. Невнимательность к этой разнице — самая частая причина, по которой тайские данные цитируют с неправильным годом.',
    },
    en: {
      label: 'B.E.',
      definition: 'Thailand\'s official calendar year, used on every government statistic, law and form. It runs 543 years ahead of the Gregorian year, so B.E. 2568 is 2025 and B.E. 2569 is 2026. Misreading it is the most common way Thai statistics get quoted with the wrong year.',
    },
  },
  'nso-thailand': {
    category: 'misc',
    ru: {
      label: 'NSO',
      definition: 'Национальное статистическое управление Таиланда при Министерстве цифровой экономики и общества. Его обследование социально-экономического положения домохозяйств — официальный источник данных о доходах и расходах тайских семей по регионам и провинциям. Сколько платит арендатор, NSO не публикует.',
    },
    en: {
      label: 'NSO',
      definition: 'Thailand\'s National Statistical Office, under the Ministry of Digital Economy and Society. Its Household Socio-Economic Survey is the official source for what Thai households earn and spend, by region and province. It does not publish the rent a tenant pays.',
    },
  },
  'thai-minimum-wage-notification': {
    category: 'misc',
    ru: {
      label: 'уведомление о минимальной зарплате',
      definition: 'Постановление трёхстороннего Комитета по заработной плате, которым в Таиланде устанавливается минимальная оплата труда. Ставка дневная, а не месячная, и зависит от провинции, а иногда и от района. Постановление № 14, действующее с 1 июля 2025 года, задаёт 17 ставок: от 337 батов в день на крайнем юге до 400 батов в Бангкоке, на Пхукете, в Чонбури и на Самуи.',
    },
    en: {
      label: 'minimum wage notification',
      definition: 'The instrument that sets Thailand\'s minimum wage. It is a daily rate, not a monthly one, and varies by province and sometimes by district. Notification No.14, in force since 1 July 2025, sets 17 rates from 337 baht a day in the deep south to 400 baht in Bangkok, Phuket, Chonburi and Koh Samui.',
    },
  },
  // Thailand — education
  'obec-thailand': {
    category: 'education',
    ru: {
      label: 'OBEC',
      definition: 'Управление комиссии по базовому образованию (สพฐ.) — ведомство Министерства образования Таиланда, которое руководит государственными начальными и средними школами через региональные управления образовательных округов. Если семья иностранцев отдаёт ребёнка в тайскую государственную школу, это, как правило, школа OBEC.',
    },
    en: {
      label: 'OBEC',
      definition: 'The Ministry of Education agency that runs Thailand\'s public primary and secondary schools, through regional Educational Service Area offices. If a foreign family enrols a child in a Thai state school, it is usually an OBEC school.',
    },
  },
  'opec-thailand': {
    category: 'education',
    ru: {
      label: 'OPEC',
      definition: 'Управление комиссии по частному образованию (สช.) — ведомство Министерства образования, которое лицензирует частные школы, включая международные, и надзирает за ними. Легально работающая в Таиланде «международная школа» числится в реестре OPEC; в сентябре 2026 года счётчик на его сайте показывал 265 международных школ.',
    },
    en: {
      label: 'OPEC',
      definition: 'The Ministry of Education agency that licenses and supervises private schools, international schools included. A school legally operating as an \'international school\' in Thailand appears on OPEC\'s register, whose homepage counter showed 265 international schools in September 2026.',
    },
  },
  'compulsory-education-thailand': {
    category: 'education',
    ru: {
      label: 'обязательное образование',
      definition: 'Девять лет обучения (с Pratom 1 по Mathayom 3), которые опекуны обязаны обеспечить ребёнку с года, когда ему исполняется семь, до шестнадцати лет, по Закону об обязательном образовании B.E. 2545 (2002). Конституция 2017 года отдельно гарантирует двенадцать лет бесплатного образования.',
    },
    en: {
      label: 'compulsory education',
      definition: 'Nine years of schooling (Prathom 1 to Mathayom 3) that guardians must ensure for children from the year they turn seven until age sixteen, under the Compulsory Education Act B.E. 2545 (2002). The 2017 Constitution separately promises twelve years of free education.',
    },
  },
  // Thailand — healthcare
  'uc-scheme-thailand': {
    category: 'health',
    ru: {
      label: 'UCS',
      definition: 'Государственная программа всеобщего медицинского покрытия Таиланда, финансируемая из налогов. Создана Законом о национальном медицинском обеспечении 2002 года, управляется Национальным управлением медицинского обеспечения (NHSO). Участник прикрепляется к ближайшему к дому учреждению первичной помощи и лечится там или по его направлению. Закон говорит о «каждом человеке», но на практике регистрация привязана к тайскому удостоверению личности, поэтому иностранцы полагаются на соцстрах через работодателя или частную страховку.',
    },
    en: {
      label: 'UCS',
      definition: 'Thailand\'s tax-financed health coverage scheme, created by the National Health Security Act 2002 and run by the National Health Security Office (NHSO). Members register with a primary care unit near home and receive care there or on referral. The Act speaks of \'every person\', but in practice registration is tied to a Thai national ID, so foreign residents rely on employer social security or private insurance.',
    },
  },
  'sso-section-33': {
    category: 'health',
    ru: {
      label: 'Section 33',
      definition: 'Обязательное социальное страхование работников частного сектора в Таиланде, которым управляет Управление социального страхования (SSO). Взносы считаются с зарплаты от 1 650 до 17 500 бат в месяц (потолок поднят с 15 000 с 1 января 2026 года, в 2029 году вырастет до 20 000, в 2032-м до 23 000). После трёх месяцев взносов лечение бесплатно в больнице, к которой прикреплён застрахованный.',
    },
    en: {
      label: 'Section 33',
      definition: 'Compulsory social security for private-sector employees in Thailand, administered by the Social Security Office. Contributions are calculated on wages between 1,650 and 17,500 THB a month (ceiling raised from 15,000 on 1 January 2026, rising to 20,000 in 2029 and 23,000 in 2032). Medical care is free at the hospital the insured person is registered with, after three months of contributions.',
    },
  },
  'sso-section-39': {
    category: 'health',
    ru: {
      label: 'Section 39',
      definition: 'Добровольное продолжение тайского соцстрахования после увольнения. Нужно не менее 12 месяцев взносов по разделу 33 и заявление в течение 6 месяцев после ухода с работы. Взнос фиксированный: 432 бата в месяц (9% от условной зарплаты 4 800 бат). Покрывает шесть видов выплат, включая медицинскую помощь, но не пособие по безработице.',
    },
    en: {
      label: 'Section 39',
      definition: 'Voluntary continuation of Thai social security for people who leave employment. You must have paid into Section 33 for at least 12 months and apply within 6 months of leaving. The contribution is a flat 432 THB a month (9% of a notional 4,800 THB wage) and covers six benefits, including medical care but not unemployment.',
    },
  },
  'health-region-thailand': {
    category: 'health',
    ru: {
      label: 'регион здравоохранения',
      definition: 'Один из 13 административных медицинских округов, по которым Министерство здравоохранения Таиланда планирует услуги и кадры. Округ 13 — Бангкок; округа 7–10 охватывают северо-восток (Исан), где врачей на душу населения меньше всего в стране.',
    },
    en: {
      label: 'health region',
      definition: 'One of 13 administrative health regions used by Thailand\'s Ministry of Public Health to plan services and staff. Region 13 is Bangkok; Regions 7–10 cover the Northeast (Isan), the part of the country with the fewest doctors per head of population.',
    },
  },
  'specially-controlled-drug-thailand': {
    category: 'health',
    ru: {
      label: 'особо контролируемый препарат',
      definition: 'Категория лекарств по тайскому законодательству, которые аптека может продать только по рецепту врача и только через фармацевта. Министерство здравоохранения добавляет препараты в список своими уведомлениями. С 15 сентября 2026 года туда входят инъекционные GLP-1-препараты для похудения, например семаглутид.',
    },
    en: {
      label: 'specially controlled drug',
      definition: 'A Thai legal category of medicines that pharmacies may sell only against a doctor\'s prescription, dispensed by a pharmacist. The Ministry of Public Health adds drugs to the list by notification. From 15 September 2026 this includes injectable GLP-1 \'weight-loss pens\' such as semaglutide.',
    },
  },
  'tgia-longstay-insurance': {
    category: 'health',
    ru: {
      label: 'страховка TGIA для long stay',
      definition: 'Медицинские страховки для тайских долгосрочных виз O-A и O-X, перечисленные Тайской ассоциацией общего страхования (TGIA) на портале longstay.tgia.org. При первой подаче на O-A нужно покрытие не менее 100 000 долларов США или 3 млн бат. Для O-X нужен тайский полис с покрытием не менее 400 000 бат на стационар и 40 000 бат на амбулаторное лечение в год.',
    },
    en: {
      label: 'TGIA long-stay insurance',
      definition: 'Health insurance policies for Thai long-stay visas (O-A, O-X), listed by the Thai General Insurance Association on its longstay.tgia.org portal. First-time O-A applicants need cover of at least USD 100,000 or 3 million baht. O-X holders need a Thai policy with at least 400,000 baht inpatient and 40,000 baht outpatient cover per year.',
    },
  },
  // Thailand — infrastructure
  'thaid': {
    category: 'misc',
    ru: {
      label: 'ThaID',
      definition: 'Официальное приложение цифровой идентификации Таиланда от Департамента провинциальной администрации (DOPA). С ним владельцы тайского 13-значного идентификационного номера входят в госуслуги, например в онлайн-подачу налоговой декларации. Регистрация построена вокруг тайской ID-карты. Правил для иностранных резидентов DOPA не публикует, и одного паспорта для входа недостаточно.',
    },
    en: {
      label: 'ThaID',
      definition: 'Thailand\'s official digital-identity app, run by the Department of Provincial Administration (DOPA). It lets holders of a Thai 13-digit ID number log in to government services such as online tax filing. Registration is built around the Thai ID card. DOPA publishes no rules for foreign residents, so a passport alone will not get you in.',
    },
  },
  'saifi-saidi': {
    category: 'misc',
    ru: {
      label: 'SAIFI / SAIDI',
      definition: 'Стандартные показатели надёжности электросети. SAIFI — сколько отключений в среднем приходится на одного потребителя за год, SAIDI — сколько минут эти отключения длятся в сумме. Тайские энергокомпании публикуют оба показателя, как правило без учёта форс-мажора вроде сильных штормов.',
    },
    en: {
      label: 'SAIFI / SAIDI',
      definition: 'Standard measures of how reliable a power grid is. SAIFI is how many outages an average customer has per year; SAIDI is how many minutes of outage they add up to. Thai utilities publish both, usually leaving out force-majeure events such as major storms.',
    },
  },
  'mea-pea': {
    category: 'misc',
    ru: {
      label: 'MEA / PEA',
      definition: 'Две государственные электросетевые компании Таиланда. Metropolitan Electricity Authority (MEA) обслуживает Бангкок, Нонтхабури и Самутпракан, Provincial Electricity Authority (PEA) — всю остальную страну. От того, к какой из них относится ваш адрес, зависят выставитель счетов и телефон для сообщений об отключениях.',
    },
    en: {
      label: 'MEA / PEA',
      definition: 'Thailand\'s two state electricity distributors. The Metropolitan Electricity Authority (MEA) serves Bangkok, Nonthaburi and Samut Prakan; the Provincial Electricity Authority (PEA) serves the rest of the country. Which one you fall under decides your bill issuer and outage hotline.',
    },
  },
  'mwa': {
    category: 'misc',
    ru: {
      label: 'MWA',
      definition: 'Государственный водоканал Бангкока, Нонтхабури и Самутпракана. Вода в его сети сертифицирована Департаментом здравоохранения как питьевая. При этом MWA сама предупреждает, что баки в зданиях, насосы и старые трубы могут загрязнить воду до того, как она дойдёт до крана.',
    },
    en: {
      label: 'MWA',
      definition: 'The state water utility for Bangkok, Nonthaburi and Samut Prakan. Its network water is certified drinkable by the Department of Health. MWA itself warns that building tanks, pumps and old pipes can contaminate water before it reaches the tap.',
    },
  },
  'e-workpermit': {
    category: 'misc',
    ru: {
      label: 'e-WorkPermit',
      definition: 'Онлайн-система Департамента занятости для разрешений на работу иностранцев: eworkpermit.doe.go.th.',
    },
    en: {
      label: 'e-WorkPermit',
      definition: 'The Department of Employment\'s online system for foreign work permits at eworkpermit.doe.go.th.',
    },
  },
  'type-o-plug': {
    category: 'misc',
    ru: {
      label: 'вилка типа O',
      definition: 'Национальный стандарт вилки в Таиланде: три круглых штыря с заземлением. На практике большинство розеток гибридные и принимают также американские (A/B) и двухштырьковые европейские (C) вилки. Европейские вилки Schuko в них тоже входят, но без заземления.',
    },
    en: {
      label: 'Type O plug',
      definition: 'Thailand\'s national plug standard: three round pins with an earth. In practice most sockets are hybrids that also take US (A/B) and two-pin European (C) plugs. European Schuko plugs fit too, but they are not earthed.',
    },
  },
  // Thailand — language-culture
  'royal-gazette-thailand': {
    category: 'misc',
    ru: {
      label: 'Royal Gazette',
      definition: 'Официальный вестник Таиланда. Законы, королевские указы, а также уведомления министерств и комитетов вступают в силу только после публикации в нём, часто «со дня, следующего за днём опубликования». Многие правила, касающиеся иностранцев (часы продажи алкоголя, списки безвизовых стран), меняются уведомлением в вестнике, а не новым законом.',
    },
    en: {
      label: 'Royal Gazette',
      definition: 'Thailand\'s official journal. Laws, royal decrees and ministry or committee notifications take legal effect only once published there, often \'from the day following publication\'. Many rules that affect foreigners, such as alcohol sale hours and visa-exemption lists, change by Gazette notice rather than by new legislation.',
    },
  },
  'cu-tfl': {
    category: 'misc',
    ru: {
      label: 'CU-TFL',
      definition: 'Экзамен по тайскому языку как иностранному Университета Чулалонгкорн, проводится Институтом тайского языка имени принцессы Сириндхорн с 2013 года. Проверяет аудирование, чтение, письмо и говорение, вместе или по отдельности. Это добровольный сертификат для работы и учёбы, а не юридическое требование для визы или гражданства.',
    },
    en: {
      label: 'CU-TFL',
      definition: 'Chulalongkorn University Test of Thai as a Foreign Language, run by the Sirindhorn Thai Language Institute since 2013. It tests listening, reading, writing and speaking, separately or together. It is a voluntary certificate used for jobs and study; it is not a legal requirement for visas or citizenship.',
    },
  },
  'sansoen-phra-barami': {
    category: 'misc',
    ru: {
      label: 'Sansoen Phra Barami',
      definition: 'Королевский гимн Таиланда, отдельный от государственного гимна (Пхленг Чат). Согласно руководству Департамента провинциальной администрации, при натурализации заявитель на собеседовании должен уметь спеть оба гимна, а также говорить по-тайски и понимать речь.',
    },
    en: {
      label: 'Sansoen Phra Barami',
      definition: 'Thailand\'s royal anthem, distinct from the national anthem (Phleng Chat). DOPA\'s naturalisation manual requires applicants to be able to sing both, alongside speaking and understanding Thai, at the naturalisation interview.',
    },
  },
  'naturalisation-thailand-plaeng-sanchat': {
    category: 'misc',
    ru: {
      label: 'натурализация',
      definition: 'Приобретение тайского гражданства по статье 10 Закона о гражданстве 2508 г. B.E. Условия: совершеннолетие, хорошее поведение, постоянное занятие, пять лет непрерывного проживания в Таиланде и знание тайского языка. Решение остаётся на усмотрение министра внутренних дел, требует королевского согласия и завершается присягой на верность.',
    },
    en: {
      label: 'naturalisation',
      definition: 'Acquiring Thai citizenship under section 10 of the Nationality Act B.E. 2508. It requires adulthood, good behaviour, a regular occupation, five consecutive years of domicile in Thailand and Thai-language knowledge. It is granted at the Interior Minister\'s discretion with Royal Assent, followed by an oath of allegiance.',
    },
  },
  // Thailand — property
  'chanote': {
    category: 'property',
    ru: {
      label: 'чанот',
      definition: 'Полноценный титул собственности на землю в Таиланде: выдаёт Департамент земель, в нём есть план участка по результатам межевания. Это единственный документ, который подтверждает право собственности, а не владения, поэтому перед покупкой или долгой арендой проверяют именно его. Иностранца нельзя вписать собственником, но зарегистрировать на нём аренду, узуфрукт или ипотеку можно.',
    },
    en: {
      label: 'Chanote',
      definition: 'Thailand\'s full land ownership title deed, issued by the Department of Lands with a surveyed parcel map. It is the only land document that proves ownership rather than possession, so check for it before any purchase or long lease. Foreigners cannot be named as owner, but a lease, usufruct or mortgage can be registered on it.',
    },
  },
  'nor-sor-3-gor': {
    category: 'property',
    ru: {
      label: 'Nor Sor 3 Gor',
      definition: 'Тайское «свидетельство об использовании» земли, то есть подтверждённое владение с отмежёванными границами. Ступенью ниже чанота: продажу, аренду и ипотеку по нему регистрировать можно, но полным титулом собственности он не является.',
    },
    en: {
      label: 'Nor Sor 3 Gor',
      definition: 'A Thai \'certificate of use\' (confirmed possession) with surveyed boundaries. It sits below a Chanote: sales, leases and mortgages can be registered on it, but it is not a full ownership title.',
    },
  },
  'tabien-baan': {
    category: 'property',
    ru: {
      label: 'табиен бан',
      definition: 'Домовая книга, запись гражданской регистрации по жилью: кто прописан по этому адресу. Если владелец, живущий в своём жилье, вписан в неё на 1 января, первые 50 млн бат освобождаются от налога на землю и строения. Продавец, прописанный в ней не меньше года, не платит специальный налог на бизнес 3,3 %.',
    },
    en: {
      label: 'tabien baan',
      definition: 'The civil registration book for a dwelling, listing who is registered at that address. Thai owner-occupiers named in it on 1 January get the Land and Building Tax exemption on the first THB 50 million. A seller registered in it for at least a year is also spared the 3.3% Specific Business Tax.',
    },
  },
  'foreign-quota-49': {
    category: 'property',
    ru: {
      label: 'квота иностранцев 49 %',
      definition: 'Предел по Закону о кондоминиумах: все иностранцы вместе могут владеть не более чем 49 % общей площади юнитов в одном здании. Юниты в иностранной квоте покупаются в полную собственность, но деньги на покупку должны прийти из-за рубежа в иностранной валюте, и банк должен это подтвердить.',
    },
    en: {
      label: '49% foreign quota',
      definition: 'The Condominium Act cap: foreigners together may own at most 49% of the total floor area of all units in one condominium building. Units in the foreign quota can be bought freehold, but the purchase money must come from abroad in foreign currency, backed by bank evidence.',
    },
  },
  'leasehold-30-year': {
    category: 'property',
    ru: {
      label: 'аренда на 30 лет',
      definition: 'Максимальный срок аренды земли или зданий в Таиланде (Гражданский и торговый кодекс, ст. 540). Аренду дольше 3 лет нужно зарегистрировать в земельном офисе, иначе она не действует в полном объёме. Продлить можно, но в деле 4655/2566 Верховный суд отказал в защите заранее оплаченным обещаниям продления «30+30+30».',
    },
    en: {
      label: '30-year leasehold',
      definition: 'The maximum term for leasing land or buildings in Thailand (Civil and Commercial Code s.540). Leases over 3 years must be registered at the Land Office to bind in full. Renewal is possible, but in case 4655/2566 the Supreme Court refused to enforce pre-paid \'30+30+30\' renewal promises.',
    },
  },
  'fet-form-tor-tor-3': {
    category: 'property',
    ru: {
      label: 'форма FET',
      definition: 'Банковский документ о том, что иностранная валюта ввезена в Таиланд с указанной целью. Иностранец, покупающий юнит в иностранной квоте, предъявляет его или равноценную банковскую справку в земельном офисе как доказательство, что деньги пришли из-за рубежа.',
    },
    en: {
      label: 'FET form',
      definition: 'A bank-issued record that foreign currency was brought into Thailand for a stated purpose. A foreigner buying a condo in the foreign quota shows it, or an equivalent bank certificate, at the Land Office to prove the money came from abroad.',
    },
  },
  'specific-business-tax-th': {
    category: 'property',
    ru: {
      label: 'Specific Business Tax',
      definition: 'Тайский налог: 3 % плюс местная надбавка 10 % от налога, итого 3,3 % от большей из двух сумм, цены сделки или оценочной стоимости. Платится, когда физлицо продаёт недвижимость в течение 5 лет после покупки, если только не прописано в ней хотя бы год. Если платится этот налог, гербовый сбор 0,5 % не взимается.',
    },
    en: {
      label: 'Specific Business Tax',
      definition: 'A Thai tax of 3% plus 10% local surcharge (3.3% in total) on the higher of sale price or appraised value. It applies when an individual sells property within 5 years of acquiring it, unless they have been in its house registration for a year. When SBT applies, the 0.5% stamp duty does not.',
    },
  },
  'land-and-building-tax-th': {
    category: 'property',
    ru: {
      label: 'налог на землю и строения',
      definition: 'Ежегодный местный налог на недвижимость в Таиланде с 2020 года, считается от официальной оценочной стоимости земли, зданий и юнитов в кондоминиумах. Ставки для жилья от 0,02 % до 0,1 %; у владельцев, живущих в своём жилье и вписанных в домовую книгу, первые 50 млн бат не облагаются. Уведомление приходит в феврале, заплатить нужно до конца апреля.',
    },
    en: {
      label: 'Land and Building Tax',
      definition: 'Thailand\'s annual local property tax since 2020, charged on the official appraised value of land, buildings and condo units. Residential rates run from 0.02% to 0.1%, with the first THB 50 million exempt for owner-occupiers named in the house registration. Bills arrive in February and are due in April.',
    },
  },
  // Thailand — safety-community
  'section-112-thailand': {
    category: 'misc',
    ru: {
      label: 'статья 112',
      definition: 'Статья 112 Уголовного кодекса Таиланда: за клевету, оскорбление или угрозу королю, королеве, наследнику престола или регенту — от 3 до 15 лет лишения свободы.',
    },
    en: {
      label: 'Section 112',
      definition: 'Article of Thailand\'s Criminal Code that punishes anyone who defames, insults or threatens the King, Queen, Heir-apparent or Regent with 3 to 15 years in prison.',
    },
  },
  'computer-crime-act-thailand': {
    category: 'misc',
    ru: {
      label: 'Закон о компьютерных преступлениях',
      definition: 'Закон Таиланда о компьютерных преступлениях 2007 года (редакция 2017 года). Статья 14 карает размещение или пересылку ложных данных, способных навредить обществу или национальной безопасности, а также непристойного контента: до 5 лет лишения свободы или штраф до 100 000 бат.',
    },
    en: {
      label: 'Computer Crime Act',
      definition: 'Thailand\'s Computer-Related Crime Act B.E. 2550 (amended 2017). Section 14 punishes posting or forwarding false data likely to harm the public or national security, or obscene data, with up to 5 years in prison or a THB 100,000 fine.',
    },
  },
  'sbpac': {
    category: 'misc',
    ru: {
      label: 'SBPAC',
      definition: 'Государственный центр управления южными приграничными провинциями Таиланда (Паттани, Яла, Наратхиват и часть Сонгкхлы), где идёт вооружённый конфликт. Его статистика учитывает только инциденты, признанные основанием для государственной компенсации, поэтому цифры ниже, чем у независимых мониторов.',
    },
    en: {
      label: 'SBPAC',
      definition: 'Government agency administering Thailand\'s insurgency-affected southern provinces (Pattani, Yala, Narathiwat and parts of Songkhla). Its casualty statistics count only incidents certified for state compensation, so they are lower than independent monitors\' counts.',
    },
  },
  'tourist-police-1155': {
    category: 'misc',
    ru: {
      label: 'туристическая полиция',
      definition: 'Подразделение Королевской полиции Таиланда для иностранных туристов, круглосуточная горячая линия 1155.',
    },
    en: {
      label: 'Tourist Police',
      definition: 'Division of the Royal Thai Police serving foreign visitors, reachable 24/7 on hotline 1155.',
    },
  },
  'controlled-herb-cannabis-thailand': {
    category: 'misc',
    ru: {
      label: 'контролируемое растение',
      definition: 'Правовой статус соцветий каннабиса по уведомлению Министерства здравоохранения Таиланда от июня 2025 года. Продавать их можно только по рецепту лицензированного специалиста сроком не более чем на 30 дней; рекреационное употребление незаконно.',
    },
    en: {
      label: 'controlled herb',
      definition: 'Legal status given to cannabis buds by Thailand\'s Ministry of Public Health notification of June 2025. Shops may sell buds only against a prescription from a licensed practitioner covering no more than 30 days, and recreational use is illegal.',
    },
  },
  'overstay-blacklist-thailand': {
    category: 'misc',
    ru: {
      label: 'запрет на въезд за overstay',
      definition: 'Запрет на повторный въезд, который Иммиграционное бюро Таиланда накладывает за превышение разрешённого срока пребывания. При добровольной явке превышение более 90 дней даёт запрет на 1 год, более 5 лет — на 10 лет. При задержании запрет составляет 5 лет, если превышение меньше года, и 10 лет, если больше. Дополнительно — штраф 500 бат в день, не более 20 000.',
    },
    en: {
      label: 'overstay ban',
      definition: 'Re-entry ban imposed by Thailand\'s Immigration Bureau for staying past your permitted date. If you surrender, overstays of over 90 days bring a 1-year ban, rising to 10 years for over 5 years. If you are arrested, the ban is 5 years for under a year\'s overstay and 10 years beyond that. There is also a fine of THB 500 per day, capped at 20,000.',
    },
  },
  // Thailand — taxes
  'por-161-2566': {
    category: 'tax',
    ru: {
      label: 'Por. 161/2566',
      definition: 'Приказ Налогового департамента Таиланда от 15 сентября 2023 года, по-новому истолковавший статью 41 Налогового кодекса. С 1 января 2024 года иностранный доход, полученный в год налогового резидентства, облагается в том году, когда его ввезли в Таиланд, даже через несколько лет. Старая практика «ввези на следующий год» больше не работает. Парный приказ Por. 162/2566 не распространяет правило на доход, возникший до 2024 года.',
    },
    en: {
      label: 'Por. 161/2566',
      definition: 'A Revenue Department order of 15 September 2023 that reinterpreted section 41 of the Thai Revenue Code. From 1 January 2024, foreign income that a Thai tax resident earned in a year of residence is taxed in whatever later year it is brought into Thailand, ending the old practice of waiting a year before remitting. A companion order, Por. 162/2566, exempts income that arose before 2024.',
    },
  },
  'remittance-basis-thailand': {
    category: 'tax',
    ru: {
      label: 'налог по факту ввоза',
      definition: 'Принцип, по которому Таиланд облагает иностранный доход резидента: не в момент получения, а в момент ввоза в страну. С 2024 года облагается ввоз в любом последующем году, если доход получен начиная с 1 января 2024 года в год, когда человек был резидентом.',
    },
    en: {
      label: 'remittance basis',
      definition: 'The way Thailand taxes a resident\'s foreign income: not when it is earned, but when it is brought into the country. Since 2024, bringing it in any later year counts, as long as it was earned in a year when the person was resident, on or after 1 January 2024.',
    },
  },
  'royal-decree-743': {
    category: 'tax',
    ru: {
      label: 'Королевский декрет 743',
      definition: 'Королевский декрет 2022 года о налоговых льготах визы Long-Term Resident. Он освобождает ввезённый в Таиланд иностранный доход для трёх категорий LTR: состоятельные граждане мира, состоятельные пенсионеры, специалисты, работающие из Таиланда. Высококвалифицированным специалистам вместо этого доступен налог 17 % у источника с зарплаты от работодателя из целевой отрасли.',
    },
    en: {
      label: 'Royal Decree 743',
      definition: 'A 2022 Thai royal decree with the tax benefits of the Long-Term Resident visa. It exempts foreign income brought into Thailand for three LTR categories (Wealthy Global Citizens, Wealthy Pensioners, Work-from-Thailand Professionals). Highly-Skilled Professionals may instead pay 17% withholding on salary from a target-industry employer.',
    },
  },
  'pnd-90-91': {
    category: 'tax',
    ru: {
      label: 'PND 90 / 91',
      definition: 'Годовые декларации по подоходному налогу в Таиланде. PND 91 подают те, у кого доход только зарплата; PND 90 подают все, у кого есть иной доход: аренда, гонорары, ввезённый иностранный доход.',
    },
    en: {
      label: 'PND 90 / 91',
      definition: 'Thailand\'s annual personal income tax returns. PND 91 is for people whose only income is salary; PND 90 is for everyone with other income, such as rent, freelance fees or remitted foreign income.',
    },
  },
  'assessable-income-thailand': {
    category: 'tax',
    ru: {
      label: 'облагаемый доход',
      definition: 'Термин Налогового кодекса Таиланда для дохода, облагаемого подоходным налогом. Статья 40 делит его на восемь категорий: от зарплаты и гонораров до аренды, дивидендов и предпринимательского дохода. Из него вычитают стандартные вычеты и личные льготы, чтобы получить чистый налогооблагаемый доход.',
    },
    en: {
      label: 'assessable income',
      definition: 'The Thai Revenue Code\'s term for income subject to personal income tax, divided into eight categories under section 40, from employment and professional fees to rent, dividends and business income. Standard deductions and personal allowances are taken off it to reach net taxable income.',
    },
  },
  // Thailand — transport
  'por-ror-bor': {
    category: 'transit',
    ru: {
      label: 'Por Ror Bor',
      definition: 'Обязательное автострахование в Таиланде; его обычно называют по аббревиатуре закона, который его вводит. Профиль ВОЗ по безопасности дорожного движения 2023 года подтверждает, что тайский закон требует страховать гражданскую ответственность владельцев механических транспортных средств.',
    },
    en: {
      label: 'Por Ror Bor',
      definition: 'Thailand\'s compulsory motor insurance, commonly called by the abbreviation of the Act that requires it. WHO\'s 2023 road-safety profile confirms that Thai law mandates third-party liability insurance for powered vehicles.',
    },
  },
  'dlt-thailand': {
    category: 'transit',
    ru: {
      label: 'DLT',
      definition: 'Ведомство Министерства транспорта Таиланда: регистрирует транспорт, собирает ежегодный транспортный налог и выдаёт водительские права.',
    },
    en: {
      label: 'DLT',
      definition: 'The Ministry of Transport agency that registers vehicles, collects annual vehicle tax and issues driving licences in Thailand.',
    },
  },
  'thai-temporary-driving-licence': {
    category: 'transit',
    ru: {
      label: 'временные права',
      definition: 'Первые тайские права, которые получает каждый, в том числе иностранец, переводящий права своей страны. Постоянные права можно запросить только после того, как временные пробыли у владельца не меньше года. По министерскому регламенту 2020 года иностранцам, въехавшим как туристы, для спорта или транзитом, постоянные права не выдаются.',
    },
    en: {
      label: 'temporary driving licence',
      definition: 'The first Thai driving licence everyone receives, including foreigners converting a home-country licence. A full private licence can only be requested after holding the temporary one for at least a year. Under the 2020 ministerial regulation, the full licence is not available to foreigners who entered as tourists, for sport or in transit.',
    },
  },
  'ride-hailing-car-thailand': {
    category: 'transit',
    ru: {
      label: 'машина для такси через приложение',
      definition: 'Юридическая категория транспорта, введённая в 2021 году: личный автомобиль можно перерегистрировать для работы через Grab, Bolt и похожие приложения. Не больше одной такой машины на человека и не старше девяти лет с первой регистрации.',
    },
    en: {
      label: 'ride-hailing car',
      definition: 'The legal vehicle category, created in 2021, that lets a private car be re-registered for app-based hire through Grab, Bolt and similar apps. Only one such car per person, and no older than nine years from first registration.',
    },
  },
  'songthaew': {
    category: 'transit',
    ru: {
      label: 'сонгтэо',
      definition: 'Пикап со скамейками в крытом кузове; в тайских городах служит маршрутным или заказным местным транспортом. Цену обычно оговаривают или платят по сложившемуся обычаю, счётчиков нет; официального тарифа для этой статьи найти не удалось.',
    },
    en: {
      label: 'songthaew',
      definition: 'A pickup truck fitted with bench seats in the covered back, used as shared or chartered local transport in Thai towns. Fares are generally agreed or customary rather than metered, and no official tariff was found for this article.',
    },
  },
  'ev-3-5': {
    category: 'transit',
    ru: {
      label: 'EV 3.5',
      definition: 'Вторая фаза тайской программы поддержки электромобилей на 2024–2027 годы; её ведёт Акцизный департамент. Производители-участники получают субсидию на каждую машину, которая уменьшается с каждым годом, а подходящие электромобили платят акциз 2%.',
    },
    en: {
      label: 'EV 3.5',
      definition: 'Thailand\'s second-phase electric-vehicle support scheme for 2024–2027, run by the Excise Department. Participating manufacturers get a per-car subsidy that falls each year, and qualifying EVs pay 2% excise.',
    },
  },
  // Thailand — visa
  'tdac': {
    category: 'admin',
    ru: {
      label: 'TDAC',
      definition: 'Электронная карта прибытия в Таиланд, заполняется на официальном сайте Иммиграционного бюро tdac.immigration.go.th. Официальный сервис бесплатен; сайты, берущие за неё деньги, — не государственная услуга.',
    },
    en: {
      label: 'TDAC',
      definition: 'Thailand\'s online arrival card, filed on the official Immigration Bureau site tdac.immigration.go.th. The official service charges no fee; sites that charge for it are not the government service.',
    },
  },
  'tm30': {
    category: 'admin',
    ru: {
      label: 'TM.30',
      definition: 'Уведомление иммиграционной службы Таиланда о месте проживания иностранца. Подаёт хозяин жилья, владелец, арендатор-распорядитель или управляющий отеля. Обязательно даже для держателей визы LTR.',
    },
    en: {
      label: 'TM.30',
      definition: 'Notification of a foreigner\'s place of residence to Thai immigration, filed by the housemaster, owner, possessor or hotel manager where the foreigner stays. Still required even for LTR visa holders.',
    },
  },
  'ninety-day-report': {
    category: 'admin',
    ru: {
      label: 'отчёт за 90 дней',
      definition: 'Уведомление о пребывании, которое иностранцы на долгосрочных визах (например, Non-Immigrant O-A) подают в Иммиграционное бюро каждые 90 дней непрерывного пребывания в Таиланде, по форме TM.47.',
    },
    en: {
      label: '90-day report',
      definition: 'Notification of stay that foreigners on long-stay visas such as the Non-Immigrant O-A must file with the Immigration Bureau after every 90 days of continuous stay in Thailand, on form TM.47.',
    },
  },
  'tm95-one-year-report': {
    category: 'admin',
    ru: {
      label: 'TM.95',
      definition: 'Ежегодный отчёт, заменяющий 90-дневный (TM.47) для держателей визы Long-Term Resident (LTR), проживших в Таиланде год без выезда.',
    },
    en: {
      label: 'TM.95',
      definition: 'The annual report that replaces the 90-day report (TM.47) for Long-Term Resident (LTR) visa holders who stay in Thailand for a year without leaving.',
    },
  },
  'dtv': {
    category: 'admin',
    ru: {
      label: 'DTV',
      definition: 'Пятилетняя многократная виза для удалённых работников и фрилансеров («воркейшн»), для занятий «мягкой силы» Таиланда — муай-тай, кулинарных курсов, лечения — и для их супругов и детей. Каждый въезд даёт 180 дней с однократным продлением до 180. Нужно подтвердить 500 000 бат на счёте.',
    },
    en: {
      label: 'DTV',
      definition: 'A five-year multiple-entry visa for remote workers and freelancers (\'workcation\'), for Thai soft-power activities such as Muay Thai, cooking courses or medical treatment, and for their spouses and children. Each entry allows 180 days, extendable once by up to 180 days. Applicants must show 500,000 THB in the bank.',
    },
  },
  'ltr-visa': {
    category: 'admin',
    ru: {
      label: 'виза LTR',
      definition: 'Виза на 10 лет (5+5) от Совета по инвестициям Таиланда в четырёх категориях: состоятельные граждане мира, состоятельные пенсионеры, специалисты, работающие из Таиланда, и высококвалифицированные специалисты. Заменяет 90-дневный отчёт годовым, освобождает от разрешения на повторный въезд и даёт налоговые льготы по Королевскому декрету 743.',
    },
    en: {
      label: 'LTR visa',
      definition: 'A 10-year (5+5) visa run by Thailand\'s Board of Investment in four categories: Wealthy Global Citizens, Wealthy Pensioners, Work-from-Thailand Professionals and Highly-Skilled Professionals. It replaces the 90-day report with a yearly one, exempts holders from re-entry permits and carries tax benefits under Royal Decree 743.',
    },
  },
  'boi': {
    category: 'admin',
    ru: {
      label: 'BOI',
      definition: 'Государственное агентство Таиланда по продвижению инвестиций. Устанавливает критерии и одобряет заявителей на визу LTR и на SMART-визу, оставшуюся только для стартапов.',
    },
    en: {
      label: 'BOI',
      definition: 'Thailand\'s investment-promotion agency. It sets the criteria for, and endorses applicants to, the LTR visa and the startup-only SMART visa.',
    },
  },
  'smart-s-visa': {
    category: 'admin',
    ru: {
      label: 'виза SMART-S',
      definition: 'Единственное направление SMART-визы Таиланда, сохранённое с февраля 2025 года (объявление BOI Por. 5/2568). Для основателей сертифицированных стартапов в приоритетных отраслях, подтверждающих 600 000 бат на счёте в течение трёх месяцев; прежние направления для специалистов, инвесторов и руководителей перенесены в LTR.',
    },
    en: {
      label: 'SMART-S visa',
      definition: 'The only surviving track of Thailand\'s SMART visa since February 2025 (BOI Announcement Por. 5/2568). It is for founders of certified startups in targeted industries who show 600,000 THB held for three months; the former talent, investor and executive tracks were folded into the LTR visa.',
    },
  },
  'visa-exemption-30': {
    category: 'admin',
    ru: {
      label: '30-дневный безвиз',
      definition: 'С 15 сентября 2026 года — односторонний безвизовый въезд в Таиланд для туризма и коротких деловых поездок для граждан 60 стран и территорий. Даёт до 30 дней с продлением ещё до 30 на усмотрение иммиграционного офицера. Заменил 60-дневный безвиз, действовавший для 93 стран.',
    },
    en: {
      label: '30-day visa exemption',
      definition: 'Since 15 September 2026, Thailand\'s unilateral visa-free entry for tourism and short business trips, granted to passport holders of 60 countries and territories. It allows up to 30 days, extendable by up to 30 more at an immigration officer\'s discretion. It replaced the 60-day exemption that had covered 93 countries.',
    },
  },
  'voa-thailand': {
    category: 'admin',
    ru: {
      label: 'виза по прилёту',
      definition: 'Виза, выдаваемая на специально определённых пунктах пропуска Таиланда за 2 000 бат. С 15 сентября 2026 года доступна только гражданам Азербайджана, Беларуси и Сербии.',
    },
    en: {
      label: 'Visa on Arrival',
      definition: 'A visa issued at designated Thai immigration checkpoints for a 2,000 THB fee. Since 15 September 2026 it is available only to passport holders of Azerbaijan, Belarus and Serbia.',
    },
  },
  'non-o-a': {
    category: 'admin',
    ru: {
      label: 'Non-Immigrant O-A',
      definition: 'Пенсионная виза долгосрочного пребывания в Таиланде на один год для заявителей от 50 лет. Нужны 800 000 бат на счёте, или доход 65 000 бат в месяц, или комбинация на 800 000 бат в год, плюс медстраховка на 3 000 000 бат (100 000 долларов) за страховой год. Работа запрещена.',
    },
    en: {
      label: 'Non-Immigrant O-A',
      definition: 'Thailand\'s one-year retirement long-stay visa for applicants aged 50 or over. It requires 800,000 THB in the bank, or a 65,000 THB monthly income, or a combination totalling 800,000 THB a year, plus health insurance of 3,000,000 THB (USD 100,000) per policy year. Employment is prohibited.',
    },
  },
  'non-o-x': {
    category: 'admin',
    ru: {
      label: 'Non-Immigrant O-X',
      definition: 'Пенсионная виза на 5+5 лет для заявителей от 50 лет из фиксированного списка гражданств. Нужны 3 млн бат в тайском банке или 1,8 млн бат плюс доход 1,2 млн бат в год. Визовый сбор — 10 000 бат.',
    },
    en: {
      label: 'Non-Immigrant O-X',
      definition: 'A 5+5-year retirement visa for applicants aged 50 or over from a fixed list of nationalities. It requires 3 million THB in a Thai bank, or 1.8 million THB plus 1.2 million THB a year in income. The visa fee is 10,000 THB.',
    },
  },
  'thailand-privilege': {
    category: 'admin',
    ru: {
      label: 'Thailand Privilege',
      definition: 'Платное членство, к которому прилагается виза для долгого пребывания. Уровни — от Bronze (650 000 бат, 5 лет) до Reserve (5 000 000 бат, 20 лет). К постоянному виду на жительство не ведёт.',
    },
    en: {
      label: 'Thailand Privilege',
      definition: 'A fee-paid membership that comes with a long-stay visa. Tiers range from Bronze (650,000 THB, 5 years) to Reserve (5,000,000 THB, 20 years). It does not lead to permanent residence.',
    },
  },
  'tm9-pr-application': {
    category: 'admin',
    ru: {
      label: 'TM.9',
      definition: 'Форма заявления на постоянный вид на жительство в Таиланде. Подаётся только в ежегодное квотное окно: 100 человек на гражданство и 50 лиц без гражданства. Для квоты 2025 года окно было с 9 марта по 3 апреля 2026.',
    },
    en: {
      label: 'TM.9',
      definition: 'The form used to apply for permanent residence in Thailand. It can be filed only during the annual quota window: 100 applicants per nationality and 50 stateless persons. For the 2025 quota, the window was 9 March – 3 April 2026.',
    },
  },
};

/** Look up a glossary entry; returns `null` if the term is not registered. */
export function getGlossaryEntry(id: string, lang: LangCode): { label: string; definition: string } | null {
  const entry = GLOSSARY[id];
  if (!entry) return null;
  return lang === 'ru' ? entry.ru : entry.en;
}

/** True if a given id exists in the glossary. */
export function hasGlossaryEntry(id: string): boolean {
  return id in GLOSSARY;
}
